import React from 'react';
import { createRoot } from 'react-dom/client';

/* ===== icons.jsx ===== */
/* icons.jsx
   Font-based <Icon name="…"/> for the docs, backed by the decius icon web
   font (dist/css/decius-icons.css). The framework ships icons as a font, so
   there is no sprite to fetch — a glyph is just `<i class="di di-name">`.
*/

const ICON_PX = { sm: 12, lg: 20, xl: 28 };

function Icon({ name, size, className = '', style = {}, title }) {
  const px = ICON_PX[size] || 16;
  const cls = `di di-${name}${className ? ' ' + className : ''}`;
  return (
    <i
      className={cls}
      style={{ fontSize: px, lineHeight: 1, ...style }}
      title={title}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    />
  );
}

// Master catalog used by the icon-browser section
const ICON_CATALOG = [
  { group: 'File / system', icons: ['file', 'folder', 'folder-open', 'save', 'export', 'import', 'trash', 'cog'] },
  { group: 'Edit', icons: ['undo', 'redo', 'cut', 'copy', 'paste', 'edit', 'delete', 'duplicate', 'bug'] },
  { group: 'Transform', icons: ['move', 'rotate', 'scale', 'scale-corners', 'pivot', 'pivot-rotate', 'mirror'] },
  { group: 'Snap / align / gizmo', icons: ['snap', 'snap-pos', 'snap-rot', 'snap-grid', 'magnet', 'align-l', 'align-c-h', 'align-r', 'align-t', 'align-c-v', 'align-b', 'distribute-h', 'distribute-v', 'gizmo', 'gizmo-off'] },
  { group: 'View', icons: ['eye', 'eye-off', 'zoom-in', 'zoom-out', 'fit', 'fullscreen', 'grid-show', 'grid-hide', 'grid-snap'] },
  { group: 'View modes', icons: ['view-bbox', 'view-wire', 'view-solid', 'view-lit', 'view-tex', 'view-render'] },
  { group: 'Objects', icons: ['cube', 'sphere', 'cylinder', 'plane', 'cone', 'torus', 'mesh', 'light', 'camera', 'bone'] },
  { group: 'Tools', icons: ['brush', 'eraser', 'fill', 'pen', 'select', 'lasso', 'marquee'] },
  { group: 'UI / nav', icons: ['chevron-right', 'chevron-left', 'chevron-down', 'chevron-up', 'caret-up-down', 'close', 'check', 'plus', 'minus', 'search', 'menu', 'more-h', 'more-v', 'grip', 'grip-h'] },
  { group: 'Transport', icons: ['play', 'pause', 'stop', 'record', 'rewind', 'fast-forward', 'skip-back', 'skip-fwd', 'volume', 'mic', 'loop'] },
  { group: 'Audio / DAW', icons: ['headphones', 'speaker', 'metronome', 'tempo', 'bars', 'beat', 'sample', 'piano', 'drum', 'midi', 'cable', 'gain', 'eq', 'compress', 'limiter', 'gate', 'sidechain', 'pan', 'stereo', 'mono', 'mute', 'solo', 'arm'] },
  { group: 'Waveforms / DSP', icons: ['wave-sine', 'wave-square', 'wave-saw', 'wave-tri', 'wave-noise', 'envelope', 'lfo', 'filter-lp', 'filter-hp', 'filter-bp', 'filter-notch'] },
  { group: 'Synth / mod', icons: ['oscillator', 'adsr', 'modulate', 'modwheel', 'pitchbend', 'vibrato', 'portamento', 'voice', 'poly', 'preset', 'arp', 'glide'] },
  { group: 'Video editing', icons: ['clip', 'track-v', 'track-a', 'splice', 'blade', 'ripple', 'slip', 'transition', 'fade-in', 'fade-out', 'marker', 'marker-in', 'marker-out', 'filmstrip', 'aspect', 'color-grade', 'keyframe'] },
  { group: 'Painting / drawing', icons: ['brush', 'pencil', 'marker-pen', 'airbrush', 'stamp', 'clone', 'smudge', 'blur', 'sharpen', 'dodge', 'burn', 'sponge', 'tablet-pen', 'opacity', 'flow', 'pressure', 'layer-mask', 'history-brush', 'mixer-brush'] },
  { group: 'Outliner', icons: ['layers', 'group', 'ungroup', 'link', 'link-broken', 'lock', 'unlock', 'pin', 'star', 'star-outline', 'heart', 'heart-outline', 'tag'] },
  { group: 'Render / shading', icons: ['render', 'wireframe', 'shaded', 'textured', 'normals', 'uv'] },
  { group: 'Animation', icons: ['key', 'keys', 'timeline', 'curve', 'graph', 'spline'] },
  { group: 'Color / image', icons: ['palette', 'droplet', 'eyedropper', 'image', 'texture'] },
  { group: 'Status', icons: ['info', 'alert', 'error', 'check-circle', 'help'] },
  { group: 'Misc', icons: ['grid', 'axes', 'globe', 'cpu', 'gpu', 'ai', 'bolt', 'cross-target', 'decimate', 'subdivide', 'extrude', 'array', 'rocket'] },
];

Object.assign(window, { Icon, ICON_CATALOG });


/* ===== components.jsx ===== */
/* dcs.jsx
   React wrappers for decius.css DCC components.
   These render the same class structure shown in the docs code blocks,
   making the live examples and the documented HTML interchangeable.
*/

const { useState, useRef, useEffect, useMemo, useCallback, useContext, createContext } = React;

/* ─────────── Typed drag & drop (react reference layer) ───────────
   A small, idiomatic React DnD system that dovetails with native HTML5 DnD:

     <DndProvider>                         holds the one active drag
     const drag = useDrag({ type, item })  spread `drag` onto a source
     const { dropProps, valid, invalid, isOver } = useDrop({ accept, canDrop, onDrop })

   Sources carry a `type` and `items`; targets list accepted `type`s and an
   optional `canDrop(payload)` predicate, and get live valid/invalid/over
   state for affordance. The live payload lives in a context ref so targets
   can validate by type during dragover (dataTransfer can't be read then).
   <Draggable>/<DropZone> are thin component wrappers over the hooks. */
const DndContext = createContext(null);
function DndProvider({ children }) {
  const ref = useRef(null);                 // { type, items } — live, readable in dragover
  const [active, setActive] = useState(null);
  const api = useMemo(() => ({
    ref, active,
    begin: (p) => { ref.current = p; setActive({ type: p.type, n: (p.items ? p.items.length : 0) }); },
    end: () => { ref.current = null; setActive(null); },
  }), [active]);
  return <DndContext.Provider value={api}>{children}</DndContext.Provider>;
}
function useDndPayload() { const d = useContext(DndContext); return d ? d.ref.current : null; }
function dndAccepts(accept, type) { return Array.isArray(accept) ? accept.includes(type) : accept === type; }

function useDrag({ type, item, disabled }) {
  const dnd = useContext(DndContext);
  return {
    draggable: disabled ? undefined : true,
    onDragStart: disabled ? undefined : (e) => {
      const items = typeof item === 'function' ? item() : Array.isArray(item) ? item : item == null ? [] : [item];
      if (dnd) dnd.begin({ type, items });
      e.dataTransfer.effectAllowed = 'copyMove';
      try { e.dataTransfer.setData('text/plain', items.map(x => (x && (x.label ?? x.id)) ?? x).join(', ')); } catch (_) {}
    },
    onDragEnd: disabled ? undefined : () => { if (dnd) dnd.end(); },
  };
}

function useDrop({ accept, canDrop, onDrop }) {
  const dnd = useContext(DndContext);
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const p = dnd && dnd.active ? dnd.ref.current : null;           // re-renders while a drag is active
  const ok = (pl) => !!pl && dndAccepts(accept, pl.type) && (!canDrop || canDrop(pl));
  const valid = over && ok(p);
  const invalid = over && !!p && !ok(p);
  return {
    isOver: over, valid, invalid, payload: p,
    dropProps: {
      onDragEnter: () => { depth.current += 1; setOver(true); },
      onDragOver: (e) => { if (ok(dnd && dnd.ref.current)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } },
      onDragLeave: () => { depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } },
      onDrop: (e) => {
        const pl = dnd && dnd.ref.current; depth.current = 0; setOver(false);
        if (ok(pl)) { e.preventDefault(); if (onDrop) onDrop(pl, e); if (dnd) dnd.end(); }
      },
    },
  };
}

function Draggable({ type, item, disabled, className = '', style, children, as = 'div' }) {
  const drag = useDrag({ type, item, disabled });
  return React.createElement(as, { className, style, ...drag }, children);
}
function DropZone({ accept, canDrop, onDrop, className = '', style, children, as = 'div' }) {
  const { dropProps, valid, invalid, isOver } = useDrop({ accept, canDrop, onDrop });
  const cls = `${className}${valid ? ' dcs-drop--valid' : ''}${invalid ? ' dcs-drop--invalid' : ''}`;
  return React.createElement(as, { className: cls, style, ...dropProps },
    typeof children === 'function' ? children({ valid, invalid, isOver }) : children);
}

// Controlled when an onChange is given; otherwise self-manage state seeded from
// `value` — so docs demos are interactive without per-instance state wiring.
// When uncontrolled we still follow `value` if it changes (e.g. the animated
// hero), but a fixed literal only seeds once, so drags persist.
function useControl(value, onChange) {
  const [internal, setInternal] = useState(value);
  useEffect(() => { if (!onChange) setInternal(value); }, [value, onChange]);
  return onChange ? [value, onChange] : [internal, setInternal];
}

// Outside-click + Esc dismissal, scoped to a wrapper ref.
function useDismiss(ref, open, close) {
  useEffect(() => {
    if (!open) return;
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const key = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key); };
  }, [open]);
}

// Renders the items of a .dcs-menu (icons, shortcuts, checks, danger, separators, submenus).
function MenuList({ items, onPick }) {
  return items.map((it, i) => it.sep ? <div key={i} className="dcs-menu__sep" /> : (
    <div
      key={i}
      className={`dcs-menu__item${it.danger ? ' dcs-menu__item--danger' : ''}${it.sub ? ' dcs-menu__item--has-sub' : ''}`}
      onClick={(e) => { if (it.sub) { e.stopPropagation(); return; } onPick && onPick(it.label); }}
    >
      {it.check
        ? <span className="dcs-menu__check"><Icon name="check" size="sm" /></span>
        : <span className="dcs-menu__icon">{it.icon && <Icon name={it.icon} size="sm" />}</span>}
      <span className="dcs-menu__label-text">{it.label}</span>
      {it.shortcut && <span className="dcs-menu__shortcut">{it.shortcut}</span>}
      {it.sub && <span className="dcs-menu__caret"><Icon name="chevron-right" size="sm" /></span>}
      {it.sub && <div className="dcs-menu dcs-menu__sub"><MenuList items={it.sub} onPick={onPick} /></div>}
    </div>
  ));
}

// Drag-resize seam. Calls onDelta(px) incrementally while dragging.
function Splitter({ horizontal, onDelta }) {
  const [active, setActive] = useState(false);
  const onDown = (e) => {
    e.preventDefault();
    setActive(true);
    const axis = horizontal ? 'clientY' : 'clientX';
    let last = e[axis];
    const move = (ev) => { const d = ev[axis] - last; last = ev[axis]; if (d) onDelta(d); };
    const up = () => { setActive(false); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return <div className={`dcs-splitter${horizontal ? ' dcs-splitter--h' : ''}${active ? ' dcs-splitter--active' : ''}`} onPointerDown={onDown} />;
}

/* ─────────── Docking layout (real grab-and-dock) ───────────
   Binary-tree layout of split/tabs nodes. Tabs drag between groups; dropping
   on a group's center adds a tab, dropping on an edge splits that group
   (left/right → row, top/bottom → column). Splitters resize. */
let DOCK_ID = 0;
function normLayout(node) {
  const id = `dk${++DOCK_ID}`;
  if (node.type === 'tabs') return { type: 'tabs', id, tabs: [...node.tabs], active: node.active || node.tabs[0] };
  return { type: 'split', id, dir: node.dir, sizes: node.sizes ? [...node.sizes] : node.children.map(() => 1), children: node.children.map(normLayout) };
}
function setActiveInTree(node, gid, tab) {
  if (node.type === 'tabs') return node.id === gid ? { ...node, active: tab } : node;
  return { ...node, children: node.children.map(c => setActiveInTree(c, gid, tab)) };
}
function removeTabFromTree(node, tab) {
  if (node.type === 'tabs') {
    if (!node.tabs.includes(tab)) return node;
    const tabs = node.tabs.filter(t => t !== tab);
    if (!tabs.length) return null;
    return { ...node, tabs, active: node.active === tab ? tabs[0] : node.active };
  }
  const kids = [], sizes = [];
  node.children.forEach((c, i) => { const r = removeTabFromTree(c, tab); if (r !== null) { kids.push(r); sizes.push(node.sizes[i]); } });
  if (kids.length === 0) return null;
  if (kids.length === 1) return kids[0];
  return { ...node, children: kids, sizes };
}
function addTabToTree(node, gid, tab, zone) {
  if (node.type === 'tabs') {
    if (node.id !== gid) return node;
    if (zone === 'center') return { ...node, tabs: [...node.tabs, tab], active: tab };
    const leaf = normLayout({ type: 'tabs', tabs: [tab] });
    const dir = (zone === 'left' || zone === 'right') ? 'row' : 'col';
    const before = zone === 'left' || zone === 'top';
    return { type: 'split', id: `dk${++DOCK_ID}`, dir, sizes: [1, 1], children: before ? [leaf, node] : [node, leaf] };
  }
  return { ...node, children: node.children.map(c => addTabToTree(c, gid, tab, zone)) };
}
// Dock a panel against the whole workspace edge (root-level full-span split).
function addRootEdge(tree, tab, zone) {
  const leaf = normLayout({ type: 'tabs', tabs: [tab] });
  const dir = (zone === 'left' || zone === 'right') ? 'row' : 'col';
  const before = zone === 'left' || zone === 'top';
  return { type: 'split', id: `dk${++DOCK_ID}`, dir, sizes: before ? [1, 3] : [3, 1], children: before ? [leaf, tree] : [tree, leaf] };
}
function resizeSplit(node, splitId, i, dpx, total) {
  if (node.type !== 'split') return node;
  if (node.id === splitId) {
    const sizes = [...node.sizes];
    const sum = sizes.reduce((a, b) => a + b, 0);
    const dw = dpx * (sum / Math.max(1, total));
    const min = sum * 0.07;
    let a = sizes[i] + dw, b = sizes[i + 1] - dw;
    if (a < min) { b -= (min - a); a = min; }
    if (b < min) { a -= (min - b); b = min; }
    sizes[i] = a; sizes[i + 1] = b;
    return { ...node, sizes };
  }
  return { ...node, children: node.children.map(c => resizeSplit(c, splitId, i, dpx, total)) };
}
function dockZone(e, rect) {
  const x = (e.clientX - rect.left) / rect.width, y = (e.clientY - rect.top) / rect.height;
  const m = 0.3;
  if (y < m && y <= x && y <= 1 - x) return 'top';
  if (y > 1 - m && (1 - y) <= x && (1 - y) <= 1 - x) return 'bottom';
  if (x < m) return 'left';
  if (x > 1 - m) return 'right';
  return 'center';
}
const ZONE_BOX = {
  center: { inset: 6 },
  left: { left: 0, top: 0, bottom: 0, width: '50%' },
  right: { right: 0, top: 0, bottom: 0, width: '50%' },
  top: { left: 0, right: 0, top: 0, height: '50%' },
  bottom: { left: 0, right: 0, bottom: 0, height: '50%' },
};
// Root-edge docking is detected inside each group's own dragover when the
// cursor is within EDGE_M of a workspace edge that the group touches — no
// overlay bands, so nothing ever covers a group's tab strip mid-drag.
const EDGE_M = 16;
const ROOT_PREVIEW = {
  left: { left: 0, top: 0, bottom: 0, width: '28%' },
  right: { right: 0, top: 0, bottom: 0, width: '28%' },
  top: { left: 0, right: 0, top: 0, height: '28%' },
  bottom: { left: 0, right: 0, bottom: 0, height: '28%' },
};

// Top-level so re-renders (e.g. 24fps animation) don't remount the subtree.
function DockGroup({ node, ctx }) {
  const ref = useRef(null);
  const onOver = (e) => {
    if (!ctx.drag.current) return;
    e.preventDefault();
    const gr = ref.current.getBoundingClientRect();
    const dr = ctx.dockRef.current.getBoundingClientRect();
    // Near a workspace edge this group sits against → root (full-span) dock.
    let root = null;
    if (e.clientY - dr.top < EDGE_M && gr.top - dr.top < 2) root = 'top';
    else if (dr.bottom - e.clientY < EDGE_M && dr.bottom - gr.bottom < 2) root = 'bottom';
    else if (e.clientX - dr.left < EDGE_M && gr.left - dr.left < 2) root = 'left';
    else if (dr.right - e.clientX < EDGE_M && dr.right - gr.right < 2) root = 'right';
    if (root) { if (ctx.edge !== root) ctx.setEdge(root); if (ctx.hover) ctx.setHover(null); }
    else { if (ctx.edge) ctx.setEdge(null); ctx.setHover({ gid: node.id, zone: dockZone(e, gr) }); }
  };
  return (
    <div className="dcs-dockpane" ref={ref}
      style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      onDragOver={onOver}
      onDrop={(e) => { e.preventDefault(); if (ctx.edge) ctx.onRootDrop(ctx.edge); else ctx.onDrop(node.id); }}>
      <div className="dcs-dockpane__tabs">
        {node.tabs.map(tab => (
          <div key={tab} className="dcs-dockpane__tab" aria-selected={node.active === tab}
            draggable
            onDragStart={(e) => { ctx.drag.current = { tab, fromId: node.id, count: node.tabs.length }; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', tab); }}
            onDragEnd={() => { ctx.drag.current = null; ctx.setHover(null); ctx.setEdge(null); }}
            onClick={() => ctx.activate(node.id, tab)}>
            {ctx.tabMeta(tab).icon && <Icon name={ctx.tabMeta(tab).icon} size="sm" />}
            <span>{ctx.tabMeta(tab).label}</span>
            <div className="dcs-dockpane__tab-close" onClick={(e) => { e.stopPropagation(); ctx.closeTab(tab); }}><Icon name="close" size="sm" /></div>
          </div>
        ))}
      </div>
      <div className="dcs-dockpane__body" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {ctx.renderContent(node.active)}
      </div>
      {ctx.hover && ctx.hover.gid === node.id && !ctx.edge && (
        <div style={{ position: 'absolute', background: 'var(--dcs-accent-haze)', border: '1px solid var(--dcs-accent)', borderRadius: 3, pointerEvents: 'none', zIndex: 6, transition: 'all 90ms ease', ...ZONE_BOX[ctx.hover.zone] }} />
      )}
    </div>
  );
}
function DockNode({ node, ctx }) {
  const ref = useRef(null);
  if (node.type === 'tabs') return <DockGroup node={node} ctx={ctx} />;
  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: node.dir === 'row' ? 'row' : 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      {node.children.map((c, i) => (
        <React.Fragment key={c.id}>
          <div style={{ flex: `${node.sizes[i]} 1 0`, display: 'flex', minWidth: 0, minHeight: 0 }}><DockNode node={c} ctx={ctx} /></div>
          {i < node.children.length - 1 && (
            <Splitter horizontal={node.dir === 'col'} onDelta={(d) => {
              const el = ref.current;
              ctx.resize(node.id, i, d, node.dir === 'row' ? el.clientWidth : el.clientHeight);
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
function DockLayout({ initial, tabMeta, renderContent }) {
  const [root, setRoot] = useState(() => normLayout(initial));
  const [hover, setHover] = useState(null);     // inner: { gid, zone }
  const [edge, setEdge] = useState(null);       // outer root edge being hovered
  const drag = useRef(null);
  const dockRef = useRef(null);
  const ctx = {
    drag, hover, setHover, edge, setEdge, dockRef, tabMeta, renderContent,
    activate: (gid, tab) => setRoot(r => setActiveInTree(r, gid, tab)),
    closeTab: (tab) => setRoot(r => removeTabFromTree(r, tab) || r),
    resize: (id, i, d, total) => setRoot(r => resizeSplit(r, id, i, d, total)),
    onDrop: (gid) => {
      const d = drag.current, h = hover;
      drag.current = null; setHover(null); setEdge(null);
      if (!d || !h || h.gid !== gid) return;
      if (d.fromId === gid && (h.zone === 'center' || d.count <= 1)) return;
      const t = removeTabFromTree(root, d.tab);
      if (t) setRoot(addTabToTree(t, gid, d.tab, h.zone));
    },
    onRootDrop: (zone) => {
      const d = drag.current;
      drag.current = null; setEdge(null); setHover(null);
      if (!d) return;
      const t = removeTabFromTree(root, d.tab);
      if (t) setRoot(addRootEdge(t, d.tab, zone));
    },
  };
  return (
    <div className="dcs-dock" ref={dockRef} style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0 }}>
      <DockNode node={root} ctx={ctx} />
      {/* Root-edge preview — full-span dock against the workspace edge.
          pointer-events:none so it never blocks tab strips or drop targets. */}
      {edge && (
        <div style={{ position: 'absolute', pointerEvents: 'none', zIndex: 7, background: 'var(--dcs-accent-haze)', border: '2px solid var(--dcs-accent)', borderRadius: 4, transition: 'all 90ms ease', ...ROOT_PREVIEW[edge] }} />
      )}
    </div>
  );
}

/* ─────────── Panel ─────────── */
function Panel({ title, icon, tools, footer, raised, flat, bordered, closeable, onClose, headerActive, pad, children, style, className = '' }) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  return (
    <div className={`dcs-panel${raised ? ' dcs-panel--raised' : ''}${bordered ? ' dcs-panel--bordered' : ''} ${className}`} style={style}>
      {title !== undefined && (
        <div className={`dcs-panel__header${headerActive ? ' dcs-panel__header--active' : ''}`}>
          <div className="dcs-panel__title">
            {icon && <Icon name={icon} />}
            <span>{title}</span>
          </div>
          {tools && <div className="dcs-panel__tools">{tools}</div>}
          {closeable && (
            <button
              className="dcs-panel__close"
              onClick={() => { if (onClose) onClose(); else setClosed(true); }}
              aria-label="Close panel"
            >
              <Icon name="close" size="sm" />
            </button>
          )}
        </div>
      )}
      <div className={`dcs-panel__body${pad === 0 ? ' dcs-panel__body--pad-0' : pad === 'sm' ? ' dcs-panel__body--pad-sm' : ''}`}>
        {children}
      </div>
      {footer && <div className="dcs-panel__footer">{footer}</div>}
    </div>
  );
}

/* ─────────── Button ─────────── */
function Button({ children, primary, ghost, danger, sm, lg, icon, iconLeft, iconRight, pressed, onClick, disabled, className = '', ...rest }) {
  const cls = [
    'dcs-btn',
    primary && 'dcs-btn--primary',
    ghost && 'dcs-btn--ghost',
    danger && 'dcs-btn--danger',
    sm && 'dcs-btn--sm',
    lg && 'dcs-btn--lg',
    icon && 'dcs-btn--icon',
    className,
  ].filter(Boolean).join(' ');
  return (
    <button
      className={cls}
      aria-pressed={pressed !== undefined ? pressed : undefined}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} />}
      {children}
      {iconRight && <Icon name={iconRight} />}
    </button>
  );
}

function ButtonGroup({ value, onChange, options }) {
  const [cur, set] = useControl(value, onChange);
  return (
    <div className="dcs-btn-group" role="group">
      {options.map(opt => (
        <button
          key={opt.value}
          className="dcs-btn"
          aria-pressed={cur === opt.value}
          onClick={() => set(opt.value)}
        >
          {opt.icon && <Icon name={opt.icon} />}
          {opt.label && <span>{opt.label}</span>}
        </button>
      ))}
    </div>
  );
}

/* Enum dropdown — the full "choose one of N" selector for when the options
   won't fit as a segmented button-group. Trigger styled like a select; the
   option list is a .dcs-menu popover (fixed, so it never grows a panel's
   scroll), with the current value checked. options: ['a',…] or
   [{value,label,icon}]. */
function Select({ value, onChange, options = [], placeholder = 'Select…', width }) {
  const [cur, set] = useControl(value, onChange);
  const [open, setOpen] = useState(false);
  const [pop, setPop] = useState(null);
  const ref = useRef(null);
  useDismiss(ref, open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  const opts = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o));
  const sel = opts.find(o => o.value === cur);
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = ref.current.getBoundingClientRect();
    const ph = Math.min(opts.length * 28 + 8, 280);
    let top = r.bottom + 2; if (top + ph > window.innerHeight) top = Math.max(8, r.top - ph - 2);
    setPop({ top, left: r.left, width: r.width }); setOpen(true);
  };
  const pick = (o) => { set(o.value); setOpen(false); };
  return (
    <div ref={ref} className={`dcs-select dcs-select--btn${open ? ' dcs-select--open' : ''}`}
      role="combobox" aria-expanded={open} tabIndex={0}
      style={width ? { width } : undefined} onClick={toggle}>
      {sel && sel.icon && <Icon name={sel.icon} size="sm" />}
      <span className="dcs-select__label">{sel ? sel.label : placeholder}</span>
      <Icon className="dcs-select__caret" name="chevron-down" size="sm" />
      {open && pop && (
        <div className="dcs-menu" onClick={(e) => e.stopPropagation()}
          style={{ top: pop.top, left: pop.left, minWidth: pop.width }}>
          {opts.map(o => (
            <div key={o.value} role="option" aria-selected={o.value === cur}
              className={`dcs-menu__item${o.value === cur ? ' dcs-menu__item--active' : ''}`}
              onClick={() => pick(o)}>
              {o.icon
                ? <span className="dcs-menu__icon"><Icon name={o.icon} size="sm" /></span>
                : <span className="dcs-menu__check">{o.value === cur && <Icon name="check" size="sm" />}</span>}
              <span className="dcs-menu__label-text">{o.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── Pointer-drag helper (internal: sliders/faders/knobs) ─────────── */
function usePointerDrag(onDrag, opts = {}) {
  const ref = useRef({ active: false, startX: 0, startY: 0, startVal: 0 });
  return useCallback((e, startVal) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    ref.current = { active: true, startX, startY, startVal };
    const move = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      onDrag({ dx, dy, startVal, shift: ev.shiftKey, ctrl: ev.ctrlKey || ev.metaKey });
    };
    const up = () => {
      ref.current.active = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (opts.onEnd) opts.onEnd();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [onDrag, opts.onEnd]);
}

/* ─────────── Slider (DCC horizontal) ─────────── */
function Slider({ value, onChange, min = 0, max = 1, step = 0, bipolar, width, ticks }) {
  const [cur, set] = useControl(value, onChange);
  const trackRef = useRef(null);
  const v = Math.max(min, Math.min(max, cur));
  const pct = ((v - min) / (max - min)) * 100;
  const handleDown = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const update = (clientX) => {
      const t = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      let nv = min + t * (max - min);
      if (step) nv = Math.round(nv / step) * step;
      set(nv);
    };
    update(e.clientX);
    const move = ev => update(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  let fillStyle;
  if (bipolar) {
    const center = ((-min) / (max - min)) * 100;
    if (v >= 0) fillStyle = { left: `${center}%`, width: `${pct - center}%` };
    else fillStyle = { right: `${100 - center}%`, left: 'auto', width: `${center - pct}%` };
  } else {
    fillStyle = { width: `${pct}%` };
  }
  return (
    <div className="dcs-slider" style={{ width: width || '100%' }} onPointerDown={handleDown}>
      <div className="dcs-slider__track" ref={trackRef}>
        <div className="dcs-slider__fill" style={fillStyle} />
        {ticks && ticks.map((t, i) => {
          const left = ((t - min) / (max - min)) * 100;
          return <div key={i} className="dcs-slider__tick" style={{ left: `${left}%` }} />;
        })}
        <div className="dcs-slider__thumb" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ─────────── Fader (vertical, synth) ─────────── */
function Fader({ value, onChange, min = 0, max = 1, height = 140, label }) {
  const [cur, set] = useControl(value, onChange);
  const trackRef = useRef(null);
  const v = Math.max(min, Math.min(max, cur));
  const pct = 100 - ((v - min) / (max - min)) * 100;
  const handleDown = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const update = (clientY) => {
      const t = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
      set(min + (1 - t) * (max - min));
    };
    update(e.clientY);
    const move = ev => update(ev.clientY);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="dcs-fader" style={{ height }} ref={trackRef} onPointerDown={handleDown}>
      <div className="dcs-fader__track" />
      {[0.25, 0.5, 0.75].map(t => <div key={t} className="dcs-fader__tick" style={{ top: `${t * 100}%` }} />)}
      <div className="dcs-fader__thumb" style={{ top: `${pct}%` }} />
    </div>
  );
}

/* ─────────── Knob ─────────── */
function Knob({ value, onChange, min = 0, max = 1, size = 56, label, bipolar, format }) {
  const [cur, set] = useControl(value, onChange);
  const v = Math.max(min, Math.min(max, cur));
  const norm = (v - min) / (max - min);
  // Angle range: -135° to +135°
  const angle = -135 + norm * 270;
  const arcAngle = norm * 270;
  const startAngle = bipolar ? 0 : -135;
  const sweepDeg = bipolar ? (norm - 0.5) * 270 : arcAngle;

  const handleDown = (e) => {
    const startY = e.clientY;
    const startVal = v;
    const range = max - min;
    const move = (ev) => {
      const dy = startY - ev.clientY;
      const scale = ev.shiftKey ? 400 : 150;
      const delta = (dy / scale) * range;
      set(Math.max(min, Math.min(max, startVal + delta)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Arc track
  const cx = 12, cy = 12, r = 10.5;
  const arcStart = bipolar ? -90 : -225;
  const arcEnd = bipolar ? -90 + sweepDeg : -225 + arcAngle;
  const polar = (ang) => [cx + r * Math.cos(ang * Math.PI / 180), cy + r * Math.sin(ang * Math.PI / 180)];
  const [sx, sy] = polar(arcStart);
  const [ex, ey] = polar(arcEnd);
  const arcLen = Math.abs(arcEnd - arcStart);
  const largeArc = arcLen > 180 ? 1 : 0;
  const sweep = arcEnd >= arcStart ? 1 : 0;

  // Track ring (full sweep)
  const [tsx, tsy] = polar(-225);
  const [tex, tey] = polar(45);

  const formatted = format ? format(v) : v.toFixed(2);
  return (
    <div className="dcs-knob" style={{ '--knob-size': `${size}px` }} onPointerDown={handleDown}>
      <svg className="dcs-knob__ring" viewBox="0 0 24 24">
        <path d={`M ${tsx} ${tsy} A ${r} ${r} 0 1 1 ${tex} ${tey}`} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="1.5" strokeLinecap="round" />
        {Math.abs(sweepDeg) > 0.5 && (
          <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} ${sweep} ${ex} ${ey}`}
                fill="none" stroke="var(--dcs-accent)" strokeWidth="1.75" strokeLinecap="round" />
        )}
      </svg>
      <div className="dcs-knob__cap" />
      <div className="dcs-knob__indicator" style={{ '--angle': `${angle}deg` }} />
      {label && <div className="dcs-knob__label">{label}</div>}
      {label && <div className="dcs-knob__value">{formatted}</div>}
    </div>
  );
}

/* ─────────── Combo (ZBrush-style number/slider) ───────────
   `unbounded` — drop the min/max range entirely. The combo becomes a
   pure scrubber (no fill bar, no clamping). Use this for values that
   have no natural range — XYZ positions, free numeric inputs. Drag
   speed becomes `step` per pixel (so step=0.001 → 1px = 0.001). */
function Combo({ value, onChange, min = 0, max = 1, step = 0.01, label, format, width = 140, sm, lg, unbounded }) {
  const [cur, set] = useControl(value, onChange);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const v = unbounded ? cur : Math.max(min, Math.min(max, cur));
  const pct = unbounded ? 0 : ((v - min) / (max - min)) * 100;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleDown = (e) => {
    if (editing) return;
    if (e.target.classList.contains('dcs-combo__btn')) return;
    const rect = containerRef.current.getBoundingClientRect();
    const startX = e.clientX;
    let dragged = false;
    const range = max - min;
    const startVal = v;
    const move = (ev) => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 3) dragged = true;
      const scale = ev.shiftKey ? 4 : 1;
      // Unbounded: step-per-pixel scrub (no range to normalize against).
      const delta = unbounded ? dx * (step || 0.01) * scale : (dx / rect.width) * range * scale;
      let nv = unbounded ? startVal + delta : Math.max(min, Math.min(max, startVal + delta));
      if (step) nv = Math.round(nv / step) * step;
      set(nv);
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!dragged) {
        setDraft(format ? format(v) : v.toString());
        setEditing(true);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!Number.isNaN(parsed)) set(unbounded ? parsed : Math.max(min, Math.min(max, parsed)));
    setEditing(false);
  };

  const cls = ['dcs-combo', editing && 'dcs-combo--editing', sm && 'dcs-combo--sm', lg && 'dcs-combo--lg', unbounded && 'dcs-combo--unbounded'].filter(Boolean).join(' ');
  const dec = () => set(unbounded ? v - (step || 0.01) : Math.max(min, v - (step || 0.01)));
  const inc = () => set(unbounded ? v + (step || 0.01) : Math.min(max, v + (step || 0.01)));
  return (
    <div ref={containerRef}
         className={cls}
         style={{ width, '--fill': `${pct}%` }}
         onPointerDown={handleDown}>
      {!unbounded && <div className="dcs-combo__fill" />}
      <div className="dcs-combo__btn" onPointerDown={(e) => { e.stopPropagation(); dec(); }}>
        <Icon name="chevron-left" size="sm" />
      </div>
      {label && <div className="dcs-combo__label">{label}</div>}
      <div className="dcs-combo__value">{format ? format(v) : v.toFixed(2)}</div>
      <div className="dcs-combo__btn" onPointerDown={(e) => { e.stopPropagation(); inc(); }}>
        <Icon name="chevron-right" size="sm" />
      </div>
      {editing && (
        <input
          ref={inputRef}
          className="dcs-combo__edit"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') setEditing(false);
          }}
        />
      )}
    </div>
  );
}

/* ─────────── Check / Radio / Switch ─────────── */
function Check({ checked, onChange, children, radio }) {
  const [cur, set] = useControl(checked, onChange);
  return (
    <div className={`dcs-check${radio ? ' dcs-radio' : ''}`} role={radio ? 'radio' : 'checkbox'} aria-checked={cur} onClick={() => set(!cur)}>
      <div className="dcs-check__box">
        {!radio && cur && <Icon name="check" />}
      </div>
      {children != null && children !== '' && <span>{children}</span>}
    </div>
  );
}

function Switch({ checked, onChange }) {
  const [cur, set] = useControl(checked, onChange);
  return <div className="dcs-switch" role="switch" aria-checked={cur} onClick={() => set(!cur)} />;
}

/* ─────────── Tab bar ─────────── */
function Tabs({ tabs, value, onChange }) {
  const [cur, set] = useControl(value, onChange);
  return (
    <div className="dcs-tabs" role="tablist">
      {tabs.map(t => (
        <button key={t.value}
                className="dcs-tab"
                role="tab"
                aria-selected={cur === t.value}
                onClick={() => set(t.value)}>
          {t.icon && <Icon name={t.icon} />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────── Toolbar ─────────── */
function Toolbar({ children, vertical, size, floating, className = '', style }) {
  const cls = [
    'dcs-toolbar',
    vertical && 'dcs-toolbar--v',
    size && `dcs-toolbar--${size}`,
    floating && 'dcs-toolbar--floating',
    className,
  ].filter(Boolean).join(' ');
  return <div className={cls} style={style}>{children}</div>;
}
function ToolbarSep() { return <div className="dcs-toolbar__sep" />; }

/* ─────────── Tree ─────────── */
/* Tree / List — one primitive (a list is a flat tree, `flat`).
   Single-select by default; `multi` adds Ctrl/⌘ toggle + Shift range.
   `reorderable` + `onMove(dragIds, targetId, pos)` adds drag-and-drop with a
   drop indicator: pos is 'before' | 'after' | 'into' (into = reparent, trees
   only). onSelect gets a Set in multi mode, the id in single mode. */
const toIdSet = (s) => s == null ? new Set() : s instanceof Set ? new Set(s) : Array.isArray(s) ? new Set(s) : new Set([s]);
function Tree({ nodes, selected, onSelect, expanded, onExpand, multi, reorderable, onMove,
                flat, dragType = 'node', source, accept, onDropItems }) {
  const dnd = useContext(DndContext);
  const [iExp, setIExp] = useState(() => expanded || new Set());
  const [iSel, setISel] = useState(() => toIdSet(selected));
  const [anchor, setAnchor] = useState(null);
  const [drop, setDrop] = useState(null);          // { id, pos, mode:'move'|'drop' }
  const dragRef = useRef(null);                     // ids being reordered within this tree
  const exp = onExpand ? expanded : iExp;
  const selSet = onSelect ? toIdSet(selected) : iSel;
  const toggleExp = onExpand || ((id) => setIExp(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }));
  const draggable = reorderable || source;
  const dropable = reorderable || (accept && onDropItems);

  // Visible rows, in order (respecting collapse) — drives Shift-range too.
  const flatRows = [];
  const walk = (items, depth) => items.forEach(node => {
    flatRows.push({ node, depth });
    if (!flat && node.children && node.children.length && exp.has(node.id)) walk(node.children, depth + 1);
  });
  walk(nodes, 0);
  const order = flatRows.map(f => f.node.id);

  const emit = (next, primary) => { if (onSelect) onSelect(multi ? next : primary); else setISel(next); };
  const onRowClick = (id, e) => {
    if (multi && (e.metaKey || e.ctrlKey)) { const n = new Set(selSet); n.has(id) ? n.delete(id) : n.add(id); setAnchor(id); emit(n, id); }
    else if (multi && e.shiftKey && anchor != null && order.includes(anchor)) {
      const a = order.indexOf(anchor), b = order.indexOf(id), [lo, hi] = a < b ? [a, b] : [b, a];
      emit(new Set(order.slice(lo, hi + 1)), id);
    } else { setAnchor(id); emit(new Set([id]), id); }
  };

  const descendantsOf = (id) => {
    const out = new Set();
    const collect = (items) => items.forEach(n => { out.add(n.id); if (n.children) collect(n.children); });
    const find = (items) => items.some(n => n.id === id ? (collect(n.children || []), true) : (n.children && find(n.children)));
    find(nodes); return out;
  };
  const collectNodes = (ids) => { const set = new Set(ids), out = []; const w = (items) => items.forEach(n => { if (set.has(n.id)) out.push(n); if (n.children) w(n.children); }); w(nodes); return out; };
  const posFor = (node, e, into) => {
    const r = e.currentTarget.getBoundingClientRect(), y = (e.clientY - r.top) / r.height;
    const canInto = into && !flat && (node.children !== undefined || node.folder);
    return canInto ? (y < 0.28 ? 'before' : y > 0.72 ? 'after' : 'into') : (y < 0.5 ? 'before' : 'after');
  };

  const onRowDragStart = (id, e) => {
    const ids = selSet.has(id) ? [...selSet] : [id];
    if (!selSet.has(id)) { setAnchor(id); emit(new Set([id]), id); }
    if (reorderable) dragRef.current = ids;
    if (dnd) dnd.begin({ type: dragType, items: collectNodes(ids) });
    e.dataTransfer.effectAllowed = 'copyMove';
    try { e.dataTransfer.setData('text/plain', ids.join(',')); } catch (_) {}
  };
  const onRowDragOver = (node, e) => {
    const ids = dragRef.current;
    if (ids) {                                       // internal reorder
      e.preventDefault(); e.dataTransfer.dropEffect = 'move';
      if (ids.includes(node.id)) { setDrop(null); return; }
      let pos = posFor(node, e, true);
      if (pos === 'into') { const d = descendantsOf(node.id); if (ids.some(i => d.has(i))) pos = 'after'; }
      setDrop({ id: node.id, pos, mode: 'move' });
      return;
    }
    const pl = dnd && dnd.ref.current;               // external typed drop
    if (accept && onDropItems && pl && dndAccepts(accept, pl.type)) {
      e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
      setDrop({ id: node.id, pos: posFor(node, e, true), mode: 'drop' });
    }
  };
  const onDropEnd = (e) => {
    if (e) e.preventDefault();
    const ids = dragRef.current, d = drop; dragRef.current = null; setDrop(null);
    if (d) {
      if (d.mode === 'move' && ids && onMove) onMove(ids, d.id, d.pos);
      else if (d.mode === 'drop' && onDropItems) { const pl = dnd && dnd.ref.current; if (pl) onDropItems(pl, { targetId: d.id, pos: d.pos }); }
    }
    if (dnd) dnd.end();
  };
  const onDragEndClear = () => { dragRef.current = null; setDrop(null); if (dnd) dnd.end(); };

  const cCls = flat ? 'dcs-list' : 'dcs-tree';
  const rCls = flat ? 'dcs-list__item' : 'dcs-tree__row';
  const rows = flatRows.map(({ node, depth }) => {
    const isOpen = exp.has(node.id);
    const hasChildren = !flat && node.children && node.children.length;
    const dropCls = drop && drop.id === node.id ? ` ${rCls}--drop-${drop.pos}` : '';
    return (
      <div key={node.id} className={`${rCls}${dropCls}${draggable ? ` ${rCls}--draggable` : ''}`}
        style={flat ? undefined : { '--depth': depth }}
        aria-selected={selSet.has(node.id)}
        draggable={draggable || undefined}
        onDragStart={draggable ? (e) => onRowDragStart(node.id, e) : undefined}
        onDragOver={dropable ? (e) => onRowDragOver(node, e) : undefined}
        onDrop={dropable ? onDropEnd : undefined}
        onDragEnd={draggable ? onDragEndClear : undefined}
        onClick={(e) => { onRowClick(node.id, e); if (!multi && hasChildren) toggleExp(node.id); }}>
        {!flat && (
          <div className={`dcs-tree__chevron${isOpen ? ' dcs-tree__chevron--open' : ''}`}
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExp(node.id); }}>
            {hasChildren && <Icon name="chevron-right" size="sm" />}
          </div>
        )}
        {node.icon && <Icon className={flat ? undefined : 'dcs-tree__icon'} name={node.icon} />}
        <span className="dcs-tree__label">{node.label}</span>
        {node.meta && <span className="dcs-tree__meta">{node.meta}</span>}
        {node.actions && <div className="dcs-tree__actions">{node.actions}</div>}
      </div>
    );
  });
  return <div className={cCls} onDragOver={dropable ? (e) => e.preventDefault() : undefined} onDrop={dropable ? onDropEnd : undefined}>{rows}</div>;
}

/* Move dragged subtrees within a tree (or flat list). pos: before|after|into.
   Returns a new tree; safe for lists too (no children = no 'into'). */
function treeMove(roots, dragIds, targetId, pos) {
  const idset = new Set(dragIds);
  const clone = (typeof structuredClone === 'function') ? structuredClone(roots) : JSON.parse(JSON.stringify(roots));
  const removed = [];
  const prune = (list) => list.filter(n => {
    if (idset.has(n.id)) { removed.push(n); return false; }
    if (n.children) n.children = prune(n.children);
    return true;
  });
  const tree = prune(clone);
  if (!removed.length) return tree;
  if (pos === 'into') {
    const into = (list) => list.some(n => n.id === targetId ? (n.children = [...(n.children || []), ...removed], true) : (n.children && into(n.children)));
    into(tree);
  } else {
    const sib = (list) => {
      const i = list.findIndex(n => n.id === targetId);
      if (i >= 0) { list.splice(pos === 'before' ? i : i + 1, 0, ...removed); return true; }
      return list.some(n => n.children && sib(n.children));
    };
    sib(tree);
  }
  return tree;
}

/* Insert brand-new nodes into a tree at a target. pos: before|after|into.
   Used when an external typed drop (e.g. assets) lands on a tree. */
function treeInsert(roots, newNodes, targetId, pos) {
  const clone = (typeof structuredClone === 'function') ? structuredClone(roots) : JSON.parse(JSON.stringify(roots));
  if (pos === 'into') {
    const into = (list) => list.some(n => n.id === targetId ? (n.children = [...(n.children || []), ...newNodes], true) : (n.children && into(n.children)));
    if (!into(clone)) clone.push(...newNodes);
  } else {
    const sib = (list) => {
      const i = list.findIndex(n => n.id === targetId);
      if (i >= 0) { list.splice(pos === 'before' ? i : i + 1, 0, ...newNodes); return true; }
      return list.some(n => n.children && sib(n.children));
    };
    if (!sib(clone)) clone.push(...newNodes);
  }
  return clone;
}

/* ─────────── Color swatch / picker (simple) ─────────── */
function Swatch({ color, label }) {
  return (
    <div className="dcs-swatch">
      <div className="dcs-swatch__chip" style={{ '--c': color }} />
      <span>{label || color}</span>
    </div>
  );
}

/* ─────────── Foldout — Blender-style soft section ─────────── */
function Foldout({ title, icon, meta, tools, defaultOpen = true, children, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`dcs-foldout${!open ? ' dcs-foldout--collapsed' : ''}${accent ? ' dcs-foldout--accent' : ''}`}>
      <div className="dcs-foldout__header" onClick={() => setOpen(o => !o)}>
        <div className={`dcs-foldout__chevron${open ? ' dcs-foldout__chevron--open' : ''}`}>
          <Icon name="chevron-right" size="sm" />
        </div>
        {icon && <Icon className="dcs-foldout__icon" name={icon} size="sm" />}
        <div className="dcs-foldout__title">{title}</div>
        {meta && <div className="dcs-foldout__meta">{meta}</div>}
        {tools && <div className="dcs-foldout__tools" onClick={(e) => e.stopPropagation()}>{tools}</div>}
      </div>
      <div className="dcs-foldout__body">{children}</div>
    </div>
  );
}

function Foldouts({ children, style }) {
  return <div className="dcs-foldouts" style={style}>{children}</div>;
}

/* ─────────── SubPanel ─────────── */
function SubPanel({ title, icon, onClose, defaultOpen = true, children, tools }) {
  const [open, setOpen] = useState(defaultOpen);
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  return (
    <div className={`dcs-subpanel${!open ? ' dcs-subpanel--collapsed' : ''}`}>
      <div className="dcs-subpanel__header" onClick={() => setOpen(o => !o)}>
        <div className={`dcs-subpanel__chevron${open ? ' dcs-subpanel__chevron--open' : ''}`}>
          <Icon name="chevron-right" size="sm" />
        </div>
        {icon && <Icon name={icon} size="sm" style={{ width: 12, height: 12 }} />}
        <div className="dcs-subpanel__title">{title}</div>
        {tools && <div onClick={(e) => e.stopPropagation()}>{tools}</div>}
        <div className="dcs-subpanel__close" onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); else setClosed(true); }}>
          <Icon name="close" size="sm" />
        </div>
      </div>
      <div className="dcs-subpanel__body">{children}</div>
    </div>
  );
}

/* ─────────── DockPane — tabbed dockable view ─────────── */
function DockPane({ tabs, value, onChange, onClose, tools, children }) {
  const [closed, setClosed] = useState(new Set());
  const visible = tabs.filter(t => !closed.has(t.value));
  if (!visible.length) return null;
  return (
    <div className="dcs-dockpane">
      <div className="dcs-dockpane__tabs">
        {visible.map(t => (
          <div
            key={t.value}
            className="dcs-dockpane__tab"
            aria-selected={value === t.value}
            onClick={() => onChange(t.value)}
          >
            {t.icon && <Icon name={t.icon} size="sm" />}
            <span>{t.label}</span>
            {(t.closeable !== false) && (
              <div
                className="dcs-dockpane__tab-close"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClose) onClose(t.value);
                  else {
                    const n = new Set(closed); n.add(t.value); setClosed(n);
                    if (value === t.value && visible.length > 1) {
                      const next = visible.find(x => x.value !== t.value);
                      if (next) onChange(next.value);
                    }
                  }
                }}
              >
                <Icon name="close" size="sm" />
              </div>
            )}
          </div>
        ))}
        {tools && <div className="dcs-dockpane__tools">{tools}</div>}
      </div>
      <div className="dcs-dockpane__body">{children}</div>
    </div>
  );
}

/* ─────────── MenuBar ─────────── */
function MenuBar({ brand, items, menus, meta, onPick }) {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  useDismiss(ref, open !== null, () => setOpen(null));
  return (
    <div className="dcs-menubar" ref={ref}>
      {brand && (
        <div className="dcs-menubar__brand">
          {brand.icon && <Icon name={brand.icon} />}
          <span>{brand.label}</span>
        </div>
      )}
      {items && items.map(m => {
        const def = menus && menus[m];
        return (
          <div key={m} style={{ position: 'relative', display: 'flex' }}>
            <button
              className="dcs-menubar__item"
              aria-expanded={open === m}
              style={open === m ? { background: 'var(--dcs-surface-2)', color: 'var(--dcs-text)' } : null}
              onClick={() => def && setOpen(o => (o === m ? null : m))}
              onMouseEnter={() => def && setOpen(o => (o !== null ? m : o))}
            >{m}</button>
            {def && open === m && (
              <div className="dcs-menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 1 }}>
                <MenuList items={def} onPick={(v) => { onPick && onPick(m, v); setOpen(null); }} />
              </div>
            )}
          </div>
        );
      })}
      <div className="dcs-menubar__spacer" />
      {meta && <div className="dcs-menubar__meta">{meta}</div>}
    </div>
  );
}

/* ─────────── Card ─────────── */
function Card({
  title, meta, desc, media, badges, footer, tools, closeable, onClose,
  selected, onClick, horizontal, className = '', style, children,
}) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  const cls = [
    'dcs-card',
    horizontal && 'dcs-card--horizontal',
    onClick && 'dcs-card--clickable',
    className,
  ].filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      aria-selected={selected || undefined}
      onClick={onClick}
      style={style}
    >
      {badges && <div className="dcs-card__badges">{badges}</div>}
      {closeable && (
        <div
          className="dcs-card__close"
          onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); else setClosed(true); }}
        >
          <Icon name="close" />
        </div>
      )}
      {media && <div className="dcs-card__media">{media}</div>}
      {(title || meta || desc || children) && (
        <div className="dcs-card__body">
          {title && <div className="dcs-card__title">{title}</div>}
          {meta && <div className="dcs-card__meta">{meta}</div>}
          {desc && <div className="dcs-card__desc">{desc}</div>}
          {children}
        </div>
      )}
      {tools && <div className="dcs-card__tools">{tools}</div>}
      {footer && <div className="dcs-card__footer">{footer}</div>}
    </div>
  );
}

function CardGrid({ children, style }) {
  return <div className="dcs-card-grid" style={style}>{children}</div>;
}
function CardList({ children, style }) {
  return <div className="dcs-card-list" style={style}>{children}</div>;
}

Object.assign(window, {
  Panel, SubPanel, Foldout, Foldouts, Button, ButtonGroup, Slider, Fader, Knob, Combo,
  Check, Switch, Select, Tabs, Toolbar, ToolbarSep, Tree, treeMove, treeInsert, Swatch, DockPane, MenuBar,
  Card, CardGrid, CardList,
  useDismiss, MenuList, Splitter, DockLayout,
  DndProvider, DndContext, useDrag, useDrop, useDndPayload, Draggable, DropZone,
});


/* ===== tweaks-panel.jsx ===== */

// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "palette": ["#D97757", "#29261b", "#f6f4ef"],
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        options={['#D97757', '#2A6FDB', '#1F8A5B', '#7A5AE0']}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakColor  label="Palette" value={t.palette}
//                        options={[['#D97757', '#29261b', '#f6f4ef'],
//                                  ['#475569', '#0f172a', '#f1f5f9']]}
//                        onChange={(v) => setTweak('palette', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  @media (max-width: 860px){ .twk-panel{display:none} }
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:bottom right;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
  .twk-handle{position:fixed;right:16px;bottom:16px;z-index:2147483646;
    display:inline-flex;align-items:center;gap:6px;
    padding:8px 12px 8px 10px;
    background:#14161c;color:#e7e9ee;
    border:1px solid #2a2e38;border-radius:999px;
    font:600 11px/1 'IBM Plex Sans',ui-sans-serif,system-ui,sans-serif;
    letter-spacing:.05em;text-transform:uppercase;
    cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,.35);
    transition:transform 120ms ease,background 120ms ease,border-color 120ms ease}
  .twk-handle:hover{background:#1f222a;border-color:#4d9fff;transform:translateY(-1px)}
  .twk-handle svg{color:#4d9fff}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits }, '*');
    // Same-window signal so in-page listeners (deck-stage rail thumbnails)
    // can react — the parent message only reaches the host, not peers.
    window.dispatchEvent(new CustomEvent('tweakchange', { detail: edits }));
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <button
        className="twk-handle"
        data-omelette-chrome=""
        onClick={() => setOpen(true)}
        aria-label="Open tweaks"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span>Tweaks</span>
      </button>
    </>
  );
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-omelette-chrome=""
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">
          {children}
        </div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Segments wrap mid-word once per-segment width runs out. The track is
  // ~248px (280 panel − 28 body pad − 4 seg pad), each button loses 12px
  // to its own padding, and 11.5px system-ui averages ~6.3px/char — so 2
  // options fit ~16 chars each, 3 fit ~10. Past that (or >3 options), fall
  // back to a dropdown rather than wrap.
  const labelLen = (o) => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({ 2: 16, 3: 10 }[options.length] ?? 0);
  if (!fitsAsSegments) {
    // <select> emits strings — map back to the original option value so the
    // fallback stays type-preserving (numbers, booleans) like the segment path.
    const resolve = (s) => {
      const m = options.find((o) => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return <TweakSelect label={label} value={value} options={options}
                        onChange={(s) => onChange(resolve(s))} />;
  }
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

// Relative-luminance contrast pick — checkmarks drawn over a swatch need to
// read on both #111 and #fafafa without per-option configuration. Hex input
// only (#rgb / #rrggbb); named or rgb()/hsl() colors fall through to "light".
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const __TwkCheck = ({ light }) => (
  <svg viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round"
          stroke={light ? 'rgba(0,0,0,.78)' : '#fff'} />
  </svg>
);

// TweakColor — curated color/palette picker. Each option is either a single
// hex string or an array of 1-5 hex strings; the card adapts — a lone color
// renders solid, a palette renders colors[0] as the hero (left ~2/3) with the
// rest stacked in a sharp column on the right. onChange emits the
// option in the shape it was passed (string stays string, array stays array).
// Without options it falls back to the native color input for back-compat.
function TweakColor({ label, value, options, onChange }) {
  if (!options || !options.length) {
    return (
      <div className="twk-row twk-row-h">
        <div className="twk-lbl"><span>{label}</span></div>
        <input type="color" className="twk-swatch" value={value}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // Native <input type=color> emits lowercase hex per the HTML spec, so
  // compare case-insensitively. String() guards JSON.stringify(undefined),
  // which returns the primitive undefined (no .toLowerCase).
  const key = (o) => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return (
    <TweakRow label={label}>
      <div className="twk-chips" role="radiogroup">
        {options.map((o, i) => {
          const colors = Array.isArray(o) ? o : [o];
          const [hero, ...rest] = colors;
          const sup = rest.slice(0, 4);
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" role="radio"
                    aria-checked={on} data-on={on ? '1' : '0'}
                    aria-label={colors.join(', ')} title={colors.join(' · ')}
                    style={{ background: hero }}
                    onClick={() => onChange(o)}>
              {sup.length > 0 && (
                <span>
                  {sup.map((c, j) => <i key={j} style={{ background: c }} />)}
                </span>
              )}
              {on && <__TwkCheck light={__twkIsLight(hero)} />}
            </button>
          );
        })}
      </div>
    </TweakRow>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


/* ===== sections-foundations.jsx ===== */
/* sections-foundations.jsx
   Colors · Type · Spacing · Icons
*/
const { useState: useStateF } = React;

function Demo({ children, caption, checker, inset, defaultStyle = 'flat', defaultDensity, noDensity, frame = 'panel', minw }) {
  const [mode, setMode] = useStateF(defaultStyle);
  const [density, setDensity] = useStateF(defaultDensity);
  // When density/style is unset, let the global Tweaks cascade through.
  // Only emit the attribute when the user has actively chosen a value here.
  const demoAttrs = {
    'data-dcs-demo': '',
  };
  if (density) demoAttrs['data-dcs-density'] = density;
  if (mode === '3d') demoAttrs['data-dcs-style'] = '3d';
  // `minw` keeps multi-pane demos at their natural width on small screens —
  // the demo box scrolls horizontally (see .dw-demo on mobile) instead of
  // the panes collapsing into a single cramped column.
  return (
    <div style={{ marginBottom: 32 }}>
      <div className={`dw-demo dw-demo--${frame}${checker ? ' dw-demo--checker' : ''}${inset ? ' dw-demo--inset' : ''}`}>
        <div className="dcs" {...demoAttrs} style={minw ? { minWidth: minw } : undefined}>{children}</div>
      </div>
      <div className="dw-demo__caption" style={{ flexWrap: 'wrap' }}>
        <DemoToggle label="Style" value={mode} onChange={setMode} options={[
          { value: 'flat', label: 'Flat' },
          { value: '3d', label: 'Synth' },
        ]} />
        {!noDensity && (
          <DemoToggle label="Density" value={density || ''} onChange={setDensity} options={[
            { value: '', label: 'auto' },
            { value: 'compact', label: 'sm' },
            { value: 'comfortable', label: 'md' },
            { value: 'spacious', label: 'lg' },
          ]} />
        )}
        {caption && <span style={{ marginLeft: 4 }}>// {caption}</span>}
      </div>
    </div>
  );
}

function DemoToggle({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '2px 4px 2px 8px', background: 'var(--dw-bg-soft)', border: '1px solid var(--dw-line)', borderRadius: 999 }}>
      <span style={{ fontSize: 11, color: 'var(--dw-text-mute)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--dw-font-mono)' }}>{label}</span>
      <div style={{ display: 'inline-flex', background: 'var(--dw-bg)', borderRadius: 999, padding: 2, gap: 1, border: '1px solid var(--dw-line)' }}>
        {options.map(opt => (
          <button key={opt.value} onClick={() => onChange(opt.value)} style={{
            border: 'none', background: value === opt.value ? '#14161c' : 'transparent',
            color: value === opt.value ? '#fff' : 'var(--dw-text-dim)',
            fontFamily: 'var(--dw-font-mono)', fontSize: 11, padding: '3px 10px',
            borderRadius: 999, cursor: 'pointer',
          }}>{opt.label}</button>
        ))}
      </div>
    </div>
  );
}

function CodeBlock({ lang, code }) {
  const html = highlight(code, lang || 'css');
  const [copied, setCopied] = useStateF(false);
  const copy = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1400); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => {});
    } else { done(); }
  };
  return (
    <div className="dw-code">
      <button className="dw-code__copy" onClick={copy} aria-label="Copy code to clipboard">
        <Icon name={copied ? 'check' : 'copy'} size="sm" /> {copied ? 'Copied' : 'Copy'}
      </button>
      <pre><code dangerouslySetInnerHTML={{ __html: html }} /></pre>
    </div>
  );
}

// Tiny regex-based highlighter, just for visual rhythm
function highlight(code, lang) {
  let s = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  if (lang === 'html') {
    s = s.replace(/(&lt;\/?)([\w-]+)/g, '$1<span class="tk-t">$2</span>')
         .replace(/\s([\w-]+)=&quot;([^&]*?)&quot;/g, ' <span class="tk-k">$1</span>=<span class="tk-s">&quot;$2&quot;</span>');
  } else if (lang === 'css') {
    s = s.replace(/(\/\*[\s\S]*?\*\/)/g, '<span class="tk-c">$1</span>')
         .replace(/(--[\w-]+)/g, '<span class="tk-v">$1</span>')
         .replace(/([\.\#][\w-]+)/g, '<span class="tk-t">$1</span>')
         .replace(/(#[0-9a-f]{3,8})\b/gi, '<span class="tk-s">$1</span>')
         .replace(/\b(\d+(\.\d+)?(px|rem|em|%|deg|s|ms)?)\b/g, '<span class="tk-n">$1</span>');
  } else if (lang === 'js' || lang === 'jsx') {
    // Keyword pass first, THEN wrap comments — otherwise the keyword regex
    // matches "class" inside an inserted <span class="tk-c"> and corrupts it.
    s = s.replace(/\b(const|let|var|function|return|if|else|for|import|from|export|new|class)\b/g, '<span class="tk-k">$1</span>')
         .replace(/(\/\/[^\n]*)/g, '<span class="tk-c">$1</span>')
         .replace(/&quot;([^&]*?)&quot;/g, '<span class="tk-s">&quot;$1&quot;</span>')
         .replace(/\b(\d+(\.\d+)?)\b/g, '<span class="tk-n">$1</span>');
  }
  return s;
}

/* ─────────── Colors ─────────── */
function SectionColors() {
  const palette = [
    { name: '--dcs-bg-app',     v: '#1f222a', note: 'Window / app shell' },
    { name: '--dcs-bg',         v: '#2a2e38', note: 'Default panel surface' },
    { name: '--dcs-surface-1',  v: '#323744', note: 'Raised' },
    { name: '--dcs-surface-2',  v: '#3c424f', note: 'Hover · raised x2' },
    { name: '--dcs-surface-3',  v: '#474e5d', note: 'Active · pressed' },
    { name: '--dcs-surface-4',  v: '#555d6e', note: 'Inset · cap' },
    { name: '--dcs-well',       v: '#20232b', note: 'Sunken inputs' },
    { name: '--dcs-line',       v: '#14161c', note: 'Hard separator' },
  ];
  const accents = [
    { name: '--dcs-accent',    v: '#4d9fff', note: 'DCC blue · key signal' },
    { name: '--dcs-accent-hi', v: '#6fb3ff', note: 'Hover' },
    { name: '--dcs-accent-lo', v: '#2f86ee', note: 'Active' },
    { name: '--dcs-ok',        v: '#4ed18a', note: 'Success · armed' },
    { name: '--dcs-warn',      v: '#f2b14a', note: 'Warning · clipping' },
    { name: '--dcs-danger',    v: '#ef6b6b', note: 'Error · destructive' },
    { name: '--dcs-purple',    v: '#b48cff', note: 'Aux · MIDI' },
    { name: '--dcs-teal',      v: '#4ad5d5', note: 'Aux · channel' },
  ];
  const Swatchy = ({ name, v, note, light }) => (
    <div className={`palette-swatch${light ? ' palette-swatch--light' : ''}`}>
      <div className="palette-swatch__chip" style={{ background: v }} />
      <div className="palette-swatch__meta">
        <div className="palette-swatch__hex">{v}</div>
        <div className="palette-swatch__name">{name}</div>
        <div className="palette-swatch__note">{note}</div>
      </div>
    </div>
  );
  return (
    <section className="dw-section" id="colors">
      <div className="dw-section__eyebrow">Foundations · 01</div>
      <h2>Color</h2>
      <p className="dw-section__lead">
        A grayscale rail with a single bright signal color. The grays bias slightly cool to keep the
        interface feeling crisp under long sessions; the accent is the only saturated color you ever
        see in chrome — destinations earn their visibility.
      </p>

      <div className="dw-subhead"><h3>Surfaces</h3><div className="dw-subhead__meta">8 tokens · WCAG-safe pairs</div></div>
      <div className="palette-grid">
        {palette.map(p => <Swatchy key={p.name} {...p} />)}
      </div>

      <div className="dw-subhead"><h3>Accent &amp; semantic</h3><div className="dw-subhead__meta">Use sparingly</div></div>
      <div className="palette-grid">
        {accents.map(p => <Swatchy key={p.name} {...p} light />)}
      </div>

      <div className="dw-subhead"><h3>In context</h3><div className="dw-subhead__meta">Compose, don't decorate</div></div>
      <Demo caption="Buttons inherit token relationships — no per-component overrides needed.">
        <div className="dcs-row" style={{ flexWrap: 'wrap', gap: 8 }}>
          <Button primary iconLeft="play">Render</Button>
          <Button iconLeft="save">Save</Button>
          <Button ghost iconLeft="cog">Settings</Button>
          <Button danger iconLeft="trash">Delete</Button>
          <Button pressed iconLeft="magnet">Snap</Button>
        </div>
      </Demo>
    </section>
  );
}

/* ─────────── Type ─────────── */
function SectionType() {
  return (
    <section className="dw-section" id="type">
      <div className="dw-section__eyebrow">Foundations · 02</div>
      <h2>Type</h2>
      <p className="dw-section__lead">
        IBM Plex Sans for chrome and prose; JetBrains Mono for numerics, codes, addresses,
        coordinates, and anything users will want to read at a glance and trust.
      </p>

      <div className="dw-subhead"><h3>Scale</h3><div className="dw-subhead__meta">Density-aware</div></div>
      <Demo>
        <div className="type-spec">
          <div className="type-row"><div className="type-row__label dcs-mono">3xl · 32 / 1.15</div><div style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-.02em' }}>Decius shapes the cursor.</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">2xl · 24 / 1.2</div><div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.015em' }}>Panel titles &amp; modal headers</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">xl  · 18 / 1.3</div><div style={{ fontSize: 18, fontWeight: 500 }}>Section headlines</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">lg  · 15 / 1.45</div><div style={{ fontSize: 15 }}>Body text in the docs site</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">md  · 13 / 1.45</div><div style={{ fontSize: 13 }}>Default control text in DCC</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">sm  · 11 / 1.4</div><div style={{ fontSize: 11 }}>Labels, tab text, hints</div></div>
          <div className="type-row"><div className="type-row__label dcs-mono">xs  · 10 / 1.3</div><div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--dcs-text-mute)' }}>EYEBROWS · STATUS · METRICS</div></div>
        </div>
      </Demo>

      <div className="dw-subhead"><h3>Numerics</h3><div className="dw-subhead__meta">tabular-nums, .04em</div></div>
      <Demo caption="Numbers are mono-spaced, tabular, lining figures — values can be compared by column.">
        <div className="dcs-mono" style={{ fontSize: 18, color: 'var(--dcs-text)', letterSpacing: '.02em' }}>
          <div>0.000  ·  +1.428  ·  −0.952  ·  ∞</div>
          <div style={{ marginTop: 8, color: 'var(--dcs-accent)' }}>440.00 Hz  ·  −18.2 dB  ·  120.00 BPM</div>
          <div style={{ marginTop: 8, color: 'var(--dcs-text-dim)' }}>X 1.0000  Y −0.4321  Z  3.1415</div>
        </div>
      </Demo>
    </section>
  );
}

/* ─────────── Spacing / Density ─────────── */
function SectionSpacing() {
  const [d, setD] = useStateF('comfortable');
  return (
    <section className="dw-section" id="spacing">
      <div className="dw-section__eyebrow">Foundations · 03</div>
      <h2>Spacing &amp; density</h2>
      <p className="dw-section__lead">
        A 4-pixel grid with three density modes. Compact for power users on large monitors;
        comfortable as the default; spacious for trackpad navigation and presentation.
      </p>
      <Demo caption="Switch modes — every control re-fits without changing its visual relationships.">
        <div className="dcs-row" style={{ marginBottom: 16 }}>
          <ButtonGroup value={d} onChange={setD} options={[
            { value: 'compact', label: 'Compact' },
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'spacious', label: 'Spacious' },
          ]} />
        </div>
        <div className="dcs" data-dcs-density={d} style={{ background: 'transparent' }}>
          <div className="dcs-row" style={{ flexWrap: 'wrap', gap: 'var(--dcs-s-3)' }}>
            <Button iconLeft="cube">Mesh</Button>
            <Button iconLeft="light">Light</Button>
            <Button iconLeft="camera">Camera</Button>
            <Button primary iconLeft="render">Render</Button>
            <div className="dcs-divider--v" />
            <Check checked>Auto-key</Check>
            <Switch checked />
          </div>
        </div>
      </Demo>
    </section>
  );
}

/* ─────────── Icons catalog ─────────── */
function SectionIcons() {
  const [q, setQ] = useStateF('');
  const lower = q.toLowerCase();
  return (
    <section className="dw-section" id="icons">
      <div className="dw-section__eyebrow">Foundations · 04</div>
      <h2>Icons</h2>
      <p className="dw-section__lead">
        225 icons drawn at 24px on a 1.25px stroke, shipped as a web font. One glyph is just
        <code>&lt;i class="di di-cube"&gt;</code>. They inherit <code>currentColor</code> and
        font-size, so they work the same in chrome, signals, and tinted states.
      </p>

      <div className="dw-row" style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <div className="dw-search" style={{ width: 320 }}>
          <Icon name="search" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filter icons by name…"
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'inherit', fontSize: 13 }}
          />
        </div>
        <div className="dw-meta">{ICON_CATALOG.reduce((n, g) => n + g.icons.length, 0)} total</div>
      </div>

      <div className="icon-catalog">
        {ICON_CATALOG.map(group => {
          const filtered = group.icons.filter(i => !lower || i.includes(lower));
          if (!filtered.length) return null;
          return (
            <div key={group.group} className="icon-catalog__group">
              <div className="icon-catalog__group-name">{group.group}</div>
              <div className="icon-catalog__grid">
                {filtered.map(name => (
                  <div key={name} className="icon-cell" title={name}>
                    <div className="icon-cell__icon"><Icon name={name} size="lg" /></div>
                    <div className="icon-cell__name">{name}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

Object.assign(window, {
  Demo, CodeBlock,
  SectionColors, SectionType, SectionSpacing, SectionIcons,
});


/* ===== sections-components.jsx ===== */
/* sections-components.jsx
   Buttons · Inputs · Sliders · Knobs · Combo · Checks · Switches · Toolbar
*/
const { useState: useStateC } = React;

function SectionButtons() {
  const [pressed, setPressed] = useStateC({ snap: true, sym: false, prop: false });
  const [tool, setTool] = useStateC('select');
  const [snap, setSnap] = useStateC(true);
  const TOOLS = (sz) => (
    <>
      {['select', 'move', 'rotate', 'scale-corners'].map(t => (
        <Button key={t} ghost icon iconLeft={t} pressed={tool === t} onClick={() => setTool(t)} />
      ))}
      <ToolbarSep />
      {['brush', 'eraser', 'fill'].map(t => (
        <Button key={t} ghost icon iconLeft={t} pressed={tool === t} onClick={() => setTool(t)} />
      ))}
      <ToolbarSep />
      <Button ghost icon iconLeft="magnet" pressed={snap} onClick={() => setSnap(s => !s)} />
    </>
  );
  return (
    <section className="dw-section" id="buttons">
      <div className="dw-section__eyebrow">Components · 01</div>
      <h2>Buttons</h2>
      <p className="dw-section__lead">
        Five intents — default, primary, ghost, danger, toggle — across three sizes. All share the same bevel
        recipe so they stack on a toolbar without breaking rhythm.
      </p>

      <Demo caption="Intents">
        <div className="dcs-row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Button>Default</Button>
          <Button primary>Primary</Button>
          <Button ghost>Ghost</Button>
          <Button danger>Danger</Button>
          <Button disabled>Disabled</Button>
          <Button pressed>Pressed</Button>
        </div>
      </Demo>

      <Demo caption="With icons, three sizes">
        <div className="dcs-col">
          <div className="dcs-row" style={{ gap: 10 }}>
            <Button sm iconLeft="play">Render</Button>
            <Button sm icon iconLeft="save" />
            <Button sm ghost iconLeft="cog">Prefs</Button>
            <Button sm primary iconLeft="export">Export</Button>
          </div>
          <div className="dcs-row" style={{ gap: 10 }}>
            <Button iconLeft="play">Render</Button>
            <Button icon iconLeft="save" />
            <Button ghost iconLeft="cog">Prefs</Button>
            <Button primary iconLeft="export">Export</Button>
          </div>
          <div className="dcs-row" style={{ gap: 10 }}>
            <Button lg iconLeft="play">Render</Button>
            <Button lg icon iconLeft="save" />
            <Button lg ghost iconLeft="cog">Prefs</Button>
            <Button lg primary iconLeft="export">Export</Button>
          </div>
        </div>
      </Demo>

      <Demo caption="Segmented button group — for mutually exclusive modes">
        <div className="dcs-col">
          <ButtonGroup
            value={pressed.snap}
            onChange={v => setPressed(p => ({ ...p, snap: v }))}
            options={[
              { value: false, icon: 'cube', label: 'Vertex' },
              { value: true,  icon: 'array', label: 'Grid' },
              { value: 'edge', icon: 'spline', label: 'Edge' },
              { value: 'face', icon: 'plane', label: 'Face' },
            ]}
          />
          <ButtonGroup
            value={pressed.sym}
            onChange={v => setPressed(p => ({ ...p, sym: v }))}
            options={[
              { value: 'x', label: 'X' },
              { value: 'y', label: 'Y' },
              { value: 'z', label: 'Z' },
            ]}
          />
        </div>
      </Demo>

      <Demo caption="One uniform toolbar — sm / md / lg. Click a tool: selected = solid accent, dark icon, rounded.">
        <div className="dcs-col" style={{ gap: 12 }}>
          <Toolbar size="sm">{TOOLS('sm')}<div style={{ flex: 1 }} /><Button ghost icon iconLeft="undo" /><Button ghost icon iconLeft="redo" /></Toolbar>
          <Toolbar>{TOOLS('md')}<div style={{ flex: 1 }} /><Button ghost icon iconLeft="undo" /><Button ghost icon iconLeft="redo" /></Toolbar>
          <Toolbar size="lg">{TOOLS('lg')}<div style={{ flex: 1 }} /><Button ghost icon iconLeft="undo" /><Button ghost icon iconLeft="redo" /></Toolbar>
        </div>
      </Demo>

      <Demo caption="Vertical rail + a floating overlay variant — same toolbar, two placements">
        <div className="dcs-row" style={{ gap: 24, alignItems: 'flex-start' }}>
          <Toolbar vertical>
            {['select', 'move', 'rotate', 'scale-corners'].map(t => (
              <Button key={t} ghost icon iconLeft={t} pressed={tool === t} onClick={() => setTool(t)} />
            ))}
            <ToolbarSep />
            <Button ghost icon iconLeft="magnet" pressed={snap} onClick={() => setSnap(s => !s)} />
          </Toolbar>
          <div style={{ position: 'relative', flex: 1, minHeight: 120, borderRadius: 'var(--dcs-r-3)', background: 'radial-gradient(ellipse at 50% 40%, #3a4054, #161922 80%)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 10, left: 10 }}>
              <Toolbar floating size="sm">{TOOLS('sm')}</Toolbar>
            </div>
            <span style={{ position: 'absolute', bottom: 8, right: 12, fontSize: 11, color: 'var(--dcs-text-mute)', fontFamily: 'var(--dcs-font-mono)' }}>floating overlay</span>
          </div>
        </div>
      </Demo>
    </section>
  );
}

function SectionInputs() {
  const [name, setName] = useStateC('untitled.dcs');
  const [freq, setFreq] = useStateC(440);
  const [pos, setPos] = useStateC({ x: 1.428, y: -0.952, z: 3.000 });
  const [color, setColor] = useStateC('#4d9fff');
  const [renderer, setRenderer] = useStateC('cyc');
  const [quality, setQuality] = useStateC('high');
  const [vol, setVol] = useStateC(0.62);
  const [bake, setBake] = useStateC(true);
  const [live, setLive] = useStateC(true);
  const [mode, setMode] = useStateC('form'); // 'form' | 'props'
  const wrapClass = mode === 'props' ? 'dcs-props' : 'dcs-form';
  return (
    <section className="dw-section" id="inputs">
      <div className="dw-section__eyebrow">Components · 02</div>
      <h2>Text &amp; number fields</h2>
      <p className="dw-section__lead">
        Sunken inputs with mono numerics by default. The well shadow signals "type here" without
        a placeholder label fight.
      </p>

      <p className="dw-section__lead" style={{ marginTop: 24 }}>
        Wrap a stack of <code>.dcs-field</code> rows in one of two layouts.
        Use <code>.dcs-form</code> for <strong>dialog boxes and modal-like panels</strong> — labels
        right-justified, controls at their natural width, bare buttons self-centered, with an optional
        <code>.dcs-form__actions</code> footer for grouped buttons. Use <code>.dcs-props</code> for
        <strong> property inspectors and channel editors</strong> — labels left-justified, controls
        (including bare buttons) stretched to fill the row so a tall stack reads as even, same-size
        channel rows. Toggle the mode to see the same content rearrange.
      </p>

      <Demo frame="app" caption={mode === 'form' ? '.dcs-form — dialog/modal, labels right, controls natural width' : '.dcs-props — inspector, labels left, channels stretch'}>
        <div className="dcs-col" style={{ gap: 16 }}>
          <div className="dcs-btn-group" role="tablist" style={{ alignSelf: 'flex-start' }}>
            <button className={`dcs-btn ${mode === 'form' ? 'dcs-btn--primary' : ''}`}
                    onClick={() => setMode('form')} role="tab" aria-selected={mode === 'form'}>
              .dcs-form
            </button>
            <button className={`dcs-btn ${mode === 'props' ? 'dcs-btn--primary' : ''}`}
                    onClick={() => setMode('props')} role="tab" aria-selected={mode === 'props'}>
              .dcs-props
            </button>
          </div>

          <div className={wrapClass} style={{ maxWidth: 460 }}>
            <div className="dcs-field">
              <label className="dcs-field__label">Project</label>
              <input className="dcs-input" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Notes</label>
              <textarea className="dcs-textarea" defaultValue="// safe to render — caches warm" />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Frequency</label>
              <Combo value={freq} onChange={setFreq} min={20} max={20000} step={0.1} format={v => `${v.toFixed(1)} Hz`} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Position</label>
              <Combo unbounded value={pos.x} onChange={v => setPos(p => ({ ...p, x: v }))} step={0.001} label="X" format={v => v.toFixed(3)} />
              <Combo unbounded value={pos.y} onChange={v => setPos(p => ({ ...p, y: v }))} step={0.001} label="Y" format={v => v.toFixed(3)} />
              <Combo unbounded value={pos.z} onChange={v => setPos(p => ({ ...p, z: v }))} step={0.001} label="Z" format={v => v.toFixed(3)} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Color</label>
              <ColorField value={color} onChange={setColor} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Renderer</label>
              <Select value={renderer} onChange={setRenderer} options={[
                { value: 'cyc', label: 'Cycles · Pathtraced', icon: 'render' },
                { value: 'eev', label: 'Eevee · Realtime',    icon: 'play' },
                { value: 'wf',  label: 'Wireframe only',      icon: 'view-wire' },
              ]} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Quality</label>
              <ButtonGroup value={quality} onChange={setQuality} options={[
                { value: 'low',  label: 'Low'  },
                { value: 'med',  label: 'Med'  },
                { value: 'high', label: 'High' },
                { value: 'ult',  label: 'Ult'  },
              ]} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Volume</label>
              <Slider value={vol} onChange={setVol} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Auto-bake</label>
              <Check checked={bake} onChange={setBake} />
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Live preview</label>
              <Switch checked={live} onChange={setLive} />
            </div>

            {/* Lists, trees, and tables inside a field flip vertical: prompt
                ABOVE, control below at full row width in .dcs-props or 2/3
                width centered in .dcs-form. Default height 4 channel rows,
                framed in a sunken well, scrollable. */}
            <div className="dcs-field">
              <label className="dcs-field__label">Linked assets</label>
              <div className="dcs-list">
                {[['intro.scene', 'cube'], ['hero.mat', 'palette'], ['sky_4k.hdr', 'image'], ['walk.anim', 'curve'], ['notes.md', 'file']].map(([n, ic], i) => (
                  <div key={n} className="dcs-list__item" aria-selected={i === 0}>
                    <Icon name={ic} /><span style={{ flex: 1 }}>{n}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="dcs-field">
              <label className="dcs-field__label">Scene hierarchy</label>
              <Tree
                expanded={new Set(['root', 'group'])}
                selected="b"
                nodes={[{
                  id: 'root', label: 'Project', icon: 'folder-open', children: [
                    { id: 'group', label: 'Group', icon: 'group', children: [
                      { id: 'a', label: 'Node A', icon: 'cube' },
                      { id: 'b', label: 'Node B', icon: 'cube' },
                      { id: 'c', label: 'Node C', icon: 'sphere' },
                    ] },
                    { id: 'cam', label: 'Camera', icon: 'camera' },
                    { id: 'light', label: 'Light', icon: 'light' },
                  ],
                }]}
              />
            </div>

            {/* Full-width text labels — no prompt, span the row. Use .dcs-note
                for lightweight info/warning text, or drop a .dcs-alert into
                the stack for the heavier "alert panel" treatment. */}
            <div className="dcs-note">Disk is 92% full — cleanup recommended.</div>

            <div className="dcs-alert dcs-alert--warn">
              <div className="dcs-alert__icon"><Icon name="alert" /></div>
              <div className="dcs-alert__body">You have 3 uncommitted files — save or stash before switching projects.</div>
            </div>
            {/* Bare button — in .dcs-props it stretches as a channel widget;
                in .dcs-form it keeps natural size and self-centers. */}
            <button className="dcs-btn dcs-btn--primary">Apply</button>

            {/* Paired buttons — `.dcs-btn-row` centers them in .dcs-form
                and splits them evenly across the channel column in
                .dcs-props. */}
            <div className="dcs-btn-row">
              <button className="dcs-btn">Cancel</button>
              <button className="dcs-btn dcs-btn--primary">Close</button>
            </div>
          </div>

          <div className="dw-note" style={{ paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.08)' }}>
            <p>Standalone <code>&lt;Check&gt;</code> with text — outside a form/props stack — defaults to check-left, text-right:</p>
            <Check checked={bake} onChange={setBake}>Bundle textures with project</Check>
          </div>
        </div>
      </Demo>
    </section>
  );
}

function SectionSliders() {
  const [vol, setVol] = useStateC(0.62);
  const [pan, setPan] = useStateC(-0.2);
  const [time, setTime] = useStateC(0.4);
  const [fader1, setFader1] = useStateC(0.75);
  const [fader2, setFader2] = useStateC(0.5);
  const [fader3, setFader3] = useStateC(0.3);
  const [fader4, setFader4] = useStateC(0.85);

  return (
    <section className="dw-section" id="sliders">
      <div className="dw-section__eyebrow">Components · 03</div>
      <h2>Sliders &amp; faders</h2>
      <p className="dw-section__lead">
        Horizontal sliders for parameters with continuous semantics. Bipolar variant for centered
        ranges. Vertical faders for synth-style mix surfaces.
      </p>

      <Demo caption="DCC-style horizontal slider">
        <div className="dcs-col" style={{ maxWidth: 420, gap: 10 }}>
          <div className="dcs-field">
            <label className="dcs-field__label">Roughness</label>
            <Slider value={vol} onChange={setVol} />
            <span className="dcs-mono" style={{ width: 50, textAlign: 'right' }}>{vol.toFixed(3)}</span>
          </div>
          <div className="dcs-field">
            <label className="dcs-field__label">Pan</label>
            <Slider value={pan} onChange={setPan} min={-1} max={1} bipolar ticks={[-1, 0, 1]} />
            <span className="dcs-mono" style={{ width: 50, textAlign: 'right' }}>{pan >= 0 ? '+' : ''}{pan.toFixed(2)}</span>
          </div>
          <div className="dcs-field">
            <label className="dcs-field__label">Timeline</label>
            <Slider value={time} onChange={setTime} ticks={Array.from({ length: 9 }, (_, i) => i/8)} />
            <span className="dcs-mono" style={{ width: 50, textAlign: 'right' }}>{Math.round(time * 240)}f</span>
          </div>
        </div>
      </Demo>

      <Demo caption="Synth fader column — drag vertically">
        <div className="dcs-row" style={{ alignItems: 'flex-end', gap: 24, padding: '12px 0' }}>
          {[
            { v: fader1, set: setFader1, l: 'KCK' },
            { v: fader2, set: setFader2, l: 'SNR' },
            { v: fader3, set: setFader3, l: 'HAT' },
            { v: fader4, set: setFader4, l: 'BUS' },
          ].map(f => (
            <div key={f.l} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 54 }}>
              <span className="dcs-mono dcs-accent-text" style={{ fontSize: 11, width: 54, textAlign: 'center' }}>
                {Math.round((f.v - 0.75) * 60)} dB
              </span>
              <Fader value={f.v} onChange={f.set} />
              <span className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-text-dim)', letterSpacing: '.08em' }}>{f.l}</span>
            </div>
          ))}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--dcs-line)', margin: '0 8px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 54 }}>
            <span className="dcs-mono dcs-accent-text" style={{ fontSize: 11, color: 'var(--dcs-ok)', width: 54, textAlign: 'center' }}>
              -3 dB
            </span>
            <Fader value={0.92} />
            <span className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-text)', letterSpacing: '.08em' }}>MASTER</span>
          </div>
        </div>
      </Demo>
    </section>
  );
}

function SectionKnobs() {
  const [k, setK] = useStateC({ cutoff: 0.65, res: 0.3, drive: 0.2, env: 0.5, freq: 0.4, mix: 0.5, fb: 0.25 });
  const upd = (key, v) => setK(o => ({ ...o, [key]: v }));
  return (
    <section className="dw-section" id="knobs">
      <div className="dw-section__eyebrow">Components · 04</div>
      <h2>Knobs</h2>
      <p className="dw-section__lead">
        Synth-style rotary controls. Drag vertically to change; hold <code>Shift</code> for fine
        adjustment. The arc fills counter-clockwise from <code>−135°</code>, or radiates from
        center in bipolar mode.
      </p>

      <Demo caption="Three sizes · 40 / 56 / 72">
        <div className="dcs-row" style={{ gap: 36, padding: '16px 8px 12px', flexWrap: 'wrap' }}>
          <Knob value={k.cutoff} onChange={v => upd('cutoff', v)} size={40} label="CUTOFF" format={v => `${Math.round(v * 22050)}Hz`} />
          <Knob value={k.res} onChange={v => upd('res', v)} size={56} label="RESO" format={v => `${(v * 100).toFixed(0)}%`} />
          <Knob value={k.drive} onChange={v => upd('drive', v)} size={72} label="DRIVE" format={v => `${(v * 24).toFixed(1)}dB`} />
        </div>
      </Demo>

      <Demo caption="Bipolar — for ±N parameters">
        <div className="dcs-row" style={{ gap: 36, padding: '16px 8px 12px', flexWrap: 'wrap' }}>
          <Knob value={k.env}  onChange={v => upd('env', v)}  size={56} label="ENV ±" bipolar format={v => `${((v - 0.5) * 2).toFixed(2)}`} />
          <Knob value={k.freq} onChange={v => upd('freq', v)} size={56} label="DETUNE" bipolar format={v => `${((v - 0.5) * 100).toFixed(0)}ct`} />
          <Knob value={k.mix}  onChange={v => upd('mix', v)}  size={56} label="WET/DRY" bipolar format={v => v < 0.5 ? `DRY ${Math.round((0.5 - v) * 200)}` : `WET ${Math.round((v - 0.5) * 200)}`} />
          <Knob value={k.fb}   onChange={v => upd('fb', v)}   size={56} label="F/BACK" format={v => `${(v * 100).toFixed(0)}%`} />
        </div>
      </Demo>

      <Demo frame="app" caption="Knob rack — DSP signal flow legible at a glance">
        <Panel title="Filter ▸ Lowpass 24dB" icon="filter-lp">
          <div className="dcs-row" style={{ gap: 28, padding: '14px 4px 14px', flexWrap: 'wrap', justifyContent: 'space-around' }}>
            <Knob value={k.cutoff} onChange={v => upd('cutoff', v)} size={56} label="CUT" format={v => `${Math.round(v * 22050)}`} />
            <Knob value={k.res}  onChange={v => upd('res', v)}  size={56} label="RES"  format={v => `${(v*100).toFixed(0)}`} />
            <Knob value={k.drive} onChange={v => upd('drive', v)} size={56} label="DRIVE" format={v => `${(v*24).toFixed(1)}`} />
            <Knob value={k.env}  onChange={v => upd('env', v)}  size={56} label="ENV"  bipolar format={v => `${((v-0.5)*2).toFixed(2)}`} />
            <Knob value={k.mix}  onChange={v => upd('mix', v)}  size={56} label="LFO"  bipolar format={v => `${((v-0.5)*200).toFixed(0)}`} />
          </div>
        </Panel>
      </Demo>
    </section>
  );
}

function SectionCombo() {
  const [pos, setPos] = useStateC({ x: 1.428, y: -0.952, z: 3.0 });
  const [scale, setScale] = useStateC(1);
  const [rot, setRot] = useStateC(45);
  const [sub, setSub] = useStateC(3);
  return (
    <section className="dw-section" id="combo">
      <div className="dw-section__eyebrow">Components · 05</div>
      <h2>Combo number fields</h2>
      <p className="dw-section__lead">
        ZBrush-style hybrid controls. Drag horizontally to scrub the value; click without dragging
        to type a number directly; the chevrons step by a single unit. Hold <code>Shift</code>
        while dragging to coarsen, hold <code>Ctrl</code> for precision.
      </p>

      <Demo caption="A property inspector row — what 80% of a DCC inspector actually looks like">
        <div className="dcs-col" style={{ maxWidth: 480 }}>
          <div className="dcs-field">
            <label className="dcs-field__label">Position</label>
            <Combo value={pos.x} onChange={v => setPos(p => ({ ...p, x: v }))} min={-10} max={10} step={0.001} label="X" format={v => v.toFixed(3)} width={120} />
            <Combo value={pos.y} onChange={v => setPos(p => ({ ...p, y: v }))} min={-10} max={10} step={0.001} label="Y" format={v => v.toFixed(3)} width={120} />
            <Combo value={pos.z} onChange={v => setPos(p => ({ ...p, z: v }))} min={-10} max={10} step={0.001} label="Z" format={v => v.toFixed(3)} width={120} />
          </div>
          <div className="dcs-field">
            <label className="dcs-field__label">Scale</label>
            <Combo value={scale} onChange={setScale} min={0.01} max={10} step={0.01} format={v => v.toFixed(2)} width={120} />
          </div>
          <div className="dcs-field">
            <label className="dcs-field__label">Rotation</label>
            <Combo value={rot} onChange={setRot} min={-180} max={180} step={1} format={v => `${v.toFixed(0)}°`} width={120} />
          </div>
          <div className="dcs-field">
            <label className="dcs-field__label">Subdivision</label>
            <Combo value={sub} onChange={setSub} min={0} max={8} step={1} format={v => `${v.toFixed(0)}×`} width={120} />
          </div>
        </div>
      </Demo>
    </section>
  );
}

function SectionChecksSwitches() {
  const [c, setC] = useStateC({ a: true, b: false, c: true, s1: true, s2: false, s3: true, r: 'b' });
  return (
    <section className="dw-section" id="checks">
      <div className="dw-section__eyebrow">Components · 06</div>
      <h2>Checks, radios, switches</h2>
      <p className="dw-section__lead">
        Three discrete pickers for three contexts: checks for additive options, radios for exclusive
        choices, switches for live-applied bypasses.
      </p>
      <Demo>
        <div className="dcs-row" style={{ gap: 32 }}>
          <div className="dcs-col">
            <Check checked={c.a} onChange={v => setC(o => ({ ...o, a: v }))}>Cast shadows</Check>
            <Check checked={c.b} onChange={v => setC(o => ({ ...o, b: v }))}>Receive shadows</Check>
            <Check checked={c.c} onChange={v => setC(o => ({ ...o, c: v }))}>Ray-visible</Check>
          </div>
          <div className="dcs-col">
            <Check radio checked={c.r === 'a'} onChange={() => setC(o => ({ ...o, r: 'a' }))}>BVH</Check>
            <Check radio checked={c.r === 'b'} onChange={() => setC(o => ({ ...o, r: 'b' }))}>kd-Tree</Check>
            <Check radio checked={c.r === 'c'} onChange={() => setC(o => ({ ...o, r: 'c' }))}>Embree</Check>
          </div>
          <div className="dcs-col">
            <div className="dcs-row"><Switch checked={c.s1} onChange={v => setC(o => ({ ...o, s1: v }))} /><span style={{ fontSize: 12 }}>Auto-bake</span></div>
            <div className="dcs-row"><Switch checked={c.s2} onChange={v => setC(o => ({ ...o, s2: v }))} /><span style={{ fontSize: 12 }}>Cache to disk</span></div>
            <div className="dcs-row"><Switch checked={c.s3} onChange={v => setC(o => ({ ...o, s3: v }))} /><span style={{ fontSize: 12 }}>Watch live</span></div>
          </div>
        </div>
      </Demo>
    </section>
  );
}

Object.assign(window, {
  SectionButtons, SectionInputs, SectionSliders, SectionKnobs, SectionCombo, SectionChecksSwitches,
});


/* ===== sections-layout.jsx ===== */
/* sections-layout.jsx
   Panels · Dockpanels · Toolbars
*/
const { useState: useStateL } = React;

function SectionPanels() {
  return (
    <section className="dw-section" id="panels">
      <div className="dw-section__eyebrow">Layout · 01</div>
      <h2>Panels</h2>
      <p className="dw-section__lead">
        The atomic unit of a DCC interface. Every panel is a header strip, a body, and an optional
        status footer — wrapped in a 1px dark frame that gives the page its grid-snap feeling.
      </p>

      <Demo frame="app" caption="The standard panel, raised variant, and a closeable one with an X in the header">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Panel title="Properties" icon="cog">
            <div className="dcs-props">
              <div className="dcs-field"><label className="dcs-field__label">Name</label><input className="dcs-input" defaultValue="Cube.003" /></div>
              <div className="dcs-field"><label className="dcs-field__label">Mass</label><Combo value={1.000} min={0} max={100} step={0.001} format={v => v.toFixed(3)} /></div>
              <div className="dcs-field"><label className="dcs-field__label">Friction</label><Combo value={0.500} min={0} max={1} step={0.001} format={v => v.toFixed(3)} /></div>
            </div>
          </Panel>
          <Panel raised title="Material" icon="palette" headerActive
                 tools={<><Button ghost sm icon iconLeft="plus" /><Button ghost sm icon iconLeft="more-h" /></>}>
            <div className="dcs-col" style={{ gap: 8 }}>
              <Swatch color="#4d9fff" label="Albedo" />
              <Swatch color="#23262e" label="Specular" />
              <Swatch color="#b48cff" label="Emission" />
            </div>
          </Panel>
          <Panel title="Status" icon="info" closeable>
            <div className="dcs-col" style={{ gap: 6, fontSize: 12, color: 'var(--dcs-text-dim)' }}>
              <div><span className="dcs-badge dcs-badge--ok dcs-badge--dot">VALID</span> &nbsp; mesh topology</div>
              <div><span className="dcs-badge dcs-badge--warn dcs-badge--dot">12 NGONS</span></div>
              <div><span className="dcs-badge dcs-badge--accent">120,481 TRI</span></div>
            </div>
          </Panel>
        </div>
      </Demo>

      <Demo frame="app" caption="Tabbed panel — Blender-style header tabs">
        <Panel pad={0}>
          <Tabs
            value="anim"
           
            tabs={[
              { value: 'world', label: 'World', icon: 'globe' },
              { value: 'mesh', label: 'Mesh', icon: 'mesh' },
              { value: 'anim', label: 'Animation', icon: 'curve' },
              { value: 'render', label: 'Render', icon: 'render' },
            ]}
          />
          <div style={{ padding: 16 }}>
            <div className="dcs-props">
              <div className="dcs-field"><label className="dcs-field__label">FPS</label><Combo value={24} min={1} max={120} step={1} format={v => `${v}`} /></div>
              <div className="dcs-field"><label className="dcs-field__label">Length</label><Combo value={240} min={1} max={9999} step={1} format={v => `${v} frames`} /></div>
              <div className="dcs-field"><label className="dcs-field__label">Loop</label><Switch checked /></div>
            </div>
          </div>
        </Panel>
      </Demo>
    </section>
  );
}

function SectionDock() {
  const [last, setLast] = useStateL('—');
  const [opacity, setOpacity] = useStateL(0.8);
  const [rough, setRough] = useStateL(0.35);
  const [gain, setGain] = useStateL(0.6);
  const [visible, setVisible] = useStateL(true);
  const [solo, setSolo] = useStateL(false);
  const [blend, setBlend] = useStateL('normal');
  const [mode, setMode] = useStateL('move');

  const DOCK_MENUS = {
    File: [{ label: 'New', icon: 'file', shortcut: '⌘N' }, { label: 'Open…', icon: 'folder-open', shortcut: '⌘O' }, { sep: true }, { label: 'Save', icon: 'save', shortcut: '⌘S' }, { label: 'Close', icon: 'close', danger: true }],
    Edit: [{ label: 'Undo', icon: 'undo', shortcut: '⌘Z' }, { label: 'Redo', icon: 'redo', shortcut: '⇧⌘Z' }, { sep: true }, { label: 'Copy', icon: 'copy' }, { label: 'Paste', icon: 'paste' }],
    View: [{ label: 'Show grid', check: true }, { label: 'Snap', check: true }, { sep: true }, { label: 'Reset layout', icon: 'array' }],
    Help: [{ label: 'Documentation', icon: 'help' }, { label: 'About', icon: 'info' }],
  };
  const TAB_META = {
    tree: { label: 'Tree', icon: 'layers' }, files: { label: 'Files', icon: 'folder' },
    preview: { label: 'Preview', icon: 'image' }, widgets: { label: 'Widgets', icon: 'cog' },
    log: { label: 'Log', icon: 'cpu' }, inspector: { label: 'Inspector', icon: 'edit' },
  };

  const TreeView = () => (
    <Tree
      expanded={new Set(['root', 'group'])}
      selected="b"
      nodes={[{
        id: 'root', label: 'Project', icon: 'folder-open', children: [
          { id: 'group', label: 'Group', icon: 'group', children: [
            { id: 'a', label: 'Node A', icon: 'cube' },
            { id: 'b', label: 'Node B', icon: 'cube', meta: 'sel' },
            { id: 'c', label: 'Node C', icon: 'sphere' },
          ] },
          { id: 'cam', label: 'Camera', icon: 'camera' },
          { id: 'light', label: 'Light', icon: 'light' },
        ],
      }]}
    />
  );
  const Files = () => (
    <div className="dcs-list">
      {[['intro.scene', 'cube'], ['hero.mat', 'palette'], ['sky_4k.hdr', 'image'], ['walk.anim', 'curve'], ['notes.md', 'file']].map(([n, ic], i) => (
        <div key={n} className="dcs-list__item" aria-selected={i === 0}><Icon name={ic} /><span style={{ flex: 1 }}>{n}</span></div>
      ))}
    </div>
  );
  const Preview = () => (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dcs-bg-app)' }}>
      <div style={{ width: '70%', maxWidth: 260, aspectRatio: '4/3', borderRadius: 8, background: 'radial-gradient(circle at 35% 30%, #b6d6ff, #4d9fff 55%, #1c4080 100%)', boxShadow: 'inset 0 0 30px rgba(0,0,0,.4), 0 8px 24px rgba(0,0,0,.4)', opacity }} />
    </div>
  );
  // A grab-bag of "rando" widgets — proves real controls survive docking/resize.
  const Widgets = () => (
    <div className="dcs-col" style={{ gap: 12, padding: 12 }}>
      <div className="dcs-row" style={{ gap: 18, justifyContent: 'space-around' }}>
        <Knob value={gain} onChange={setGain} size={48} label="GAIN" format={v => `${(v * 100).toFixed(0)}`} />
        <Knob value={rough} onChange={setRough} size={48} label="MIX" bipolar format={v => `${((v - 0.5) * 200).toFixed(0)}`} />
      </div>
      <div className="dcs-props">
        <div className="dcs-field"><span className="dcs-field__label">Level</span><Slider value={gain} onChange={setGain} /></div>
        <div className="dcs-field"><span className="dcs-field__label">Size</span><Combo value={rough} onChange={setRough} min={0} max={1} step={0.01} format={v => v.toFixed(2)} /></div>
      </div>
      <ButtonGroup value={mode} onChange={setMode} options={[{ value: 'move', icon: 'move', label: 'Move' }, { value: 'rotate', icon: 'rotate', label: 'Rot' }, { value: 'scale', icon: 'scale-corners', label: 'Scale' }]} />
      <div className="dcs-row" style={{ gap: 16 }}>
        <Check checked={visible} onChange={setVisible}>Enabled</Check>
        <div className="dcs-row" style={{ gap: 6 }}><Switch checked={solo} onChange={setSolo} /><span style={{ fontSize: 12 }}>Solo</span></div>
      </div>
      <div className="dcs-row" style={{ gap: 8 }}><Button sm primary iconLeft="check">Apply</Button><Button sm ghost iconLeft="undo">Reset</Button></div>
    </div>
  );
  // Plain monochrome log — deliberately not colorful.
  const LogView = () => (
    <div style={{ padding: '6px 12px', fontFamily: 'var(--dcs-font-mono)', fontSize: 11, lineHeight: 1.7, color: 'var(--dcs-text-dim)' }}>
      {[
        '12:04:01  project opened',
        '12:04:01  loaded 6 nodes',
        `12:04:09  gain → ${(gain * 100).toFixed(0)}%`,
        `12:04:12  blend = ${blend}`,
        `12:04:18  menu: ${last}`,
        '12:04:20  autosave ok',
      ].map((l, i) => (
        <div key={i}><span style={{ color: 'var(--dcs-text-mute)' }}>{l.slice(0, 8)}</span>{l.slice(8)}</div>
      ))}
    </div>
  );
  const Inspector = () => (
    <div className="dcs-props">
      <div className="dcs-field"><span className="dcs-field__label">Name</span><input className="dcs-input" defaultValue="Node B" /></div>
      <div className="dcs-field"><span className="dcs-field__label">Opacity</span><Slider value={opacity} onChange={setOpacity} /><span className="dcs-mono" style={{ width: 34, textAlign: 'right' }}>{Math.round(opacity * 100)}</span></div>
      <div className="dcs-field"><span className="dcs-field__label">Rough</span><Combo value={rough} onChange={setRough} min={0} max={1} step={0.01} format={v => v.toFixed(2)} /></div>
      <div className="dcs-field"><span className="dcs-field__label">Visible</span><Switch checked={visible} onChange={setVisible} /></div>
      <div className="dcs-field"><span className="dcs-field__label">Blend</span><ButtonGroup value={blend} onChange={setBlend} options={[{ value: 'normal', label: 'Norm' }, { value: 'add', label: 'Add' }, { value: 'mul', label: 'Mul' }]} /></div>
      <div className="dcs-field"><span className="dcs-field__label">Tint</span><div className="dcs-swatch"><div className="dcs-swatch__chip" style={{ '--c': '#4d9fff' }} /><span>#4D9FFF</span></div></div>
    </div>
  );

  const renderContent = (id) => {
    switch (id) {
      case 'tree': return <div style={{ padding: 'var(--dcs-s-2) 0' }}>{TreeView()}</div>;
      case 'files': return <div style={{ padding: 'var(--dcs-s-2) 0' }}>{Files()}</div>;
      case 'preview': return <div style={{ position: 'relative', height: '100%', minHeight: 180 }}>{Preview()}</div>;
      case 'widgets': return Widgets();
      case 'log': return LogView();
      case 'inspector': return <div style={{ padding: 'var(--dcs-s-3) var(--dcs-s-4)' }}>{Inspector()}</div>;
      default: return null;
    }
  };
  const layout = {
    type: 'split', dir: 'row', sizes: [1, 1.9, 1.05], children: [
      { type: 'tabs', tabs: ['tree', 'files'] },
      { type: 'split', dir: 'col', sizes: [2, 1], children: [
        { type: 'tabs', tabs: ['preview', 'widgets'] },
        { type: 'tabs', tabs: ['log'] },
      ] },
      { type: 'tabs', tabs: ['inspector'] },
    ],
  };

  return (
    <section className="dw-section" id="dock">
      <div className="dw-section__eyebrow">Layout · 03</div>
      <h2>Dock panels</h2>
      <p className="dw-section__lead">
        A real docking workspace — small on purpose, with a tree, an inspector, a log, and a grab-bag
        of widgets. <strong style={{ color: 'var(--dw-text)' }}>Grab any tab and drag it</strong>: drop on a pane's
        <em> center</em> to add it as a tab, or an <em>edge</em> (left / right / top / bottom) to dock a new split.
        Drag the seams to resize; the menu bar drops real menus. The full
        {' '}<a href="#sample-dcc">Sample DCC tool</a> uses this same engine with richer panels.
      </p>
      <div className="dcs-alert" style={{ background: 'var(--dw-bg-soft)', borderColor: 'var(--dw-line)', borderLeftColor: 'var(--dw-accent)', color: 'var(--dw-text)', marginBottom: 20 }}>
        <div className="dcs-alert__icon"><Icon name="cpu" /></div>
        <div className="dcs-alert__body">
          <div className="dcs-alert__title" style={{ color: 'var(--dw-text)' }}>What's vanilla vs React</div>
          <div className="dcs-alert__msg" style={{ color: 'var(--dw-text-dim)' }}>
            The CSS (<code>.dcs-dock</code>, <code>.dcs-dockpane</code>, <code>.dcs-splitter</code>, <code>.dcs-menubar</code>)
            is plain framework CSS, and <code>decius.js</code> drives the <strong>menus, tabs, and splitter resize</strong>.
            The full <strong>grab-and-dock layout manager</strong> (rearranging panes via center/edge drop-zones) is a
            <strong> React reference component</strong> — docking is an app-level concern, and on
            {' '}<a href="https://github.com/benjcooley/affineui">affineui</a> the host provides it natively.
          </div>
        </div>
      </div>
      <Demo frame="app" inset noDensity minw={760} caption="Drag tabs to dock · drag seams to resize · click the menus">
        <div style={{ display: 'flex', flexDirection: 'column', height: 470 }}>
          <MenuBar
            brand={{ icon: 'decius', label: 'workspace' }}
            items={['File', 'Edit', 'View', 'Help']}
            menus={DOCK_MENUS}
            onPick={(m, v) => setLast(`${m} › ${v}`)}
            meta={<><span>untitled.scene</span></>}
          />
          <DockLayout initial={layout} tabMeta={(id) => TAB_META[id]} renderContent={renderContent} />
          <div className="dcs-statusbar">
            <span className="dcs-statusbar__item dcs-statusbar__item--ok"><Icon name="check-circle" size="sm" /> Ready</span>
            <span className="dcs-statusbar__item">menu: {last}</span>
            <span className="dcs-statusbar__spacer" />
            <span className="dcs-statusbar__item dcs-statusbar__item--accent">drag a tab to dock</span>
          </div>
        </div>
      </Demo>
    </section>
  );
}
/* ------------------------------------------------------------
   Center "Document" pane + tear-off dockable panels.
   Pure stock decius markup + behaviour: no React DockLayout.
   ------------------------------------------------------------ */
function SectionTearoff() {
  // Use a ref-based mount: render an empty container and inject the
  // workspace HTML into it once. decius.js will pick up the tabs,
  // tear-off behaviour, drag handles, etc. on init().
  const hostRef = React.useRef(null);
  React.useEffect(() => {
    if (!hostRef.current || hostRef.current.dataset.mounted) return;
    hostRef.current.dataset.mounted = '1';
    hostRef.current.innerHTML = TEAROFF_WORKSPACE_HTML;
    if (window.decius && window.decius.init) window.decius.init(hostRef.current);
  }, []);

  return (
    <section className="dw-section" id="tearoff">
      <div className="dw-section__eyebrow">Layout · 05</div>
      <h2>Document pane &amp; tear-off panels</h2>
      <p className="dw-section__lead">
        A Photoshop-style workspace built from stock decius components only — no React docking
        engine. The <strong>center "Document" pane</strong> (<code>.dcs-dockpane--center</code>) holds the
        editor, with a multi-tab document strip that's <em>locked in place</em>: documents aren't
        interchangeable with side-panel tabs. The <strong>side panels</strong> ("Tools", "Layers",
        "Properties") are regular <code>.dcs-dockpane</code>s — drag any of their tabs out and you get
        a <code>.dcs-panel--floating</code> hovering over the document; drag it back over another side
        pane's tab strip to re-dock. Document tabs stay put.
      </p>
      <div className="dcs-alert dcs-alert--info" style={{ marginBottom: 20 }}>
        <div className="dcs-alert__icon"><Icon name="info" /></div>
        <div className="dcs-alert__body">
          <div className="dcs-alert__title">How it's wired</div>
          <div className="dcs-alert__msg">
            <code>data-dcs-dock-kind</code> segregates pools: "documents" tabs only re-dock to documents
            panes, "panels" tabs only to panels panes. <code>data-dcs-dock-tearoff="false"</code> (implied by
            <code> .dcs-dockpane--center</code>) locks a pane's tabs in place. Floating panels are parented
            to the workspace's positioned ancestor, so they hover over the document but don't scroll
            with the page — and on <a href="https://github.com/benjcooley/affineui">affineui</a> the
            host can promote them to real OS child windows instead.
          </div>
        </div>
      </div>
      <Demo frame="app" inset noDensity minw={760} caption="Drag a side-panel tab anywhere to tear it off · drag it back over another tab strip to re-dock · document tabs stay put">
        <div
          ref={hostRef}
          style={{ position: 'relative', height: 470, overflow: 'hidden', background: 'var(--dcs-bg-app)' }}
        />
      </Demo>
    </section>
  );
}

const TEAROFF_WORKSPACE_HTML = `
<div class="dw-workspace" style="position:absolute;inset:0;display:grid;grid-template-columns:220px 1fr 240px;gap:1px;background:var(--dcs-line);">

  <!-- LEFT side dock — Tools / Outliner -->
  <div class="dcs-dockpane" style="background:var(--dcs-bg);">
    <div class="dcs-dockpane__tabbar">
      <div class="dcs-dockpane__tabs">
        <button class="dcs-dockpane__tab" aria-selected="true" data-dcs-target="#dw-tear-tools"><i class="di di-cube"></i> Tools</button>
        <button class="dcs-dockpane__tab" data-dcs-target="#dw-tear-outliner"><i class="di di-folder-open"></i> Outliner</button>
      </div>
    </div>
    <div class="dcs-dockpane__body" style="padding:var(--dcs-s-3);">
      <div id="dw-tear-tools" data-dcs-tabpanel>
        <div class="dcs-col" style="gap:var(--dcs-s-3);">
          <div class="dcs-row" style="gap:var(--dcs-s-2);flex-wrap:wrap;">
            <button class="dcs-btn dcs-btn--icon" aria-pressed="true"><i class="di di-move"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-rotate"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-scale"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-marquee"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-lasso"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-brush"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-eraser"></i></button>
            <button class="dcs-btn dcs-btn--icon"><i class="di di-fill"></i></button>
          </div>
          <div class="dcs-note dcs-note--mute" style="font-size:var(--dcs-fs-xs);padding:0 var(--dcs-s-1);">Drag the "Tools" tab onto the document.</div>
        </div>
      </div>
      <div id="dw-tear-outliner" data-dcs-tabpanel hidden>
        <div class="dcs-tree" data-dcs-select>
          <div class="dcs-tree__row" style="--depth:0;"><span class="dcs-tree__chevron dcs-tree__chevron--open"><i class="di di-chevron-right"></i></span><span class="dcs-tree__icon"><i class="di di-folder-open"></i></span><span class="dcs-tree__label">Scene</span></div>
          <div class="dcs-tree__row" style="--depth:1;"><span class="dcs-tree__chevron"></span><span class="dcs-tree__icon"><i class="di di-cube"></i></span><span class="dcs-tree__label">Cube</span></div>
          <div class="dcs-tree__row" aria-selected="true" style="--depth:1;"><span class="dcs-tree__chevron"></span><span class="dcs-tree__icon"><i class="di di-light"></i></span><span class="dcs-tree__label">Key Light</span></div>
          <div class="dcs-tree__row" style="--depth:1;"><span class="dcs-tree__chevron"></span><span class="dcs-tree__icon"><i class="di di-camera"></i></span><span class="dcs-tree__label">Camera</span></div>
        </div>
      </div>
    </div>
  </div>

  <!-- CENTER: locked Document pane (multi-tabbed) -->
  <div class="dcs-dockpane dcs-dockpane--center">
    <div class="dcs-dockpane__tabbar">
      <div class="dcs-dockpane__tabs">
        <button class="dcs-dockpane__tab" aria-selected="true" data-dcs-target="#dw-tear-doc-1"><i class="di di-image"></i> hero.psd <span class="dcs-dockpane__tab-close"><i class="di di-close"></i></span></button>
        <button class="dcs-dockpane__tab" data-dcs-target="#dw-tear-doc-2"><i class="di di-image"></i> banner.png <span class="dcs-dockpane__tab-close"><i class="di di-close"></i></span></button>
        <button class="dcs-dockpane__tab" data-dcs-target="#dw-tear-doc-3"><i class="di di-image"></i> Untitled-3 <span class="dcs-dockpane__tab-close"><i class="di di-close"></i></span></button>
      </div>
    </div>
    <div class="dcs-dockpane__body">
      <div id="dw-tear-doc-1" data-dcs-tabpanel style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
        <div style="width:60%;max-width:340px;aspect-ratio:16/10;border-radius:6px;background:linear-gradient(135deg,#ff8a3a 0%,#ff6b9d 40%,#7e4fff 100%);box-shadow:0 14px 40px rgba(0,0,0,.4),0 0 0 1px rgba(0,0,0,.3);"></div>
      </div>
      <div id="dw-tear-doc-2" data-dcs-tabpanel hidden style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
        <div style="width:60%;max-width:340px;aspect-ratio:16/10;border-radius:6px;background:radial-gradient(circle at 30% 30%, #4dffd2, #2a8efa 60%, #1c2870);box-shadow:0 14px 40px rgba(0,0,0,.4),0 0 0 1px rgba(0,0,0,.3);"></div>
      </div>
      <div id="dw-tear-doc-3" data-dcs-tabpanel hidden style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:var(--dcs-text-mute);">
        <div style="width:60%;max-width:340px;aspect-ratio:16/10;border-radius:6px;background-image:repeating-conic-gradient(#cfcfcf 0 90deg,#fff 90deg 180deg);background-size:18px 18px;box-shadow:0 14px 40px rgba(0,0,0,.4),0 0 0 1px rgba(0,0,0,.3);"></div>
      </div>
    </div>
  </div>

  <!-- RIGHT side dock — Layers / Properties -->
  <div class="dcs-dockpane" style="background:var(--dcs-bg);">
    <div class="dcs-dockpane__tabbar">
      <div class="dcs-dockpane__tabs">
        <button class="dcs-dockpane__tab" aria-selected="true" data-dcs-target="#dw-tear-layers"><i class="di di-layers"></i> Layers</button>
        <button class="dcs-dockpane__tab" data-dcs-target="#dw-tear-props"><i class="di di-cog"></i> Properties</button>
      </div>
    </div>
    <div class="dcs-dockpane__body" style="padding:0;">
      <div id="dw-tear-layers" data-dcs-tabpanel>
        <div class="dcs-list" data-dcs-select>
          <div class="dcs-list__item" aria-selected="true"><i class="di di-eye"></i><span style="flex:1;">Hero Layer</span><span style="color:var(--dcs-text-mute);font-size:var(--dcs-fs-xs);">Normal</span></div>
          <div class="dcs-list__item"><i class="di di-eye"></i><span style="flex:1;">Title text</span><span style="color:var(--dcs-text-mute);font-size:var(--dcs-fs-xs);">Mult</span></div>
          <div class="dcs-list__item"><i class="di di-eye-off"></i><span style="flex:1;color:var(--dcs-text-mute);">Sketch</span><span style="color:var(--dcs-text-mute);font-size:var(--dcs-fs-xs);">Normal</span></div>
          <div class="dcs-list__item"><i class="di di-eye"></i><span style="flex:1;">Background</span><span style="color:var(--dcs-text-mute);font-size:var(--dcs-fs-xs);">Normal</span></div>
        </div>
      </div>
      <div id="dw-tear-props" data-dcs-tabpanel hidden style="padding:var(--dcs-s-3);">
        <div class="dcs-props">
          <div class="dcs-field"><span class="dcs-field__label">Opacity</span><div data-dcs-slider data-min="0" data-max="1" data-value="0.85"></div></div>
          <div class="dcs-field"><span class="dcs-field__label">Fill</span><div data-dcs-slider data-min="0" data-max="1" data-value="1"></div></div>
          <div class="dcs-field"><span class="dcs-field__label">Locked</span><div class="dcs-check"><div class="dcs-check__box"></div></div></div>
        </div>
      </div>
    </div>
  </div>
</div>
`;

Object.assign(window, { SectionPanels, SectionDock, SectionSubpanels, SectionFoldouts, SectionTearoff });

function SectionFoldouts() {
  return (
    <section className="dw-section" id="foldouts">
      <div className="dw-section__eyebrow">Layout · 04</div>
      <h2>Foldouts</h2>
      <p className="dw-section__lead">
        Blender-style soft section cards. Use these inside a scrolling property inspector to group
        related controls into rounded, lighter-bg containers with comfortable margins. Click a
        header to collapse. Pair them with subpanels — foldouts are the soft, breathy version.
      </p>
      <Demo frame="app">
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'flex-start' }}>
          <Panel title="Inspector ▸ jane_body" icon="cog" pad={0}
                 tools={<><Button ghost sm icon iconLeft="pin" /><Button ghost sm icon iconLeft="more-h" /></>}>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Foldouts>
                <Foldout title="Transform" icon="move" meta="Local">
                  <div className="dcs-props">
                    {[['X', '#ef6b6b', 1.428], ['Y', '#4ed18a', -0.952], ['Z', '#4d9fff', 3.000]].map(([k, c, v]) => (
                      <div key={k} className="dcs-field">
                        <span className="dcs-field__label" style={{ color: c, flex: '0 0 16px', fontFamily: 'var(--dcs-font-num)', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>{k}</span>
                        <Combo value={v} min={-10} max={10} step={0.001} format={v => v.toFixed(3)} />
                      </div>
                    ))}
                  </div>
                </Foldout>
                <Foldout title="Material" icon="palette" meta="Lambert.001">
                  <div className="dcs-props">
                    <div className="dcs-field">
                      <span className="dcs-field__label">Albedo</span>
                      <div className="dcs-swatch"><div className="dcs-swatch__chip" style={{ '--c': '#4d9fff' }} /><span>#4D9FFF</span></div>
                    </div>
                    <div className="dcs-field">
                      <span className="dcs-field__label">Roughness</span>
                      <Slider value={0.42} />
                    </div>
                    <div className="dcs-field">
                      <span className="dcs-field__label">Metallic</span>
                      <Slider value={0.08} />
                    </div>
                    <div className="dcs-field">
                      <span className="dcs-field__label">Specular</span>
                      <Combo value={0.5} min={0} max={1} step={0.01} format={v => v.toFixed(2)} />
                    </div>
                    <div className="dcs-field">
                      <span className="dcs-field__label">Use texture</span>
                      <Switch checked />
                    </div>
                    <div className="dcs-field">
                      <span className="dcs-field__label">Tag</span>
                      <input className="dcs-input" defaultValue="opaque/metallic" />
                    </div>
                  </div>
                </Foldout>
                <Foldout title="Display" icon="eye">
                  <div className="dcs-props">
                    {[
                      { l: 'Wireframe', v: false },
                      { l: 'Smooth shading', v: true },
                      { l: 'Cast shadow', v: true },
                      { l: 'In viewport', v: true },
                      { l: 'In render', v: true },
                    ].map(it => (
                      <div key={it.l} className="dcs-field">
                        <span className="dcs-field__label">{it.l}</span>
                        <Switch checked={it.v} />
                      </div>
                    ))}
                  </div>
                </Foldout>
                <Foldout title="Constraints" icon="link" defaultOpen={false} meta="0" />
                <Foldout title="Modifiers" icon="bolt" defaultOpen={false} meta="3" />
                <Foldout title="Subdivision" icon="subdivide" defaultOpen={false} meta="L2" />
                <Foldout title="UV Maps" icon="uv" defaultOpen={false} meta="1" />
                <Foldout title="Vertex Groups" icon="mesh" defaultOpen={false} meta="0" />
              </Foldouts>
            </div>
          </Panel>
          <div className="dw-note" style={{ paddingTop: 6 }}>
            <p><strong>Foldout vs SubPanel.</strong> Both collapse. The visual difference is rhythm.</p>
            <p><strong>Foldouts</strong> are soft, lighter-bg cards with margins — they feel like grouped properties. Use for inspectors with many heterogeneous sections (Blender's N-panel, Unity's Inspector).</p>
            <p><strong>Subpanels</strong> are full-width seams in a stack — they feel like rigid rows. Use when controls are dense and visual chunking matters more than soft separation (Maya's Channel Box, Houdini's parameter editor).</p>
          </div>
        </div>
      </Demo>

      <h3 style={{ marginTop: 36, marginBottom: 8 }}>It's a base component — raw CSS or JS</h3>
      <p className="dw-section__lead" style={{ marginTop: 0 }}>
        A foldout is just markup. Wrap it in a native <code>&lt;details&gt;</code> and it opens and closes
        with <strong style={{ color: 'var(--dw-text)' }}>zero JavaScript</strong> — same class names, same look
        (the second one below starts collapsed). Or drive it yourself by toggling the
        {' '}<code>.dcs-foldout--collapsed</code> class — that's what React does here, and what the vanilla
        {' '}<code>decius.js</code> runtime wires up automatically on any <code>.dcs-foldout__header</code>.
      </p>
      <Demo frame="app">
        <div className="dcs-foldouts" style={{ maxWidth: 360 }}>
          <details className="dcs-foldout" open>
            <summary className="dcs-foldout__header">
              <span className="dcs-foldout__chevron"><Icon name="chevron-right" size="sm" /></span>
              <Icon className="dcs-foldout__icon" name="move" size="sm" />
              <span className="dcs-foldout__title">Transform</span>
              <span className="dcs-foldout__meta">native &lt;details&gt;</span>
            </summary>
            <div className="dcs-foldout__body">
              <div className="dcs-props">
                <div className="dcs-field"><span className="dcs-field__label">Position</span><input className="dcs-input" defaultValue="0.0, 0.0, 0.0" /></div>
                <div className="dcs-field"><span className="dcs-field__label">Scale</span><input className="dcs-input" defaultValue="1.0" /></div>
              </div>
            </div>
          </details>
          <details className="dcs-foldout">
            <summary className="dcs-foldout__header">
              <span className="dcs-foldout__chevron"><Icon name="chevron-right" size="sm" /></span>
              <Icon className="dcs-foldout__icon" name="palette" size="sm" />
              <span className="dcs-foldout__title">Material</span>
              <span className="dcs-foldout__meta">native &lt;details&gt;</span>
            </summary>
            <div className="dcs-foldout__body">
              <div className="dcs-props">
                <div className="dcs-field"><span className="dcs-field__label">Albedo</span><input className="dcs-input" defaultValue="#4D9FFF" /></div>
                <div className="dcs-field"><span className="dcs-field__label">Roughness</span><input className="dcs-input" defaultValue="0.42" /></div>
              </div>
            </div>
          </details>
        </div>
      </Demo>
    </section>
  );
}

function SectionSubpanels() {
  return (
    <section className="dw-section" id="subpanels">
      <div className="dw-section__eyebrow">Layout · 03</div>
      <h2>Subpanels</h2>
      <p className="dw-section__lead">
        Inside a panel, group properties into collapsible subpanels with a single chevron and a hover-revealed
        close affordance. Subpanels share the panel's chrome — they're horizontal seams in a stack, not
        nested boxes.
      </p>
      <Demo frame="app">
        <div style={{ maxWidth: 320 }}>
          <Panel title="Inspector ▸ jane_body" icon="cog" pad={0}
                 tools={<><Button ghost sm icon iconLeft="pin" /><Button ghost sm icon iconLeft="more-h" /></>}>
            <SubPanel title="Transform" icon="move">
              <div className="dcs-props" style={{ '--dcs-props-label-w': '14px' }}>
                <div className="dcs-field">
                  <span className="dcs-field__label" style={{ fontSize: 10, color: 'var(--dcs-danger)', fontFamily: 'var(--dcs-font-mono)' }}>X</span>
                  <Combo value={1.428} min={-10} max={10} step={0.001} format={v => v.toFixed(3)} />
                </div>
                <div className="dcs-field">
                  <span className="dcs-field__label" style={{ fontSize: 10, color: 'var(--dcs-ok)', fontFamily: 'var(--dcs-font-mono)' }}>Y</span>
                  <Combo value={-0.952} min={-10} max={10} step={0.001} format={v => v.toFixed(3)} />
                </div>
                <div className="dcs-field">
                  <span className="dcs-field__label" style={{ fontSize: 10, color: 'var(--dcs-accent)', fontFamily: 'var(--dcs-font-mono)' }}>Z</span>
                  <Combo value={3.000} min={-10} max={10} step={0.001} format={v => v.toFixed(3)} />
                </div>
              </div>
            </SubPanel>
            <SubPanel title="Display" icon="eye">
              <div className="dcs-props">
                <div className="dcs-field"><span className="dcs-field__label">Wireframe</span><Switch checked={false} /></div>
                <div className="dcs-field"><span className="dcs-field__label">Smooth shade</span><Switch checked /></div>
                <div className="dcs-field"><span className="dcs-field__label">Cast shadow</span><Switch checked /></div>
              </div>
            </SubPanel>
            <SubPanel title="Material" icon="palette" defaultOpen={false}>
              <div className="dcs-props">
                <div className="dcs-field">
                  <span className="dcs-field__label">Albedo</span>
                  <div className="dcs-swatch"><div className="dcs-swatch__chip" style={{ '--c': '#4d9fff' }} /><span>#4D9FFF</span></div>
                </div>
                <div className="dcs-field"><span className="dcs-field__label">Roughness</span><Slider value={0.42} /></div>
              </div>
            </SubPanel>
            <SubPanel title="Constraints" icon="link" defaultOpen={false}>
              <div className="dcs-props">
                <div className="dcs-field"><span className="dcs-field__label">Lock X</span><Switch checked={false} /></div>
                <div className="dcs-field"><span className="dcs-field__label">Track to</span><Combo value={0} min={0} max={4} step={1} format={() => 'Cam.001'} /></div>
              </div>
            </SubPanel>
            <SubPanel title="Subdivision" icon="subdivide" defaultOpen={false}>
              <div className="dcs-props">
                <div className="dcs-field"><span className="dcs-field__label">Level</span><Combo value={2} min={0} max={6} step={1} format={v => `${v}×`} /></div>
              </div>
            </SubPanel>
          </Panel>
        </div>
      </Demo>
    </section>
  );
}


/* ===== sections-data.jsx ===== */
/* sections-data.jsx
   Lists · Trees · Tables · Badges
*/
const { useState: useStateD } = React;

const LIST_ITEMS = [
  { id: 0, label: 'Lambert.001',     meta: '#4d9fff', icon: 'palette' },
  { id: 1, label: 'CarPaint_red',    meta: '#ff6b6b', icon: 'palette' },
  { id: 2, label: 'Concrete_Rough',  meta: '#7c8492', icon: 'palette' },
  { id: 3, label: 'GlassGreen_01',   meta: '#4ed18a', icon: 'palette' },
  { id: 4, label: 'StudioBack.exr',  meta: '4k · 32-bit', icon: 'image' },
  { id: 5, label: 'AnimWalk.fbx',    meta: '48 bones', icon: 'bone' },
  { id: 6, label: 'OakBark_2k.png',  meta: '2k · sRGB', icon: 'texture' },
  { id: 7, label: 'jane_rig.fbx',    meta: '64 bones', icon: 'bone' },
  { id: 8, label: 'Sparks.vdb',      meta: 'volume', icon: 'cube' },
  { id: 9, label: 'Marble_4k.exr',   meta: '4k · linear', icon: 'image' },
  { id: 10, label: 'Brass_worn.mat', meta: 'PBR', icon: 'palette' },
  { id: 11, label: 'crowd_walk.anim', meta: '240f', icon: 'curve' },
  { id: 12, label: 'Velvet_red.mat', meta: 'PBR', icon: 'palette' },
  { id: 13, label: 'city_dusk.hdr',  meta: '8k · HDR', icon: 'image' },
  { id: 14, label: 'eric_rig.fbx',   meta: '52 bones', icon: 'bone' },
  { id: 15, label: 'Smoke_sim.vdb',  meta: 'volume', icon: 'cube' },
  { id: 16, label: 'Tiles_4k.png',   meta: '4k · sRGB', icon: 'texture' },
  { id: 17, label: 'idle_loop.anim', meta: '120f', icon: 'curve' },
  { id: 18, label: 'Chrome.mat',     meta: 'PBR · metal', icon: 'palette' },
  { id: 19, label: 'Bark_disp.exr',  meta: '2k · linear', icon: 'image' },
];

// "Basic JS interactions vs React full behavior" — the line we draw everywhere.
function LayerNote() {
  return (
    <div className="dcs-alert" style={{ background: 'var(--dw-bg-soft)', borderColor: 'var(--dw-line)', borderLeftColor: 'var(--dw-accent)', color: 'var(--dw-text)', marginTop: 14 }}>
      <div className="dcs-alert__icon"><Icon name="cpu" /></div>
      <div className="dcs-alert__body">
        <div className="dcs-alert__msg" style={{ color: 'var(--dw-text-dim)' }}>
          <strong style={{ color: 'var(--dw-text)' }}>Styling</strong> is plain CSS (selection ribbon, drop line).
          {' '}<strong style={{ color: 'var(--dw-text)' }}>Basic interactions</strong> — click / Ctrl / Shift selection — ship in the
          {' '}<code>decius.js</code> runtime (<code>data-dcs-select</code>). The <strong style={{ color: 'var(--dw-text)' }}>full behavior</strong> shown
          here — multi-select plus drag-and-drop reorder / reparent with a live drop indicator — is the React reference component.
        </div>
      </div>
    </div>
  );
}

function SectionLists() {
  const [items, setItems] = useStateD(LIST_ITEMS);
  const [sel, setSel] = useStateD(() => new Set([2, 3]));
  const [q, setQ] = useStateD('');
  const [searching, setSearching] = useStateD(false);
  const ql = q.trim().toLowerCase();
  const shown = ql ? items.filter(it => it.label.toLowerCase().includes(ql)) : items;
  return (
    <section className="dw-section" id="lists">
      <div className="dw-section__eyebrow">Data · 01</div>
      <h2>Lists</h2>
      <p className="dw-section__lead">
        Flat, selectable rows — a tree without folding. <strong style={{ color: 'var(--dw-text)' }}>Click</strong> to select,
        {' '}<strong style={{ color: 'var(--dw-text)' }}>Ctrl/⌘-click</strong> to toggle, <strong style={{ color: 'var(--dw-text)' }}>Shift-click</strong> for a
        range, and <strong style={{ color: 'var(--dw-text)' }}>drag</strong> the selection to reorder — a 2px accent line shows where it lands.
        The search button filters; the list scrolls when it overflows.
      </p>
      <Demo frame="app">
        <div style={{ maxWidth: 360 }}>
          <Panel title="Assets" icon="folder" pad={0}
                 tools={<><Button ghost sm icon iconLeft="search" pressed={searching} onClick={() => setSearching(s => !s)} /><Button ghost sm icon iconLeft="plus" /></>}>
            {searching && (
              <div style={{ padding: 6, borderBottom: '1px solid var(--dcs-line)' }}>
                <input className="dcs-input" placeholder="Filter assets…" value={q} autoFocus onChange={e => setQ(e.target.value)} />
              </div>
            )}
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              <Tree flat multi reorderable={!ql}
                nodes={shown} selected={sel} onSelect={setSel}
                onMove={(ids, t, pos) => setItems(prev => treeMove(prev, ids, t, pos))} />
              {shown.length === 0 && <div style={{ padding: 14, fontSize: 12, color: 'var(--dcs-text-mute)' }}>No matches for "{q}"</div>}
            </div>
          </Panel>
        </div>
      </Demo>
      <LayerNote />
    </section>
  );
}

const OUTLINER_NODES = [{
  id: 'scn', label: 'Scene_Intro_v014', icon: 'globe', meta: '37',
  children: [
                    { id: 'env', label: 'Environment', icon: 'folder-open', meta: '4', children: [
                      { id: 'hdri', label: 'Sky_4k.hdr', icon: 'image' },
                      { id: 'fog', label: 'VolumeFog', icon: 'cube' },
                      { id: 'sun', label: 'Sun.001', icon: 'light' },
                      { id: 'ground', label: 'GroundPlane', icon: 'plane' },
                    ]},
                    { id: 'chars', label: 'Characters', icon: 'folder-open', meta: '2', children: [
                      { id: 'jane', label: 'Jane', icon: 'folder-open', children: [
                        { id: 'mesh', label: 'jane_body.geo', icon: 'mesh', meta: '64,201' },
                        { id: 'rig', label: 'jane_rig', icon: 'bone', children: [
                          { id: 'spine', label: 'spine', icon: 'bone' },
                          { id: 'lhand', label: 'L_hand', icon: 'bone', meta: 'IK' },
                          { id: 'rhand', label: 'R_hand', icon: 'bone' },
                        ]},
                        { id: 'mat', label: 'jane_skin.mat', icon: 'palette' },
                      ]},
                      { id: 'eric', label: 'Eric', icon: 'folder' },
                    ]},
    { id: 'cams', label: 'Cameras', icon: 'folder', meta: '3' },
    { id: 'fx', label: 'FX', icon: 'folder', meta: '7' },
  ],
}];

function filterTree(nodes, q) {
  const m = (n) => n.label.toLowerCase().includes(q);
  const walk = (list) => list.flatMap(n => {
    if (m(n)) return [n];
    const kids = n.children ? walk(n.children) : [];
    return kids.length ? [{ ...n, children: kids }] : [];
  });
  return walk(nodes);
}
function allTreeIds(nodes, acc = new Set()) {
  nodes.forEach(n => { acc.add(n.id); if (n.children) allTreeIds(n.children, acc); });
  return acc;
}

function SectionTrees() {
  const [nodes, setNodes] = useStateD(OUTLINER_NODES);
  const [expanded, setExpanded] = useStateD(new Set(['scn', 'env', 'chars', 'jane', 'rig']));
  const [sel, setSel] = useStateD(() => new Set(['mesh', 'rig']));
  const [q, setQ] = useStateD('');
  const [searching, setSearching] = useStateD(false);
  const toggle = (id) => setExpanded(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const query = q.trim().toLowerCase();
  const view = query ? filterTree(nodes, query) : nodes;
  const exp = query ? allTreeIds(view) : expanded;
  return (
    <section className="dw-section" id="trees">
      <div className="dw-section__eyebrow">Data · 02</div>
      <h2>Trees</h2>
      <p className="dw-section__lead">
        The outliner. Deep hierarchies of scenes, rigs, layers, nodes. Same selection model as lists —
        <strong style={{ color: 'var(--dw-text)' }}> Ctrl/⌘</strong> toggles, <strong style={{ color: 'var(--dw-text)' }}>Shift</strong> ranges —
        and you can <strong style={{ color: 'var(--dw-text)' }}>drag rows to reorder or reparent</strong>: a line marks a sibling
        drop, a highlighted row marks dropping <em>into</em> a folder. Chevrons expand; search filters and auto-expands.
      </p>
      <Demo frame="app">
        <div style={{ maxWidth: 380 }}>
          <Panel title="Outliner" icon="layers" pad={0}
                 tools={<><Button ghost sm icon iconLeft="search" pressed={searching} onClick={() => setSearching(s => !s)} /><Button ghost sm icon iconLeft="more-h" /></>}>
            {searching && (
              <div style={{ padding: 6, borderBottom: '1px solid var(--dcs-line)' }}>
                <input className="dcs-input" placeholder="Filter outliner…" value={q} autoFocus onChange={e => setQ(e.target.value)} />
              </div>
            )}
            <div style={{ padding: '4px 0', maxHeight: 300, overflowY: 'auto' }}>
              <Tree multi reorderable={!query}
                expanded={exp} onExpand={toggle}
                selected={sel} onSelect={setSel} nodes={view}
                onMove={(ids, t, pos) => setNodes(prev => treeMove(prev, ids, t, pos))} />
              {view.length === 0 && <div style={{ padding: 14, fontSize: 12, color: 'var(--dcs-text-mute)' }}>No matches for "{q}"</div>}
            </div>
          </Panel>
        </div>
      </Demo>
    </section>
  );
}

/* ─────────── Typed cross-view drag & drop ─────────── */
const DND_ASSETS = [
  { id: 'as-brass',  label: 'Brass_worn.mat', icon: 'palette' },
  { id: 'as-oak',    label: 'OakBark_2k.png', icon: 'texture' },
  { id: 'as-marble', label: 'Marble_4k.exr',  icon: 'image' },
  { id: 'as-normal', label: 'rock_normal.png', icon: 'texture' },
  { id: 'as-walk',   label: 'walk.anim',      icon: 'curve' },
  { id: 'as-chrome', label: 'Chrome.mat',     icon: 'palette' },
];
const DND_HIER = [{
  id: 'scene', label: 'Scene', icon: 'globe', children: [
    { id: 'env', label: 'Environment', icon: 'folder-open', children: [
      { id: 'ground', label: 'Ground', icon: 'plane' },
      { id: 'sky', label: 'Sky', icon: 'image' },
    ] },
    { id: 'hero', label: 'Hero', icon: 'folder-open', children: [
      { id: 'body', label: 'body.geo', icon: 'mesh' },
    ] },
    { id: 'cam', label: 'Camera', icon: 'camera' },
  ],
}];
let dndUid = 0;

// An inspector channel that accepts a dragged asset or hierarchy item (DnD).
function LinkField({ label, value, onLink, onClear }) {
  return (
    <div className="dcs-field">
      <span className="dcs-field__label">{label}</span>
      <DropZone accept={['asset', 'node']} onDrop={(p) => onLink(p.items[0])}
        className={`dcs-linkfield${value ? ' dcs-linkfield--set' : ''}`}>
        {value
          ? <><Icon name={value.icon || 'link'} size="sm" /><span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.label}</span>
              <span className="dcs-dockpane__tab-close" style={{ opacity: .6 }} onClick={onClear}><Icon name="close" size="sm" /></span></>
          : <span className="dcs-linkfield__empty">drop an asset…</span>}
      </DropZone>
    </div>
  );
}

function SectionDragDrop() {
  const [assetSel, setAssetSel] = useStateD(() => new Set());
  const [tree, setTree] = useStateD(DND_HIER);
  const [hierSel, setHierSel] = useStateD(() => new Set());
  const [exp, setExp] = useStateD(() => new Set(['scene', 'env', 'hero']));
  const [base, setBase] = useStateD(null);
  const [normal, setNormal] = useStateD(null);
  const toggle = (id) => setExp(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const dropAssets = (payload, { targetId, pos }) => {
    const added = payload.items.map(it => ({ id: `h${++dndUid}_${it.id}`, label: it.label, icon: it.icon }));
    setTree(t => treeInsert(t, added, targetId, pos));
  };
  const pane = { borderRight: '1px solid var(--dcs-line)', borderRadius: 0 };
  return (
    <section className="dw-section" id="dnd">
      <div className="dw-section__eyebrow">Data · 03</div>
      <h2>Drag &amp; drop</h2>
      <p className="dw-section__lead">
        A typed drag-and-drop system at the React layer — the backbone of a DCC tool. Every source carries a
        {' '}<strong style={{ color: 'var(--dw-text)' }}>type</strong>; every target declares which types it
        {' '}<strong style={{ color: 'var(--dw-text)' }}>accepts</strong> and verifies with a callback, lighting up
        {' '}<strong style={{ color: 'var(--dw-text)' }}>valid</strong> (accent) or <strong style={{ color: 'var(--dw-text)' }}>invalid</strong> (red).
        Multi-select assets and drag them into the hierarchy, reorder/reparent the hierarchy itself, or drop an asset
        {' '}<em>or</em> a hierarchy item onto an inspector channel to <strong style={{ color: 'var(--dw-text)' }}>link</strong> it.
      </p>
      <Demo frame="app" caption="useDrag / useDrop + Draggable / DropZone — sources & targets are typed; targets validate by type + canDrop">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr 1.15fr', height: 340, border: '1px solid var(--dcs-line)', borderRadius: 'var(--dcs-r-2)', overflow: 'hidden', background: 'var(--dcs-bg)' }}>
          <Panel title="Assets" icon="folder" pad={0} style={pane}
                 meta={assetSel.size ? `${assetSel.size} selected` : null}>
            <div style={{ padding: '4px 0', height: '100%', overflowY: 'auto' }}>
              <Tree flat multi source dragType="asset" nodes={DND_ASSETS} selected={assetSel} onSelect={setAssetSel} />
            </div>
          </Panel>
          <Panel title="Hierarchy" icon="layers" pad={0} style={pane}>
            <div style={{ padding: '4px 0', height: '100%', overflowY: 'auto' }}>
              <Tree multi reorderable accept={['asset']} onDropItems={dropAssets}
                expanded={exp} onExpand={toggle} selected={hierSel} onSelect={setHierSel}
                nodes={tree} onMove={(ids, t, pos) => setTree(p => treeMove(p, ids, t, pos))} />
            </div>
          </Panel>
          <Panel title="Inspector · Material" icon="edit" pad="sm" style={{ borderRadius: 0 }}>
            <div className="dcs-props">
              <div className="dcs-field"><span className="dcs-field__label">Name</span><input className="dcs-input" defaultValue="hero_skin" /></div>
              <LinkField label="Base Color" value={base} onLink={setBase} onClear={() => setBase(null)} />
              <LinkField label="Normal" value={normal} onLink={setNormal} onClear={() => setNormal(null)} />
              <div className="dcs-field"><span className="dcs-field__label">Rough</span><Combo value={0.4} min={0} max={1} step={0.01} format={v => v.toFixed(2)} /></div>
            </div>
          </Panel>
        </div>
      </Demo>
    </section>
  );
}

function SectionTables() {
  const [sel, setSel] = useStateD(0);
  const rows = [
    { job: 'Scene_Intro_v014',  frames: '1–240',   engine: 'Cycles',    time: '04:12:08', icon: 'check-circle', ic: 'var(--dcs-ok)',       badge: <span className="dcs-badge dcs-badge--ok dcs-badge--dot">Done</span> },
    { job: 'Hero_Closeup_v003', frames: '48–96',   engine: 'Cycles',    time: '00:48:32', icon: 'play',         ic: 'var(--dcs-accent)',   badge: <span className="dcs-badge dcs-badge--accent dcs-badge--dot">62%</span> },
    { job: 'Title_Anim_v007',   frames: '1–120',   engine: 'Eevee',     time: '—',        icon: 'pause',        ic: 'var(--dcs-text-mute)', badge: <span className="dcs-badge">Queued</span> },
    { job: 'BgPlate_Lit_v002',  frames: '120–240', engine: 'Cycles',    time: '00:00:48', icon: 'alert',        ic: 'var(--dcs-warn)',     badge: <span className="dcs-badge dcs-badge--warn dcs-badge--dot">Missing tex</span> },
    { job: 'Test_Sim_009',      frames: '1–48',    engine: 'Mantaflow', time: '00:12:04', icon: 'error',        ic: 'var(--dcs-danger)',   badge: <span className="dcs-badge dcs-badge--danger dcs-badge--dot">Fault</span> },
    { job: 'Crowd_Wide_v001',   frames: '1–600',   engine: 'Cycles',    time: '11:02:40', icon: 'check-circle', ic: 'var(--dcs-ok)',       badge: <span className="dcs-badge dcs-badge--ok dcs-badge--dot">Done</span> },
    { job: 'Logo_Spin_v012',    frames: '1–96',    engine: 'Eevee',     time: '00:03:11', icon: 'play',         ic: 'var(--dcs-accent)',   badge: <span className="dcs-badge dcs-badge--accent dcs-badge--dot">8%</span> },
    { job: 'Smoke_Test_044',    frames: '1–48',    engine: 'Mantaflow', time: '—',        icon: 'pause',        ic: 'var(--dcs-text-mute)', badge: <span className="dcs-badge">Queued</span> },
  ];
  return (
    <section className="dw-section" id="tables">
      <div className="dw-section__eyebrow">Data · 03</div>
      <h2>Tables</h2>
      <p className="dw-section__lead">
        Render queues, file lists, channel banks. Sticky-headered, mono-numeric, with selectable
        rows — click a row to select it; the body scrolls under the pinned header.
      </p>
      <Demo frame="app" inset>
        <Panel title="Render queue" icon="render" pad={0}
               tools={<><Button ghost sm icon iconLeft="play" /><Button ghost sm icon iconLeft="pause" /><Button ghost sm icon iconLeft="trash" /></>}>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table className="dcs-table dcs-table--mono">
              <thead>
                <tr><th style={{ width: 28 }}></th><th>Job</th><th>Frames</th><th>Engine</th><th>Time</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.job} aria-selected={sel === i} onClick={() => setSel(i)} style={{ cursor: 'default' }}>
                    <td><Icon name={r.icon} style={{ color: r.ic }} /></td>
                    <td style={{ fontFamily: 'var(--dcs-font)' }}>{r.job}</td>
                    <td>{r.frames}</td><td>{r.engine}</td><td>{r.time}</td><td>{r.badge}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </Demo>
    </section>
  );
}

function SectionBadges() {
  return (
    <section className="dw-section" id="badges">
      <div className="dw-section__eyebrow">Data · 04</div>
      <h2>Badges &amp; keys</h2>
      <p className="dw-section__lead">
        Six semantic chips and a kbd glyph for shortcut hints. Composable on any background — pick
        the variant by signal, not by hue you happen to like.
      </p>
      <Demo>
        <div className="dcs-col" style={{ gap: 12 }}>
          <div className="dcs-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="dcs-badge">DEFAULT</span>
            <span className="dcs-badge dcs-badge--accent">ACCENT</span>
            <span className="dcs-badge dcs-badge--ok dcs-badge--dot">READY</span>
            <span className="dcs-badge dcs-badge--warn dcs-badge--dot">CLIPPING</span>
            <span className="dcs-badge dcs-badge--danger dcs-badge--dot">FAULT</span>
            <span className="dcs-badge dcs-badge--accent">v0.6.2</span>
          </div>
          <div className="dcs-row" style={{ gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--dcs-text-dim)' }}>Snap to grid</span>
            <span className="dcs-kbd">⇧</span><span className="dcs-kbd">G</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: 'var(--dcs-text-dim)' }}>Frame all</span>
            <span className="dcs-kbd">F</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: 'var(--dcs-text-dim)' }}>Render</span>
            <span className="dcs-kbd">⌃</span><span className="dcs-kbd">F12</span>
          </div>
        </div>
      </Demo>
    </section>
  );
}

Object.assign(window, { SectionLists, SectionTrees, SectionDragDrop, SectionTables, SectionBadges, SectionCards });

function SectionCards() {
  const [sel, setSel] = useStateD('m2');
  const materials = [
    { id: 'm1', name: 'Lambert.001',   meta: 'PBR · 4K · sRGB', g: 'linear-gradient(135deg, #4d9fff, #1c4080)' },
    { id: 'm2', name: 'CarPaint_Red',  meta: 'PBR · 4K · clearcoat', g: 'linear-gradient(135deg, #ff7070, #8a1818)' },
    { id: 'm3', name: 'Concrete_R',    meta: 'PBR · 2K', g: 'linear-gradient(135deg, #aab0bd, #6b7180)' },
    { id: 'm4', name: 'GlassGreen_01', meta: 'PBR · 1K · transmissive', g: 'linear-gradient(135deg, #4ed18a, #1a6e44)' },
    { id: 'm5', name: 'Brushed_Steel', meta: 'PBR · 4K', g: 'repeating-linear-gradient(45deg, #cfd4dc, #cfd4dc 1px, #aab0bd 1px, #aab0bd 3px)' },
    { id: 'm6', name: 'Marble_Statue', meta: 'PBR · 4K', g: 'linear-gradient(45deg, #e7e9ee, #aab0bd)' },
  ];
  const recent = [
    { id: 'r1', name: 'jane_walk_v02.fbx', meta: '48 bones · 2.4 MB · 4 minutes ago', icon: 'bone' },
    { id: 'r2', name: 'crate_lod1.usd',    meta: '128 verts · 14 KB · today',          icon: 'cube' },
    { id: 'r3', name: 'sky_dawn_4k.hdr',   meta: 'HDR · 32-bit · yesterday',           icon: 'image' },
  ];
  return (
    <section className="dw-section" id="cards">
      <div className="dw-section__eyebrow">Data · 05</div>
      <h2>Cards</h2>
      <p className="dw-section__lead">
        A reusable container for browseable items: assets, presets, recent files, render queue
        thumbnails. Cards compose into <code>CardGrid</code> for visual browsers and
        <code>CardList</code> for vertical stacks. Hover reveals tools and an optional close X.
      </p>

      <Demo frame="app" caption="Card grid — material browser, with selection, hover tools, and close affordance">
        <CardGrid>
          {materials.map(m => (
            <Card
              key={m.id}
              selected={sel === m.id}
              onClick={() => setSel(m.id)}
              closeable
              media={<div style={{ aspectRatio: '1', background: m.g }} />}
              title={m.name}
              meta={m.meta}
              badges={<span className="dcs-badge dcs-badge--soft">PBR</span>}
              tools={<><Button sm icon iconLeft="eye" /><Button sm icon iconLeft="more-v" /></>}
            />
          ))}
        </CardGrid>
      </Demo>

      <Demo frame="app" caption="Card list — horizontal layout for vertical stacks">
        <CardList style={{ maxWidth: 520 }}>
          {recent.map(r => (
            <Card
              key={r.id}
              horizontal
              closeable
              media={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--dcs-accent)' }}><Icon name={r.icon} size="xl" /></div>}
              title={r.name}
              meta={r.meta}
              tools={<><Button sm icon iconLeft="export" /><Button sm icon iconLeft="copy" /></>}
            />
          ))}
        </CardList>
      </Demo>

      <Demo frame="app" caption="Inside a panel — close button removes the card from the layout">
        <Panel title="Active layers" icon="layers" pad="sm" style={{ maxWidth: 380 }}>
          <CardList>
            <Card title="Background_v3" meta="2,048 × 2,048 · 12 KB" closeable
                  media={<div style={{ aspectRatio: '4/1', background: 'linear-gradient(90deg, #1c4080, #4d9fff)' }} />}
                  badges={<span className="dcs-badge dcs-badge--ok dcs-badge--dot">VISIBLE</span>}
            />
            <Card title="Hero_Pose" meta="4 channels · 144 KB" closeable
                  media={<div style={{ aspectRatio: '4/1', background: 'radial-gradient(circle at 30% 50%, #ff7ab8, #6f4eea)' }} />}
                  badges={<span className="dcs-badge dcs-badge--accent">ALPHA</span>}
            />
            <Card title="FX_Sparks" meta="emitter · 1,200 particles" closeable
                  media={<div style={{ aspectRatio: '4/1', background: 'radial-gradient(ellipse at 70% 50%, #f2b14a, #14161c)' }} />}
                  badges={<span className="dcs-badge dcs-badge--warn dcs-badge--dot">SOLO</span>}
            />
          </CardList>
        </Panel>
      </Demo>
    </section>
  );
}


/* ===== sections-feedback.jsx ===== */
/* sections-feedback.jsx
   Alerts · Modals · Toasts
*/
const { useState: useStateFB } = React;

function SectionAlerts() {
  return (
    <section className="dw-section" id="alerts">
      <div className="dw-section__eyebrow">Feedback · 01</div>
      <h2>Alerts</h2>
      <p className="dw-section__lead">
        Inline messages with a 3px semantic border-left and a leading icon. Slot them into panel
        bodies, headers, or full-width docks — they keep their rhythm.
      </p>
      <Demo>
        <div className="dcs-col" style={{ gap: 10 }}>
          <div className="dcs-alert dcs-alert--ok">
            <div className="dcs-alert__icon"><Icon name="check-circle" /></div>
            <div className="dcs-alert__body">
              <div className="dcs-alert__title">Bake complete</div>
              <div className="dcs-alert__msg">240 frames · 4 h 12 m · cached to <code style={{ fontFamily: 'var(--dcs-font-mono)' }}>./cache/intro_v014</code></div>
            </div>
            <Button ghost sm icon iconLeft="close" />
          </div>
          <div className="dcs-alert">
            <div className="dcs-alert__icon"><Icon name="info" /></div>
            <div className="dcs-alert__body">
              <div className="dcs-alert__title">New version available</div>
              <div className="dcs-alert__msg">decius v0.5 "Tullus" introduces vector field knobs and node thumbnails.</div>
            </div>
          </div>
          <div className="dcs-alert dcs-alert--warn">
            <div className="dcs-alert__icon"><Icon name="alert" /></div>
            <div className="dcs-alert__body">
              <div className="dcs-alert__title">GPU clipping detected</div>
              <div className="dcs-alert__msg">2 channels exceeded headroom on frame 081. Lower exposure or enable tone-map clamp.</div>
            </div>
          </div>
          <div className="dcs-alert dcs-alert--danger">
            <div className="dcs-alert__icon"><Icon name="error" /></div>
            <div className="dcs-alert__body">
              <div className="dcs-alert__title">Plugin failed to load</div>
              <div className="dcs-alert__msg"><code style={{ fontFamily: 'var(--dcs-font-mono)' }}>libfx_volumetrics.so</code> — symbol <code style={{ fontFamily: 'var(--dcs-font-mono)' }}>fx_init_v3</code> not found.</div>
            </div>
          </div>
        </div>
      </Demo>
    </section>
  );
}

function SectionModals() {
  const [open, setOpen] = useStateFB(null);
  return (
    <section className="dw-section" id="modals">
      <div className="dw-section__eyebrow">Feedback · 02</div>
      <h2>Modals</h2>
      <p className="dw-section__lead">
        For destructive confirmations, large-form preferences, and "are you sure" gates. The
        backdrop blurs the world behind it; the modal lifts on a strong shadow.
      </p>

      <Demo>
        <div className="dcs-row" style={{ gap: 10 }}>
          <Button onClick={() => setOpen('confirm')} iconLeft="trash">Delete selected…</Button>
          <Button primary onClick={() => setOpen('save')} iconLeft="save">Save as…</Button>
          <Button ghost onClick={() => setOpen('prefs')} iconLeft="cog">Preferences…</Button>
        </div>
      </Demo>

      {open === 'confirm' && (
        <div className="dcs">
          <div className="dcs-modal-backdrop" onClick={() => setOpen(null)}>
            <div className="dcs-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
              <div className="dcs-modal__header"><Icon name="alert" style={{ color: 'var(--dcs-warn)' }} /><span>Delete 3 objects?</span></div>
              <div className="dcs-modal__body">
                <p style={{ margin: 0, color: 'var(--dcs-text-dim)' }}>This will remove <strong style={{ color: 'var(--dcs-text)' }}>Cube.003, Cube.004, Light.002</strong> from the scene. Linked instances stay intact.</p>
                <div style={{ marginTop: 12 }}>
                  <Check checked={false}>Also delete from outliner collections</Check>
                </div>
              </div>
              <div className="dcs-modal__footer">
                <Button ghost onClick={() => setOpen(null)}>Cancel</Button>
                <Button danger iconLeft="trash" onClick={() => setOpen(null)}>Delete</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {open === 'save' && (
        <div className="dcs">
          <div className="dcs-modal-backdrop" onClick={() => setOpen(null)}>
            <div className="dcs-modal" style={{ width: 500 }} onClick={e => e.stopPropagation()}>
              <div className="dcs-modal__header"><Icon name="save" /><span>Save project as…</span></div>
              <div className="dcs-modal__body">
                <div className="dcs-col" style={{ gap: 10 }}>
                  <div className="dcs-field"><label className="dcs-field__label">Name</label><input className="dcs-input" defaultValue="Scene_Intro_v014.dcs" style={{ flex: 1 }} /></div>
                  <div className="dcs-field"><label className="dcs-field__label">Location</label><input className="dcs-input" defaultValue="~/work/intro/" style={{ flex: 1 }} /><Button sm ghost iconLeft="folder">Browse…</Button></div>
                  <div className="dcs-field"><label className="dcs-field__label">Format</label>
                    <select className="dcs-select" style={{ flex: 1 }}>
                      <option>.dcs · native (zip)</option>
                      <option>.usd · packaged</option>
                      <option>.fbx · export</option>
                    </select>
                  </div>
                  <div className="dcs-field"><label className="dcs-field__label">&nbsp;</label><Check checked>Bundle textures</Check></div>
                  <div className="dcs-field"><label className="dcs-field__label">&nbsp;</label><Check checked={false}>Snapshot to history</Check></div>
                </div>
              </div>
              <div className="dcs-modal__footer">
                <Button ghost onClick={() => setOpen(null)}>Cancel</Button>
                <Button primary iconLeft="save" onClick={() => setOpen(null)}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {open === 'prefs' && (
        <div className="dcs">
          <div className="dcs-modal-backdrop" onClick={() => setOpen(null)}>
            <div className="dcs-modal" style={{ width: 640 }} onClick={e => e.stopPropagation()}>
              <div className="dcs-modal__header"><Icon name="cog" /><span>Preferences</span><span style={{ flex: 1 }} /><Button ghost sm icon iconLeft="close" onClick={() => setOpen(null)} /></div>
              <div style={{ display: 'flex', minHeight: 320 }}>
                <div style={{ width: 160, borderRight: '1px solid var(--dcs-line)', background: 'var(--dcs-surface-1)' }}>
                  <div className="dcs-tree" style={{ padding: '6px 0' }}>
                    {['Interface', 'Theme', 'Viewport', 'Animation', 'Files', 'Performance', 'Shortcuts', 'Plugins'].map((n, i) => (
                      <div key={n} className="dcs-tree__row" aria-selected={i === 1} style={{ '--depth': 0, paddingLeft: 14 }}>
                        <span style={{ width: 14 }} />
                        <Icon name={['cog', 'palette', 'cube', 'curve', 'folder', 'cpu', 'bolt', 'rocket'][i]} className="dcs-tree__icon" />
                        <span className="dcs-tree__label">{n}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Theme</div>
                  <div className="dcs-col" style={{ gap: 8 }}>
                    <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 100 }}>Accent</label>
                      <ButtonGroup value="blue" options={[
                        { value: 'blue', label: 'Blue' }, { value: 'cyan', label: 'Cyan' }, { value: 'orange', label: 'Orange' }, { value: 'violet', label: 'Violet' },
                      ]} />
                    </div>
                    <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 100 }}>Darkness</label>
                      <ButtonGroup value="cool" options={[
                        { value: 'dark', label: 'Darker' }, { value: 'cool', label: 'Cool mid' }, { value: 'light', label: 'Lighter' },
                      ]} />
                    </div>
                    <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 100 }}>Density</label>
                      <ButtonGroup value="comf" options={[
                        { value: 'cmp', label: 'Compact' }, { value: 'comf', label: 'Comfortable' }, { value: 'sp', label: 'Spacious' },
                      ]} />
                    </div>
                    <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 100 }}>Smooth UI</label><Switch checked /></div>
                    <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 100 }}>Hi-DPI line scale</label><Slider value={0.6} /></div>
                  </div>
                </div>
              </div>
              <div className="dcs-modal__footer">
                <Button ghost onClick={() => setOpen(null)}>Close</Button>
                <Button primary onClick={() => setOpen(null)}>Apply</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

Object.assign(window, { SectionAlerts, SectionModals });


/* ===== sections-overlays.jsx ===== */
/* sections-overlays.jsx — new components (menus, popovers, toasts) and the
   vanilla decius.js runtime docs. Demos use the real framework classes. */
const { useState: useStateO, useRef: useRefO, useEffect: useEffectO } = React;
// useDismiss + MenuList now live in dcs.jsx (shared with MenuBar/DockLayout).

/* ─────────── Menus ─────────── */
const MENUS = {
  File: [
    { label: 'New', icon: 'file', shortcut: '⌘N' },
    { label: 'Open…', icon: 'folder-open', shortcut: '⌘O' },
    { sep: true },
    { label: 'Export As', icon: 'export', sub: [{ label: 'PNG' }, { label: 'EXR' }, { label: 'glTF' }, { label: 'USD' }] },
    { sep: true },
    { label: 'Delete', icon: 'trash', danger: true },
  ],
  Edit: [
    { label: 'Undo', icon: 'undo', shortcut: '⌘Z' },
    { label: 'Redo', icon: 'redo', shortcut: '⇧⌘Z' },
    { sep: true },
    { label: 'Cut', icon: 'cut' }, { label: 'Copy', icon: 'copy' }, { label: 'Paste', icon: 'paste' },
  ],
  View: [
    { label: 'Wireframe', check: true }, { label: 'Grid', check: true }, { label: 'Gizmos' },
    { sep: true },
    { label: 'Fullscreen', icon: 'fullscreen', shortcut: 'F11' },
  ],
  Help: [{ label: 'Documentation', icon: 'help' }, { label: 'About decius', icon: 'info' }],
};

function MenuBarDemo({ onPick }) {
  const [open, setOpen] = useStateO(null);
  const ref = useRefO(null);
  useDismiss(ref, open !== null, () => setOpen(null));
  return (
    <div className="dcs-menubar" ref={ref}>
      <div className="dcs-menubar__brand"><Icon name="decius" /> <span>modeler</span></div>
      {Object.keys(MENUS).map(name => (
        <div key={name} style={{ position: 'relative', display: 'flex' }}>
          <button
            className="dcs-menubar__item"
            style={open === name ? { background: 'var(--dcs-surface-2)', color: 'var(--dcs-text)' } : null}
            onClick={() => setOpen(o => (o === name ? null : name))}
            onMouseEnter={() => setOpen(o => (o !== null ? name : o))}
          >{name}</button>
          {open === name && (
            <div className="dcs-menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2 }}>
              <MenuList items={MENUS[name]} onPick={(v) => { onPick(v); setOpen(null); }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DropdownDemo({ onPick }) {
  const [open, setOpen] = useStateO(false);
  const ref = useRefO(null);
  useDismiss(ref, open, () => setOpen(false));
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Button primary iconLeft="cog" onClick={() => setOpen(o => !o)}>Options <Icon name="chevron-down" size="sm" /></Button>
      {open && (
        <div className="dcs-menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4 }}>
          <MenuList items={MENUS.View} onPick={(v) => { onPick(v); setOpen(false); }} />
        </div>
      )}
    </div>
  );
}

function ContextMenuDemo({ onPick }) {
  const [pos, setPos] = useStateO(null);
  const ref = useRefO(null);
  useDismiss(ref, pos !== null, () => setPos(null));
  const items = [
    { label: 'Cut', icon: 'cut' }, { label: 'Copy', icon: 'copy' }, { label: 'Paste', icon: 'paste' },
    { sep: true }, { label: 'Rename', icon: 'edit' }, { label: 'Delete', icon: 'trash', danger: true },
  ];
  return (
    <>
      <div
        className="dcs-well"
        onContextMenu={(e) => { e.preventDefault(); setPos({ x: e.clientX, y: e.clientY }); }}
        style={{ flex: 1, minWidth: 180, padding: 18, borderRadius: 5, textAlign: 'center', color: 'var(--dcs-text-mute)', cursor: 'context-menu' }}
      >
        right-click here
      </div>
      {pos && (
        <div ref={ref} className="dcs-menu" style={{ position: 'fixed', top: pos.y, left: pos.x, zIndex: 9999 }}>
          <MenuList items={items} onPick={(v) => { onPick(v); setPos(null); }} />
        </div>
      )}
    </>
  );
}

function SectionMenus() {
  const [last, setLast] = useStateO('—');
  return (
    <section className="dw-section" id="menus">
      <div className="dw-section__eyebrow">Overlays · 01</div>
      <h2>Menus &amp; dropdowns</h2>
      <p className="dw-section__lead">
        Floating command menus for menubars, buttons, and right-click. Items take icons, shortcuts,
        checks, danger styling, separators, and nested submenus (hover <em>Export As</em>). All live:
      </p>
      <Demo frame="app" caption="menu bar · dropdown button · right-click context menu">
        <div className="dcs-col" style={{ gap: 0 }}>
          <MenuBarDemo onPick={setLast} />
          <div className="dcs-row" style={{ gap: 16, alignItems: 'flex-start', padding: 16 }}>
            <DropdownDemo onPick={setLast} />
            <ContextMenuDemo onPick={setLast} />
          </div>
          <div style={{ padding: '0 16px 14px', fontSize: 12, fontFamily: 'var(--dcs-font-mono)', color: 'var(--dcs-text-mute)' }}>
            last action: <span style={{ color: 'var(--dcs-accent)' }}>{last}</span>
          </div>
        </div>
      </Demo>
      <CodeBlock lang="html" code={`<button class="dcs-btn" data-dcs-toggle="menu" data-dcs-target="#file-menu">File</button>
<div class="dcs-menu" id="file-menu">
  <div class="dcs-menu__item" data-dcs-value="new">
    <span class="dcs-menu__icon"><i class="di di-file"></i></span>
    <span class="dcs-menu__label-text">New</span><span class="dcs-menu__shortcut">⌘N</span>
  </div>
  <div class="dcs-menu__item dcs-menu__item--has-sub">Export As
    <div class="dcs-menu dcs-menu__sub"><div class="dcs-menu__item">PNG</div></div>
  </div>
</div>
<div data-dcs-menu="#file-menu">right-click me</div>   <!-- context menu -->`} />
    </section>
  );
}

/* ─────────── Popovers ─────────── */
function PopoverDemo({ placement = 'bottom', label }) {
  const [open, setOpen] = useStateO(false);
  const [opacity, setOpacity] = useStateO(0.8);
  const ref = useRefO(null);
  useDismiss(ref, open, () => setOpen(false));
  const top = placement === 'bottom';
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Button primary={open} iconLeft="info" onClick={() => setOpen(o => !o)}>{label}</Button>
      {open && (
        <div className="dcs-popover" data-dcs-pos={placement}
             style={{ position: 'absolute', left: 0, width: 240, [top ? 'top' : 'bottom']: '100%', [top ? 'marginTop' : 'marginBottom']: 10 }}>
          <div className="dcs-popover__arrow" style={{ [top ? 'top' : 'bottom']: -6, left: 20 }} />
          <div className="dcs-popover__header">Layer settings<span style={{ flex: 1 }} />
            <span className="dcs-toast__close" onClick={() => setOpen(false)}><Icon name="close" size="sm" /></span>
          </div>
          <div className="dcs-popover__body dcs-col" style={{ gap: 10 }}>
            <div className="dcs-field"><span className="dcs-field__label">Opacity</span><Slider value={opacity} onChange={setOpacity} /><span className="dcs-mono" style={{ width: 34, textAlign: 'right' }}>{Math.round(opacity * 100)}</span></div>
            <div className="dcs-field" style={{ justifyContent: 'space-between' }}><span style={{ fontSize: 11 }}>Lock layer</span><Switch /></div>
            <div className="dcs-field" style={{ justifyContent: 'space-between' }}><span style={{ fontSize: 11 }}>Blend</span><ButtonGroup value="normal" options={[{ value: 'normal', label: 'Norm' }, { value: 'add', label: 'Add' }, { value: 'mul', label: 'Mul' }]} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionPopovers() {
  return (
    <section className="dw-section" id="popovers">
      <div className="dw-section__eyebrow">Overlays · 02</div>
      <h2>Popovers</h2>
      <p className="dw-section__lead">
        A small floating card anchored to a trigger — richer than a tooltip, it can hold real
        controls. Click to open; outside-click or <code>Esc</code> dismisses.
        <code>data-dcs-placement</code> picks the side and the arrow follows.
      </p>
      <Demo frame="app" caption="click a button — the popover holds live controls">
        <div className="dcs-row" style={{ gap: 16, padding: '12px 4px 56px' }}>
          <PopoverDemo placement="bottom" label="Below" />
          <PopoverDemo placement="top" label="Above" />
        </div>
      </Demo>
      <CodeBlock lang="html" code={`<button class="dcs-btn" data-dcs-toggle="popover"
        data-dcs-target="#info" data-dcs-placement="top">Details</button>

<div class="dcs-popover" id="info">
  <div class="dcs-popover__header">Layer settings</div>
  <div class="dcs-popover__body">…controls…</div>
  <div class="dcs-popover__arrow"></div>
</div>`} />
    </section>
  );
}

/* ─────────── Toasts (live) ─────────── */
const TOAST_ICON = { ok: 'check-circle', warn: 'alert', danger: 'error', info: 'info' };
function SectionToasts() {
  const [toasts, setToasts] = useStateO([]);
  const push = (variant, title, msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, variant, title, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  };
  return (
    <section className="dw-section" id="toasts">
      <div className="dw-section__eyebrow">Overlays · 03</div>
      <h2>Toasts &amp; notifications</h2>
      <p className="dw-section__lead">
        Transient, stacked notifications. Create them in one call — <code>decius.toast(…)</code> —
        with <code>ok</code>, <code>warn</code>, <code>danger</code>, and <code>info</code> variants.
        Try it:
      </p>
      <Demo caption="real toasts, dismissed automatically">
        <div className="dcs-row" style={{ gap: 8 }}>
          <Button iconLeft="check-circle" onClick={() => push('ok', 'Saved', 'scene.blend written')}>OK</Button>
          <Button iconLeft="alert" onClick={() => push('warn', 'Heads up', 'Unsaved changes')}>Warn</Button>
          <Button danger iconLeft="error" onClick={() => push('danger', 'Render failed', 'GPU out of memory')}>Danger</Button>
          <Button iconLeft="info" onClick={() => push('info', 'Exported', 'turntable.mp4 · 1080p')}>Info</Button>
        </div>
      </Demo>
      <CodeBlock lang="js" code={`decius.toast({
  title: 'Saved',
  message: 'scene.blend written',
  variant: 'ok',        // ok | warn | danger | info
  timeout: 4000,        // ms; 0 = sticky
  placement: 'bottom-right',
});`} />
      {toasts.length > 0 && (
        <div className="dcs" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
          <div className="dcs-toasts" style={{ position: 'static', width: 320 }}>
            {toasts.map((t) => (
              <div key={t.id} className={`dcs-toast dcs-toast--${t.variant}`}>
                <div className="dcs-toast__icon"><Icon name={TOAST_ICON[t.variant]} /></div>
                <div className="dcs-toast__body">
                  <div className="dcs-toast__title">{t.title}</div>
                  <div className="dcs-toast__msg">{t.msg}</div>
                </div>
                <div className="dcs-toast__close" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}><Icon name="close" size="sm" /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────── JavaScript runtime ─────────── */
function SectionJavaScript() {
  return (
    <section className="dw-section" id="javascript">
      <div className="dw-section__eyebrow">Overview · JavaScript</div>
      <h2>JavaScript</h2>
      <p className="dw-section__lead">
        Components that need behavior — collapse, modals, menus, popovers, tabs, toasts, and the
        drag controls (sliders, faders, knobs, combo fields) — are driven by <code>decius.js</code>:
        a single, <strong style={{ color: 'var(--dw-text)' }}>zero-dependency</strong> vanilla
        script. No React required. (A React layer exists too — it's what powers this site.)
      </p>

      <div className="dw-subhead"><h3>1. Include it</h3><div className="dw-subhead__meta">~14 kB min</div></div>
      <CodeBlock lang="html" code={`<link rel="stylesheet" href="…/decius.bundle.min.css">
<script src="…/decius.min.js"></script>
<!-- auto-initializes on DOMContentLoaded -->`} />

      <div className="dw-subhead"><h3>2. Use the data API</h3><div className="dw-subhead__meta">Bootstrap-style</div></div>
      <CodeBlock lang="html" code={`<!-- modal -->
<button data-dcs-toggle="modal" data-dcs-target="#m">Open</button>
<div class="dcs-modal-backdrop" id="m"> … <button data-dcs-dismiss="modal">Close</button> </div>

<!-- tabs -->
<div class="dcs-tabs">
  <button class="dcs-tab" aria-selected="true" data-dcs-target="#p1">Mesh</button>
  <button class="dcs-tab" data-dcs-target="#p2">Shading</button>
</div>
<div id="p1" data-dcs-tabpanel>…</div>
<div id="p2" data-dcs-tabpanel hidden>…</div>

<!-- controls build themselves from data-* -->
<div data-dcs-slider data-min="0" data-max="1" data-value="0.6"></div>
<div data-dcs-knob data-min="0" data-max="1" data-value="0.4" data-label="CUT"></div>`} />

      <div className="dw-subhead"><h3>3. Or call the API</h3><div className="dw-subhead__meta">window.decius</div></div>
      <CodeBlock lang="js" code={`decius.toast({ title: 'Saved', variant: 'ok' });
decius.modal.open('#confirm');
decius.menu.open('#ctx', { x: 120, y: 200 });
decius.init(container);   // wire up dynamically-added markup

// components emit events:
slider.addEventListener('input', (e) => console.log(e.detail.value));
menu.addEventListener('dcs:select', (e) => console.log(e.detail.value));`} />

      <div style={{ marginTop: 24 }}>
        <div className="dcs-alert" style={{ background: 'var(--dw-bg-soft)', borderColor: 'var(--dw-line)', borderLeftColor: 'var(--dw-accent)', color: 'var(--dw-text)' }}>
          <div className="dcs-alert__icon"><Icon name="bolt" /></div>
          <div className="dcs-alert__body">
            <div className="dcs-alert__title" style={{ color: 'var(--dw-text)' }}>Built for affineui</div>
            <div className="dcs-alert__msg" style={{ color: 'var(--dw-text-dim)' }}>
              On <a href="https://github.com/benjcooley/affineui">affineui</a>, your app supplies behavior natively;
              <code>decius.js</code> is the browser-side equivalent so the same markup is interactive anywhere.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { SectionMenus, SectionPopovers, SectionToasts, SectionJavaScript });


/* ===== sections-editors.jsx ===== */
/* sections-editors.jsx
   Color pickers · Curve · Graph · Texture pickers
*/
const { useState: useStateE, useRef: useRefE, useEffect: useEffectE } = React;

/* ─────────── Color helpers ─────────── */
function hsvToRgb(h, s, v) {
  const c = v * s;
  const hh = (h / 60) % 6;
  const x = c * (1 - Math.abs(hh % 2 - 1));
  let r=0, g=0, b=0;
  if (hh < 1) [r,g,b] = [c,x,0];
  else if (hh < 2) [r,g,b] = [x,c,0];
  else if (hh < 3) [r,g,b] = [0,c,x];
  else if (hh < 4) [r,g,b] = [0,x,c];
  else if (hh < 5) [r,g,b] = [x,0,c];
  else [r,g,b] = [c,0,x];
  const m = v - c;
  return [r+m, g+m, b+m];
}
function rgbHex([r,g,b]) {
  const f = n => Math.round(n*255).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
}
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
}
function rgbToHsv([r, g, b]) {
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
}
const hexToHsv = (hex) => { const rgb = hexToRgb(hex); return rgb && rgbToHsv(rgb); };

/* ─────────── Color wheel + triangle (HSV) ─────────── */
function ColorWheel({ size = 220 }) {
  const [h, setH] = useStateE(210);
  const [s, setS] = useStateE(0.7);
  const [v, setV] = useStateE(0.85);
  const wrapRef = useRefE(null);

  const cx = size / 2, cy = size / 2;
  const outerR = size / 2;
  const ringW = 26;
  const innerR = outerR - ringW;
  const triR = innerR - 6;
  const innerPct = (innerR / outerR) * 100;

  // Triangle vertices: V1 = hue tip (rotates with h), V2 = white, V3 = black
  // hue=0 (red) at top; angles in SVG: 0° = right, 90° = down
  const a1 = (h - 90) * Math.PI / 180;
  const a2 = a1 + (2 * Math.PI / 3);
  const a3 = a1 + (4 * Math.PI / 3);
  const V1 = [cx + triR * Math.cos(a1), cy + triR * Math.sin(a1)];  // hue
  const V2 = [cx + triR * Math.cos(a2), cy + triR * Math.sin(a2)];  // white
  const V3 = [cx + triR * Math.cos(a3), cy + triR * Math.sin(a3)];  // black

  // Convert (s, v) → barycentric (a_hue, b_white, c_black) → point inside triangle
  // v = a + b, s = a / (a + b)
  const a = s * v;
  const b = (1 - s) * v;
  const c = 1 - v;
  const cursorX = a * V1[0] + b * V2[0] + c * V3[0];
  const cursorY = a * V1[1] + b * V2[1] + c * V3[1];

  const rgb = hsvToRgb(h, s, v);
  const hex = rgbHex(rgb);
  const hueColor = rgbHex(hsvToRgb(h, 1, 1));

  // Drag handlers
  const distFromCenter = (x, y) => Math.hypot(x - cx, y - cy);
  const handleDown = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const scale = size / rect.width;
    const startX = (e.clientX - rect.left) * scale;
    const startY = (e.clientY - rect.top) * scale;
    const d = distFromCenter(startX, startY);
    const mode = d > innerR ? 'hue' : 'sv';

    const updateHue = (cx0, cy0) => {
      const ang = Math.atan2(cy0 - cy, cx0 - cx);
      let deg = ang * 180 / Math.PI + 90;
      if (deg < 0) deg += 360;
      if (deg >= 360) deg -= 360;
      setH(deg);
    };
    const updateSV = (px, py) => {
      // Barycentric coords of point in triangle V1V2V3
      const denom = (V2[1] - V3[1]) * (V1[0] - V3[0]) + (V3[0] - V2[0]) * (V1[1] - V3[1]);
      let ba = ((V2[1] - V3[1]) * (px - V3[0]) + (V3[0] - V2[0]) * (py - V3[1])) / denom;
      let bb = ((V3[1] - V1[1]) * (px - V3[0]) + (V1[0] - V3[0]) * (py - V3[1])) / denom;
      let bc = 1 - ba - bb;
      // Clamp to triangle
      ba = Math.max(0, ba); bb = Math.max(0, bb); bc = Math.max(0, bc);
      const sum = ba + bb + bc;
      ba /= sum; bb /= sum; bc /= sum;
      const nv = ba + bb;
      const ns = nv > 0 ? ba / nv : 0;
      setS(Math.max(0, Math.min(1, ns)));
      setV(Math.max(0, Math.min(1, nv)));
    };

    if (mode === 'hue') updateHue(startX, startY);
    else updateSV(startX, startY);

    const move = (ev) => {
      const x = (ev.clientX - rect.left) * scale;
      const y = (ev.clientY - rect.top) * scale;
      if (mode === 'hue') updateHue(x, y);
      else updateSV(x, y);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Hue indicator: a notch on the ring at the current hue
  const hueAng = (h - 90) * Math.PI / 180;
  const hueX1 = cx + (outerR + 2) * Math.cos(hueAng);
  const hueY1 = cy + (outerR + 2) * Math.sin(hueAng);
  const hueX2 = cx + (innerR - 2) * Math.cos(hueAng);
  const hueY2 = cy + (innerR - 2) * Math.sin(hueAng);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: size + 30 }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '1' }}>
      {/* Smooth hue ring (conic gradient masked to a donut) */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'conic-gradient(from 0deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
        WebkitMaskImage: `radial-gradient(circle closest-side, transparent ${innerPct - 0.5}%, #000 ${innerPct}%)`,
        maskImage: `radial-gradient(circle closest-side, transparent ${innerPct - 0.5}%, #000 ${innerPct}%)`,
      }} />
      <svg ref={wrapRef} viewBox={`0 0 ${size} ${size}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', cursor: 'crosshair', touchAction: 'none' }} onPointerDown={handleDown}>
        <defs>
          <linearGradient id="cw-white" x1={V1[0]} y1={V1[1]} x2={V2[0]} y2={V2[1]} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={hueColor} stopOpacity="0" />
            <stop offset="100%" stopColor="#fff" />
          </linearGradient>
          <linearGradient id="cw-black"
            x1={(V1[0] + V2[0]) / 2} y1={(V1[1] + V2[1]) / 2}
            x2={V3[0]} y2={V3[1]} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" />
          </linearGradient>
        </defs>

        {/* Hue thumb (notch) */}
        <line x1={hueX1} y1={hueY1} x2={hueX2} y2={hueY2} stroke="#fff" strokeWidth="2.5" />
        <line x1={hueX1} y1={hueY1} x2={hueX2} y2={hueY2} stroke="#000" strokeWidth="1" />

        {/* Triangle */}
        <polygon points={`${V1[0]},${V1[1]} ${V2[0]},${V2[1]} ${V3[0]},${V3[1]}`} fill={hueColor} stroke="rgba(0,0,0,.3)" strokeWidth=".5" />
        <polygon points={`${V1[0]},${V1[1]} ${V2[0]},${V2[1]} ${V3[0]},${V3[1]}`} fill="url(#cw-white)" />
        <polygon points={`${V1[0]},${V1[1]} ${V2[0]},${V2[1]} ${V3[0]},${V3[1]}`} fill="url(#cw-black)" />

        {/* SV cursor */}
        <circle cx={cursorX} cy={cursorY} r="6" fill="none" stroke="#000" strokeWidth="2" />
        <circle cx={cursorX} cy={cursorY} r="6" fill="none" stroke="#fff" strokeWidth="1.25" />
      </svg>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr 1fr', gap: 4, alignItems: 'center' }}>
        <div style={{ width: 32, height: 32, borderRadius: 4, background: hex, border: '1px solid var(--dcs-line)' }} />
        <input className="dcs-input dcs-num" value={`#${hex.slice(1).toUpperCase()}`} readOnly style={{ textAlign: 'center', padding: 0 }} />
        <input className="dcs-input dcs-num" value={`${Math.round(h)}°`} readOnly style={{ textAlign: 'center', padding: 0 }} />
        <input className="dcs-input dcs-num" value={`${Math.round(s * 100)}/${Math.round(v * 100)}`} readOnly style={{ textAlign: 'center', padding: 0 }} />
      </div>
    </div>
  );
}

/* ─────────── Color picker (embedded large, square+bar) ─────────── */
function ColorPicker({ value, onChange, compact }) {
  const [hsv, setHsv] = useStateE({ h: 210, s: .7, v: .85 });
  const [prevColor] = useStateE('#2f86ee');
  const [mode, setMode] = useStateE('hex');
  const sqRef = useRefE(null);
  const hueRef = useRefE(null);
  const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
  const hex = rgbHex(rgb);
  const hueColor = rgbHex(hsvToRgb(hsv.h, 1, 1));
  const recents = ['#4d9fff', '#2f86ee', '#ff7ab8', '#4ed18a', '#f2b14a', '#b48cff', '#4ad5d5', '#ef6b6b'];

  const dragSquare = (e) => {
    const rect = sqRef.current.getBoundingClientRect();
    const update = (cx, cy) => {
      const s = Math.max(0, Math.min(1, (cx - rect.left) / rect.width));
      const v = 1 - Math.max(0, Math.min(1, (cy - rect.top) / rect.height));
      setHsv(h => ({ ...h, s, v }));
    };
    update(e.clientX, e.clientY);
    const move = ev => update(ev.clientX, ev.clientY);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const dragHue = (e) => {
    const rect = hueRef.current.getBoundingClientRect();
    const update = (cy) => {
      const t = Math.max(0, Math.min(1, (cy - rect.top) / rect.height));
      setHsv(h => ({ ...h, h: t * 360 }));
    };
    update(e.clientY);
    const move = ev => update(ev.clientY);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  return (
    <div style={{ width: compact ? 240 : 320, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Mode tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--dcs-line)', gap: 2 }}>
        {['hex', 'rgb', 'hsl', 'hsv'].map(m => (
          <button key={m}
            onClick={() => setMode(m)}
            style={{
              background: 'transparent', border: 'none',
              padding: '6px 12px', fontSize: 10, letterSpacing: '.08em',
              fontFamily: 'var(--dcs-font-mono)', textTransform: 'uppercase',
              color: mode === m ? 'var(--dcs-accent)' : 'var(--dcs-text-mute)',
              cursor: 'pointer', borderBottom: mode === m ? '1px solid var(--dcs-accent)' : '1px solid transparent',
              marginBottom: -1,
            }}>{m}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button title="Pick from screen"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--dcs-text-mute)', padding: '4px 6px',
          }}><Icon name="eyedropper" /></button>
      </div>

      {/* SV square + vertical hue bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 16px', gap: 8 }}>
        <div ref={sqRef} className="dcs-color-square" style={{ '--hue': hueColor, aspectRatio: '1.2 / 1' }} onPointerDown={dragSquare}>
          <div className="dcs-color-square__cursor" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }} />
        </div>
        <div ref={hueRef}
             onPointerDown={dragHue}
             style={{
               width: 16,
               background: 'linear-gradient(180deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
               border: '1px solid var(--dcs-line)',
               borderRadius: 'var(--dcs-r-1)',
               position: 'relative',
               cursor: 'ns-resize',
             }}>
          <div style={{
            position: 'absolute', left: -2, right: -2,
            top: `${(hsv.h / 360) * 100}%`,
            transform: 'translateY(-50%)',
            height: 4,
            border: '1px solid #fff',
            background: '#14161c',
            borderRadius: 1,
            pointerEvents: 'none',
          }} />
        </div>
      </div>

      {/* Alpha bar */}
      <div className="dcs-alpha-bar" style={{ '--c': hex }}>
        <div style={{ position: 'absolute', top: -2, bottom: -2, left: '100%', transform: 'translateX(-50%)', width: 4, background: '#fff', border: '1px solid #14161c', borderRadius: 1 }} />
      </div>

      {/* Current / previous comparison */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', height: 28,
        border: '1px solid var(--dcs-line)', borderRadius: 'var(--dcs-r-1)',
        overflow: 'hidden',
        background: 'repeating-conic-gradient(rgba(255,255,255,.05) 0 90deg, rgba(255,255,255,.1) 90deg 180deg) 0 0 / 12px 12px',
      }}>
        <div style={{ background: prevColor, display: 'flex', alignItems: 'flex-end', padding: '2px 6px', fontFamily: 'var(--dcs-font-mono)', fontSize: 9, color: 'rgba(0,0,0,.6)' }}>previous</div>
        <div style={{ background: hex, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '2px 6px', fontFamily: 'var(--dcs-font-mono)', fontSize: 9, color: 'rgba(0,0,0,.6)' }}>new</div>
      </div>

      {/* Value fields by mode */}
      {mode === 'hex' && (
        <div className="dcs-row" style={{ gap: 4 }}>
          <span className="dcs-mono" style={{ fontSize: 11, color: 'var(--dcs-text-mute)', minWidth: 28 }}>#</span>
          <input className="dcs-input dcs-mono" value={hex.slice(1).toUpperCase()} readOnly style={{ flex: 1 }} />
          <Button sm icon iconLeft="copy" />
        </div>
      )}
      {mode === 'rgb' && (
        <div className="dcs-row" style={{ gap: 4 }}>
          {['R', 'G', 'B'].map((k, i) => (
            <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 9, color: 'var(--dcs-text-mute)', textAlign: 'center', fontFamily: 'var(--dcs-font-mono)' }}>{k}</div>
              <input className="dcs-input dcs-mono" defaultValue={Math.round(rgb[i] * 255)} style={{ textAlign: 'center', padding: 0 }} />
            </div>
          ))}
        </div>
      )}
      {mode === 'hsl' && (
        <div className="dcs-row" style={{ gap: 4 }}>
          {[['H', `${Math.round(hsv.h)}°`], ['S', `${Math.round(hsv.s * 100)}%`], ['L', `${Math.round(hsv.v * 50)}%`]].map(([k, v]) => (
            <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 9, color: 'var(--dcs-text-mute)', textAlign: 'center', fontFamily: 'var(--dcs-font-mono)' }}>{k}</div>
              <input className="dcs-input dcs-mono" defaultValue={v} style={{ textAlign: 'center', padding: 0 }} />
            </div>
          ))}
        </div>
      )}
      {mode === 'hsv' && (
        <div className="dcs-row" style={{ gap: 4 }}>
          {[['H', `${Math.round(hsv.h)}°`], ['S', `${Math.round(hsv.s * 100)}%`], ['V', `${Math.round(hsv.v * 100)}%`]].map(([k, v]) => (
            <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 9, color: 'var(--dcs-text-mute)', textAlign: 'center', fontFamily: 'var(--dcs-font-mono)' }}>{k}</div>
              <input className="dcs-input dcs-mono" defaultValue={v} style={{ textAlign: 'center', padding: 0 }} />
            </div>
          ))}
        </div>
      )}

      {!compact && (
        <div>
          <div style={{ fontSize: 9, color: 'var(--dcs-text-mute)', letterSpacing: '.08em', textTransform: 'uppercase', fontFamily: 'var(--dcs-font-mono)', marginBottom: 4 }}>Recent</div>
          <div style={{ display: 'flex', gap: 3 }}>
            {recents.map(c => (
              <div key={c} title={c.toUpperCase()} style={{
                flex: 1, height: 18, background: c,
                border: '1px solid var(--dcs-line)', borderRadius: 2,
                cursor: 'pointer',
              }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Channel-editor color field: a mini swatch at input height that opens a
   small dropdown picker. */
// A reusable color CHIP that scrubs HSV on drag: ←→ hue, ↕ value, Ctrl+←→ sat.
// Same behavior wherever a chip appears (channel widget, color-only, etc.).
function ColorChip({ hsv, setHsv, size }) {
  const onDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, s0 = { ...hsv };
    const clamp = (n) => Math.max(0, Math.min(1, n));
    const move = (ev) => {
      const dx = ev.clientX - sx, dy = sy - ev.clientY;
      let h = s0.h, s = s0.s;
      const v = clamp(s0.v + dy / 200);
      if (ev.ctrlKey || ev.metaKey) s = clamp(s0.s + dx / 200);
      else { h = (s0.h + dx) % 360; if (h < 0) h += 360; }
      setHsv({ h, s, v });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  return (
    <span className="dcs-colorfield__chip" onPointerDown={onDown}
      title="drag: ←→ hue · ↕ value · Ctrl saturation"
      style={{ background: rgbHex(hsvToRgb(hsv.h, hsv.s, hsv.v)), ...(size ? { width: size, height: size } : null) }} />
  );
}

// Compact, bound SV picker for the color-field popover: SV square + a hue
// slider + a mini swatch, driving the field's hsv directly.
function MiniPicker({ hsv, set }) {
  const sqRef = useRefE(null), hueRef = useRefE(null);
  const clamp = (n) => Math.max(0, Math.min(1, n));
  const hueColor = rgbHex(hsvToRgb(hsv.h, 1, 1));
  const hex = rgbHex(hsvToRgb(hsv.h, hsv.s, hsv.v));
  const dragSquare = (e) => {
    e.preventDefault();
    const rect = sqRef.current.getBoundingClientRect(), h0 = hsv.h;
    const upd = (cx, cy) => set({ h: h0, s: clamp((cx - rect.left) / rect.width), v: 1 - clamp((cy - rect.top) / rect.height) });
    upd(e.clientX, e.clientY);
    const move = (ev) => upd(ev.clientX, ev.clientY);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const dragHue = (e) => {
    e.preventDefault();
    const rect = hueRef.current.getBoundingClientRect(), s0 = hsv.s, v0 = hsv.v;
    const upd = (cx) => set({ h: clamp((cx - rect.left) / rect.width) * 360, s: s0, v: v0 });
    upd(e.clientX);
    const move = (ev) => upd(ev.clientX);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  return (
    <div style={{ width: 188, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div ref={sqRef} className="dcs-color-square" style={{ '--hue': hueColor, aspectRatio: '1.4 / 1' }} onPointerDown={dragSquare}>
        <div className="dcs-color-square__cursor" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }} />
      </div>
      <div ref={hueRef} className="dcs-hue-bar" onPointerDown={dragHue}>
        <div className="dcs-hue-bar__cursor" style={{ left: `${(hsv.h / 360) * 100}%` }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 22, height: 22, flex: '0 0 auto', borderRadius: 'var(--dcs-r-1)', background: hex, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.35)' }} />
        <input className="dcs-input dcs-mono" value={hex.toUpperCase()} spellCheck={false}
          onChange={(e) => { const h = hexToHsv(e.target.value); if (h) set(h); }}
          style={{ flex: 1, minWidth: 0, height: 'var(--dcs-h-in)' }} />
        <Button sm icon iconLeft="eyedropper" title="Pick colour from screen" />
      </div>
    </div>
  );
}

// Channel color widget: chip (drag-scrub) + editable/copy-paste hex + dropdown
// picker. `colorOnly` drops the hex for a compact swatch. Row height matches
// the other channel widgets (var(--dcs-h-in)) so inspector rows align.
function ColorField({ value = '#4d9fff', onChange, colorOnly }) {
  const [hsv, setHsv] = useStateE(() => hexToHsv(value) || { h: 210, s: 0.7, v: 0.85 });
  const [open, setOpen] = useStateE(false);
  const [pop, setPop] = useStateE(null);          // fixed-position popover coords
  const ref = useRefE(null);
  useDismiss(ref, open, () => setOpen(false));
  // True popover: fixed to the viewport so it overlays cleanly and never grows
  // the panel's scroll; closes on scroll/resize like a menu.
  useEffectE(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = ref.current.getBoundingClientRect();
    const PW = 204, PH = 232;
    let top = r.bottom + 4; if (top + PH > window.innerHeight) top = Math.max(8, r.top - PH - 4);
    let left = r.left; if (left + PW > window.innerWidth) left = Math.max(8, window.innerWidth - PW - 8);
    setPop({ top, left }); setOpen(true);
  };
  const set = (h) => { setHsv(h); if (onChange) onChange(rgbHex(hsvToRgb(h.h, h.s, h.v))); };
  const hex = rgbHex(hsvToRgb(hsv.h, hsv.s, hsv.v));
  const onHex = (e) => { const h = hexToHsv(e.target.value); if (h) set(h); };
  return (
    <div ref={ref} className={`dcs-colorfield${colorOnly ? ' dcs-colorfield--swatch' : ''}`} style={{ position: 'relative' }}>
      <ColorChip hsv={hsv} setHsv={set} />
      {!colorOnly && (
        <input className="dcs-colorfield__hex" value={hex.toUpperCase()} spellCheck={false}
          onChange={onHex} onPointerDown={(e) => e.stopPropagation()} />
      )}
      <span className="dcs-colorfield__caret" onClick={toggle}><Icon name="chevron-down" size="sm" /></span>
      {open && pop && (
        <div style={{ position: 'fixed', top: pop.top, left: pop.left, zIndex: 200 }}>
          <Panel raised pad="sm" style={{ boxShadow: 'var(--dcs-shadow-pop)', width: 204 }}>
            <MiniPicker hsv={hsv} set={set} />
          </Panel>
        </div>
      )}
    </div>
  );
}

function SectionColorPicker() {
  const [open, setOpen] = useStateE(false);
  const swatches = [
    '#4d9fff', '#2f86ee', '#1c5fb0', '#0b3a78', '#ef6b6b', '#f2b14a', '#4ed18a',
    '#b48cff', '#ff7ab8', '#4ad5d5', '#e7e9ee', '#aab0bd', '#767c8a', '#14161c',
    '#3c424f', '#2a2e38', '#1f222a', '#181a21', '#00b8d4', '#ff8a3a',
  ];
  return (
    <section className="dw-section" id="color-pickers">
      <div className="dw-section__eyebrow">Editors · 01</div>
      <h2>Color pickers</h2>
      <p className="dw-section__lead">
        Three sizes, one model. The large embedded picker for material editors; a popup variant
        that appears next to swatch buttons; tight chip widgets for inspectors and palette wells.
      </p>

      <Demo frame="app" minw={640} caption="HSV wheel — outer ring for hue, inner triangle for saturation/value">
        <div className="dcs-row" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Panel title="Material ▸ HSV" icon="palette" pad="sm">
            <ColorWheel size={240} />
          </Panel>
          <Panel title="Same color · different model" icon="droplet">
            <ColorPicker compact />
          </Panel>
        </div>
      </Demo>

      <Demo frame="app" minw={620} caption="Large embedded — for material / shader panels">
        <div className="dcs-row" style={{ gap: 16, alignItems: 'flex-start' }}>
          <Panel title="Material · Lambert" icon="palette">
            <ColorPicker />
          </Panel>
          <Panel title="Live preview" icon="eye" style={{ minWidth: 200 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ width: '100%', aspectRatio: '1', borderRadius: 6, background: 'radial-gradient(circle at 35% 30%, #b6d6ff, #4d9fff 60%, #1c4080 100%)', boxShadow: 'inset 0 0 24px rgba(0,0,0,.4)' }} />
              <div className="dcs-num" style={{ fontSize: 11, color: 'var(--dcs-text-dim)', lineHeight: 1.7 }}>
                <div>RGB · 077 159 255</div>
                <div>HSV · 210° 70% 100%</div>
                <div>HEX · #4D9FFF</div>
                <div>LIN · 0.072 0.348 1.000</div>
              </div>
            </div>
          </Panel>
        </div>
      </Demo>

      <Demo frame="app" minw={520} caption="Swatch button → popup">
        <div className="dcs-col" style={{ gap: 10, position: 'relative', paddingBottom: open ? 360 : 0 }}>
          <div className="dcs-row" style={{ gap: 12 }}>
            <div className="dcs-field" style={{ minWidth: 0 }}>
              <label className="dcs-field__label">Diffuse</label>
              <div className="dcs-swatch" onClick={() => setOpen(o => !o)}>
                <div className="dcs-swatch__chip" style={{ '--c': '#4d9fff' }} />
                <span>#4D9FFF</span>
              </div>
            </div>
            <div className="dcs-field" style={{ minWidth: 0 }}>
              <label className="dcs-field__label">Specular</label>
              <div className="dcs-swatch">
                <div className="dcs-swatch__chip" style={{ '--c': '#23262e' }} />
                <span>#23262E</span>
              </div>
            </div>
            <div className="dcs-field" style={{ minWidth: 0 }}>
              <label className="dcs-field__label">Emission</label>
              <div className="dcs-swatch">
                <div className="dcs-swatch__chip" style={{ '--c': '#000000' }} />
                <span>#000000</span>
              </div>
            </div>
          </div>
          {open && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 10 }}>
              <Panel raised pad="sm" style={{ boxShadow: 'var(--dcs-shadow-pop)' }}>
                <ColorPicker compact />
              </Panel>
            </div>
          )}
        </div>
      </Demo>

      <Demo frame="app" minw={540} caption="Channel widgets — every inspector control shares one row height, so a textbox, value editor, color chip, dropdown, toggle/checkbox and a multiple-choice group all stack with even prompt → widget metrics.">
        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap', paddingBottom: 260 }}>
          {/* Stacked in an inspector — every row is the same height (var(--dcs-h-in)):
              prompt on the left, widget on the right. They "just stack" uniformly. */}
          <Panel title="Material" icon="palette" pad="sm" style={{ minWidth: 264 }}>
            <div className="dcs-props">
              <div className="dcs-field"><span className="dcs-field__label">Name</span><input className="dcs-input" defaultValue="jane_skin" /></div>
              <div className="dcs-field"><span className="dcs-field__label">Roughness</span><Combo value={0.35} min={0} max={1} step={0.01} format={v => v.toFixed(2)} /></div>
              <div className="dcs-field"><span className="dcs-field__label">Base</span><ColorField value="#4d9fff" /></div>
              <div className="dcs-field"><span className="dcs-field__label">Rim</span><ColorField value="#ff7ab8" /></div>
              <div className="dcs-field"><span className="dcs-field__label">Blend</span>
                <Select value="normal" options={[
                  { value: 'normal', label: 'Normal' }, { value: 'add', label: 'Add' },
                  { value: 'mul', label: 'Multiply' }, { value: 'screen', label: 'Screen' },
                  { value: 'overlay', label: 'Overlay' }, { value: 'sub', label: 'Subtract' },
                ]} />
              </div>
              <div className="dcs-field"><span className="dcs-field__label">Shading</span>
                <ButtonGroup value="pbr" options={[{ value: 'flat', label: 'Flat' }, { value: 'pbr', label: 'PBR' }, { value: 'toon', label: 'Toon' }]} />
              </div>
              <div className="dcs-field"><span className="dcs-field__label">Two-sided</span><Switch /></div>
              <div className="dcs-field"><span className="dcs-field__label">Cast shadow</span><Check checked /></div>
            </div>
          </Panel>
          <div className="dcs-col" style={{ gap: 12, minWidth: 200 }}>
            <div className="dcs-row" style={{ gap: 10, alignItems: 'center' }}>
              <ColorField value="#4ad5d5" colorOnly />
              <ColorField value="#f2b14a" colorOnly />
              <ColorField value="#b48cff" colorOnly />
              <span style={{ fontSize: 11, color: 'var(--dcs-text-mute)' }}>color-only</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--dcs-text-dim)', lineHeight: 1.6, maxWidth: 220 }}>
              Each row is one channel widget at a uniform height —
              <strong style={{ color: 'var(--dw-text)' }}> textbox, value editor, color chip, enum dropdown, toggle / checkbox</strong>,
              and a <strong style={{ color: 'var(--dw-text)' }}>multiple-choice</strong> group — so they line up when stacked.
              The <strong style={{ color: 'var(--dw-text)' }}>Blend</strong> enum opens a popover list (the full form);
              the <strong style={{ color: 'var(--dw-text)' }}>Shading</strong> group is the same kind of choice as inline buttons —
              the shorthand for when the options fit.
              The color chip <strong style={{ color: 'var(--dw-text)' }}>scrubs</strong> on drag
              (←→ hue, ↕ value, Ctrl saturation); its hex is copy/pasteable, and the chevron opens the full picker.
            </div>
          </div>
        </div>
      </Demo>

      <Demo frame="app" caption="Palette well — pin colors per project">
        <Panel title="Palette · Project" icon="palette" pad="sm"
               tools={<><Button ghost sm icon iconLeft="plus" /><Button ghost sm icon iconLeft="eyedropper" /></>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 }}>
            {swatches.map(c => (
              <div key={c} className="dcs-tooltip" data-tip={c.toUpperCase()} style={{
                aspectRatio: '1', borderRadius: 'var(--dcs-r-1)',
                background: c, cursor: 'pointer',
                border: '1px solid var(--dcs-line)',
                boxShadow: 'var(--dcs-bevel-down)'
              }} />
            ))}
            <div style={{ aspectRatio: '1', borderRadius: 'var(--dcs-r-1)', background: 'var(--dcs-well)', border: '1px dashed var(--dcs-line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--dcs-text-mute)' }}>
              <Icon name="plus" size="sm" />
            </div>
          </div>
        </Panel>
      </Demo>
    </section>
  );
}

/* ─────────── Curve editor ─────────── */
// Each point: { x, y, hx, hy } where (hx, hy) is the RIGHT-side tangent
// half-vector in normalized 0..1 space. The left handle mirrors as (-hx, -hy)
// so the curve stays smooth across the point.
function CurveEditor({ height = 220, points: initial, showHandles = true, color = '#4d9fff' }) {
  // Migrate legacy points (with just .h scalar or none) → {x, y, hx, hy}
  const seed = React.useMemo(() => {
    const src = initial || [
      { x: 0,    y: 1 },
      { x: 0.15, y: 0.2 },
      { x: 0.5,  y: 0.6 },
      { x: 0.85, y: 0.1 },
      { x: 1,    y: 0.3 },
    ];
    return src.map((p, i, a) => {
      if (p.hx !== undefined && p.hy !== undefined) return p;
      const prev = a[Math.max(0, i - 1)];
      const next = a[Math.min(a.length - 1, i + 1)];
      const dx = (next.x - prev.x) / 3;
      const dy = (next.y - prev.y) / 3;
      return { x: p.x, y: p.y, hx: dx, hy: dy };
    });
  }, [initial]);

  const [pts, setPts] = useStateE(seed);
  const [active, setActive] = useStateE(2);
  const [size, setSize] = useStateE({ w: 600, h: 260 });
  const wrapRef = useRefE(null);
  const svgRef = useRefE(null);

  useEffectE(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => {
      const r = entries[0].contentRect;
      setSize({ w: Math.max(100, Math.round(r.width)), h: Math.max(60, Math.round(r.height)) });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const W = size.w, H = size.h;
  const px = (p) => p.x * W;
  const py = (p) => (1 - p.y) * H;
  // Handle pixel positions (right + left, mirrored)
  const hxPx = (p) => p.hx * W;
  const hyPx = (p) => -p.hy * H;   // y is flipped in screen coords

  const dragPoint = (i) => (e) => {
    e.stopPropagation();
    setActive(i);
    const rect = svgRef.current.getBoundingClientRect();
    const move = (ev) => {
      const nx = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      const ny = 1 - Math.max(0, Math.min(1, (ev.clientY - rect.top) / rect.height));
      setPts(p => p.map((q, j) => j === i ? { ...q, x: nx, y: ny } : q));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // side = +1 for right handle, -1 for left. Either drag updates the
  // right-handle (hx, hy); left-handle is computed as the mirror.
  const dragHandle = (i, side) => (e) => {
    e.stopPropagation();
    setActive(i);
    const rect = svgRef.current.getBoundingClientRect();
    const center = pts[i];
    const move = (ev) => {
      const nx = (ev.clientX - rect.left) / rect.width;
      const ny = 1 - (ev.clientY - rect.top) / rect.height;
      // Offset from point center, in normalized space
      const dx = (nx - center.x) * side;
      const dy = (ny - center.y) * side;
      // Clamp to a sensible reach
      const max = 0.5;
      setPts(p => p.map((q, j) => j === i ? {
        ...q,
        hx: Math.max(-max, Math.min(max, dx)),
        hy: Math.max(-max, Math.min(max, dy)),
      } : q));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // Build cubic path using each point's tangent as the cubic control offset.
  const path = pts.length < 2 ? '' : pts.reduce((acc, p, i, a) => {
    if (i === 0) return `M ${px(p)} ${py(p)}`;
    const p0 = a[i - 1];
    const p1 = p;
    // cp1 = p0 + p0's right tangent;  cp2 = p1 - p1's right tangent (= p1 + p1.left)
    const cp1x = px(p0) + hxPx(p0);
    const cp1y = py(p0) + hyPx(p0);
    const cp2x = px(p1) - hxPx(p1);
    const cp2y = py(p1) - hyPx(p1);
    return acc + ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${px(p1)} ${py(p1)}`;
  }, '');

  const curveColor = color;       // curve + points take the channel/key color
  const tanColor = '#aab0bd';
  const accent = color;

  return (
    <div ref={wrapRef} className="dcs-graph" style={{ height, position: 'relative' }}>
      <div className="dcs-graph__major" />
      <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <path d={`${path} L ${W} ${H} L 0 ${H} Z`} fill={color} fillOpacity=".12" />
        <path d={path} fill="none" stroke={curveColor} strokeWidth="1.75" />

        {/* Tangent handles for the active point only — draggable in 2D, mirrored */}
        {showHandles && active !== null && active >= 0 && active < pts.length && (() => {
          const p = pts[active];
          const cx = px(p), cy = py(p);
          const dxp = hxPx(p), dyp = hyPx(p);
          const rx = cx + dxp, ry = cy + dyp;
          const lx = cx - dxp, ly = cy - dyp;
          return (
            <g>
              <line x1={lx} y1={ly} x2={rx} y2={ry}
                    stroke={tanColor} strokeWidth="1" strokeDasharray="3 3" opacity=".9" />
              {/* Right handle */}
              <circle cx={rx} cy={ry} r="9" fill="transparent"
                      style={{ cursor: 'move' }}
                      onPointerDown={dragHandle(active, +1)} />
              <circle cx={rx} cy={ry} r="4"
                      fill={accent} stroke="var(--dcs-bg)" strokeWidth="1.5"
                      style={{ pointerEvents: 'none' }} />
              {/* Left handle (mirrored) */}
              <circle cx={lx} cy={ly} r="9" fill="transparent"
                      style={{ cursor: 'move' }}
                      onPointerDown={dragHandle(active, -1)} />
              <circle cx={lx} cy={ly} r="4"
                      fill={accent} stroke="var(--dcs-bg)" strokeWidth="1.5"
                      style={{ pointerEvents: 'none' }} />
            </g>
          );
        })()}

        {pts.map((p, i) => (
          <g key={i} onPointerDown={dragPoint(i)} style={{ cursor: 'move' }}>
            <circle cx={px(p)} cy={py(p)} r="9" fill="transparent" />
            <circle cx={px(p)} cy={py(p)} r="4.5"
                    fill={i === active ? curveColor : 'var(--dcs-bg)'}
                    stroke={curveColor} strokeWidth="1.5" />
            {i === active && (
              <text x={px(p) + 10} y={py(p) - 8} fontFamily="JetBrains Mono" fontSize="10" fill={curveColor}>
                {p.x.toFixed(2)}, {p.y.toFixed(2)}
              </text>
            )}
          </g>
        ))}

        <text x="6" y="14" fontFamily="JetBrains Mono" fontSize="10" fill="var(--dcs-text-mute)" opacity=".6">1.0</text>
        <text x="6" y={H - 6} fontFamily="JetBrains Mono" fontSize="10" fill="var(--dcs-text-mute)" opacity=".6">0.0</text>
        <text x={W - 26} y={H - 6} fontFamily="JetBrains Mono" fontSize="10" fill="var(--dcs-text-mute)" opacity=".6">1.0</text>
      </svg>
    </div>
  );
}

function SectionCurve() {
  return (
    <section className="dw-section" id="curve">
      <div className="dw-section__eyebrow">Editors · 02</div>
      <h2>Curve editor</h2>
      <p className="dw-section__lead">
        Drag control points to shape envelopes, color ramps, falloffs, or animation eases. The
        curve takes its channel/key color and fills against it at low opacity, so overlaid channels
        stay legible — exactly like an animation graph editor (X red, Y green, Z blue).
      </p>
      <Demo frame="app" caption="Drag any point or its tangent handles to reshape the curve">
        <Panel title="Envelope · Filter cutoff" icon="envelope" pad="sm"
               tools={<><ButtonGroup value="bez" options={[
                  { value: 'lin', label: 'Linear' },
                  { value: 'bez', label: 'Bezier' },
                  { value: 'step', label: 'Step' },
               ]} /></>}>
          <CurveEditor color="#4d9fff" />
        </Panel>
      </Demo>

      <Demo frame="app" caption="Each curve carries its channel's key color">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Panel title="Location · X" icon="curve" pad="sm">
            <CurveEditor height={160} color="#ef6b6b" points={[
              { x: 0, y: 0 }, { x: 0.18, y: 0.08 }, { x: 0.5, y: 0.45 }, { x: 0.8, y: 0.85 }, { x: 1, y: 0.97 }
            ]} />
          </Panel>
          <Panel title="Location · Y" icon="curve" pad="sm">
            <CurveEditor height={160} color="#4ed18a" points={[
              { x: 0, y: 0.5 }, { x: 0.2, y: 0.9 }, { x: 0.4, y: 0.15 }, { x: 0.6, y: 0.65 }, { x: 0.8, y: 0.25 }, { x: 1, y: 0.5 }
            ]} />
          </Panel>
        </div>
      </Demo>
    </section>
  );
}

/* ─────────── Graph editor (node graph) ─────────── */
const GRAPH_NODE_W = 132, GRAPH_HEAD = 24, GRAPH_ROW = 22, GRAPH_BODY_PAD = 6;
const GRAPH_ZMIN = 0.4, GRAPH_ZMAX = 2.4;
const GRAPH_PALETTE = ['#4d9fff', '#b48cff', '#f2b14a', '#4ed18a', '#ff7ab8', '#4ad5d5'];
let graphSeq = 100;
const nodeHeight = (n) => GRAPH_HEAD + GRAPH_BODY_PAD * 2 + (n.ins.length + n.outs.length) * GRAPH_ROW;

function NodeGraph() {
  const [nodes, setNodes] = useStateE([
    { id: 'noise',  x: 16,  y: 30,  title: 'Noise',   icon: 'wave-noise', color: '#b48cff', ins: [],                 outs: ['fac'] },
    { id: 'tex',    x: 16,  y: 176, title: 'Texture', icon: 'image',      color: '#f2b14a', ins: ['uv'],             outs: ['rgb'] },
    { id: 'mix',    x: 250, y: 84,  title: 'Mix',     icon: 'array',      color: '#4d9fff', ins: ['a', 'b', 'fac'],  outs: ['rgb'] },
    { id: 'gamma',  x: 470, y: 70,  title: 'Gamma',   icon: 'bolt',       color: '#4ed18a', ins: ['in'],             outs: ['out'] },
    { id: 'output', x: 660, y: 100, title: 'Output',  icon: 'render',     color: '#4d9fff', ins: ['color'],          outs: [] },
  ]);
  const [wires, setWires] = useStateE([
    { id: 'w1', fn: 'noise', fs: 'fac', tn: 'mix',    ts: 'a' },
    { id: 'w2', fn: 'tex',   fs: 'rgb', tn: 'mix',    ts: 'b' },
    { id: 'w3', fn: 'mix',   fs: 'rgb', tn: 'gamma',  ts: 'in' },
    { id: 'w4', fn: 'gamma', fs: 'out', tn: 'output', ts: 'color' },
  ]);
  const [sel, setSel] = useStateE(null);          // { type:'node'|'wire', id }
  const [view, setView] = useStateE({ z: 1, ox: 0, oy: 0 });
  const [drag, setDrag] = useStateE(null);        // live connect: { fn, fs, x, y } (x,y in world)
  const wrapRef = useRefE(null);

  const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
  // World pixel space — the viewport <div> applies the pan/zoom transform, so
  // node coords and socket centers share one space and wires always meet sockets.
  const socketY = (n, idx) => n.y + GRAPH_HEAD + GRAPH_BODY_PAD + idx * GRAPH_ROW + GRAPH_ROW / 2;
  const outPos = (n, name) => [n.x + GRAPH_NODE_W, socketY(n, n.ins.length + n.outs.indexOf(name))];
  const inPos  = (n, name) => [n.x, socketY(n, n.ins.indexOf(name))];
  const wirePath = (fx, fy, tx, ty) => { const mx = (fx + tx) / 2; return `M ${fx} ${fy} C ${mx} ${fy}, ${mx} ${ty}, ${tx} ${ty}`; };
  const toWorld = (cx, cy) => {
    const r = wrapRef.current.getBoundingClientRect();
    return [(cx - r.left - view.ox) / view.z, (cy - r.top - view.oy) / view.z];
  };

  // ── Zoom (buttons + wheel) ───────────────────────────────────────────
  const zoomAt = (factor, cx, cy) => setView(v => {
    const z = Math.max(GRAPH_ZMIN, Math.min(GRAPH_ZMAX, v.z * factor));
    const k = z / v.z;
    return { z, ox: cx - (cx - v.ox) * k, oy: cy - (cy - v.oy) * k };
  });
  const zoomBtn = (factor) => () => {
    const r = wrapRef.current.getBoundingClientRect();
    zoomAt(factor, r.width / 2, r.height / 2);
  };
  const fit = () => {
    if (!nodes.length || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
    for (const n of nodes) { a = Math.min(a, n.x); b = Math.min(b, n.y); c = Math.max(c, n.x + GRAPH_NODE_W); d = Math.max(d, n.y + nodeHeight(n)); }
    const pad = 28, cw = r.width - pad * 2, ch = r.height - pad * 2;
    const z = Math.max(GRAPH_ZMIN, Math.min(GRAPH_ZMAX, Math.min(cw / (c - a), ch / (d - b), 1.2)));
    setView({ z, ox: pad + (cw - (c - a) * z) / 2 - a * z, oy: pad + (ch - (d - b) * z) / 2 - b * z });
  };
  useEffectE(() => {
    const el = wrapRef.current; if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Background: pan on drag, deselect on click ──────────────────────
  const onBgDown = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, ox0 = view.ox, oy0 = view.oy;
    let moved = false;
    const move = (ev) => {
      if (Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) > 3) moved = true;
      setView(v => ({ ...v, ox: ox0 + (ev.clientX - sx), oy: oy0 + (ev.clientY - sy) }));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (!moved) setSel(null); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // ── Move a node by its header ───────────────────────────────────────
  const dragNode = (id) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation(); e.preventDefault();
    setSel({ type: 'node', id });
    const n0 = byId[id], ox = e.clientX, oy = e.clientY, sx = n0.x, sy = n0.y, z = view.z;
    const move = (ev) => setNodes(ns => ns.map(n => n.id === id
      ? { ...n, x: sx + (ev.clientX - ox) / z, y: Math.max(0, sy + (ev.clientY - oy) / z) } : n));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // ── Connect / disconnect wires by dragging sockets ──────────────────
  const findInputAt = (cx, cy) => {
    const r = wrapRef.current.getBoundingClientRect();
    let best = null, bestD = 18;
    for (const n of nodes) for (const s of n.ins) {
      const [wx, wy] = inPos(n, s);
      const d = Math.hypot(cx - (r.left + view.ox + wx * view.z), cy - (r.top + view.oy + wy * view.z));
      if (d < bestD) { bestD = d; best = { nid: n.id, sock: s }; }
    }
    return best;
  };
  const addWire = (fn, fs, tn, ts) => {
    if (fn === tn) return;
    setWires(ws => [...ws.filter(w => !(w.tn === tn && w.ts === ts)), { id: 'w' + (graphSeq++), fn, fs, tn, ts }]);
  };
  const beginDrag = (fn, fs, e) => {
    const move = (ev) => { const [x, y] = toWorld(ev.clientX, ev.clientY); setDrag({ fn, fs, x, y }); };
    move(e);
    const up = (ev) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const hit = findInputAt(ev.clientX, ev.clientY);
      setDrag(null);
      if (hit) addWire(fn, fs, hit.nid, hit.sock);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const startConnect = (fn, fs) => (e) => { e.stopPropagation(); e.preventDefault(); beginDrag(fn, fs, e); };
  const startFromInput = (tn, ts) => (e) => {
    e.stopPropagation(); e.preventDefault();
    const w = wires.find(x => x.tn === tn && x.ts === ts);
    if (!w) return;                       // empty input — nothing to grab
    setWires(ws => ws.filter(x => x.id !== w.id));
    beginDrag(w.fn, w.fs, e);             // re-drag from the original source
  };

  // ── Add / delete ────────────────────────────────────────────────────
  const addNode = () => {
    const id = 'n' + (graphSeq++);
    const r = wrapRef.current.getBoundingClientRect();
    const x = Math.max(0, (r.width / 2 - view.ox) / view.z - GRAPH_NODE_W / 2);
    const y = Math.max(0, (r.height / 2 - view.oy) / view.z - 30);
    const color = GRAPH_PALETTE[nodes.length % GRAPH_PALETTE.length];
    setNodes(ns => [...ns, { id, x, y, title: 'Node', icon: 'bolt', color, ins: ['in'], outs: ['out'] }]);
    setSel({ type: 'node', id });
  };
  const deleteSel = () => {
    if (!sel) return;
    if (sel.type === 'node') {
      setNodes(ns => ns.filter(n => n.id !== sel.id));
      setWires(ws => ws.filter(w => w.fn !== sel.id && w.tn !== sel.id));
    } else {
      setWires(ws => ws.filter(w => w.id !== sel.id));
    }
    setSel(null);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSel(); }
    else if (e.key === 'Escape') setSel(null);
  };

  const Socket = ({ side, filled, onPointerDown }) => (
    <div onPointerDown={onPointerDown}
      title={side === 'out' ? 'drag to connect' : 'drag to rewire / detach'}
      style={{
        width: 10, height: 10, borderRadius: '50%',
        background: filled ? 'var(--dcs-accent)' : 'var(--dcs-bg)',
        border: '2px solid var(--dcs-bg)',
        boxShadow: `0 0 0 1px ${filled ? 'var(--dcs-accent)' : 'var(--dcs-line-strong)'}`,
        [side === 'in' ? 'marginLeft' : 'marginRight']: -14, flex: '0 0 auto', cursor: 'crosshair',
      }} />
  );

  const selWire = sel && sel.type === 'wire' ? sel.id : null;

  return (
    <div ref={wrapRef} className="dcs-graph" tabIndex={0}
      onPointerDown={onBgDown} onKeyDown={onKeyDown}
      onPointerDownCapture={() => wrapRef.current && wrapRef.current.focus()}
      style={{ height: 320, position: 'relative', overflow: 'hidden', outline: 'none' }}>
      <div className="dcs-graph__major" />

      {/* Pan/zoom viewport — pointer-events pass through empty space to the bg. */}
      <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', pointerEvents: 'none',
        transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.z})` }}>
        <svg width="3000" height="2000" style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
          {wires.map(w => {
            const a = byId[w.fn], b = byId[w.tn];
            if (!a || !b) return null;
            const [fx, fy] = outPos(a, w.fs);
            const [tx, ty] = inPos(b, w.ts);
            const d = wirePath(fx, fy, tx, ty);
            const on = w.id === selWire;
            return (
              <g key={w.id}>
                <path d={d} fill="none" stroke="transparent" strokeWidth="12"
                  style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                  onPointerDown={(e) => { e.stopPropagation(); setSel({ type: 'wire', id: w.id }); }} />
                <path d={d} fill="none" stroke={on ? '#fff' : a.color} strokeWidth={on ? 3 : 2}
                  strokeOpacity={on ? 1 : 0.95} style={{ pointerEvents: 'none' }} />
              </g>
            );
          })}
          {drag && byId[drag.fn] && (() => {
            const [fx, fy] = outPos(byId[drag.fn], drag.fs);
            return <path d={wirePath(fx, fy, drag.x, drag.y)} fill="none" stroke="var(--dcs-accent)"
              strokeWidth="2" strokeDasharray="4 3" strokeOpacity=".8" style={{ pointerEvents: 'none' }} />;
          })()}
        </svg>

        {nodes.map(n => {
          const conn = new Set(wires.filter(w => w.tn === n.id).map(w => w.ts));
          const on = sel && sel.type === 'node' && sel.id === n.id;
          return (
            <div key={n.id}
              onPointerDown={(e) => { e.stopPropagation(); setSel({ type: 'node', id: n.id }); }}
              style={{
                position: 'absolute', left: n.x, top: n.y, width: GRAPH_NODE_W,
                background: 'var(--dcs-bg)',
                border: `1px solid ${on ? 'var(--dcs-accent)' : 'var(--dcs-line)'}`,
                borderRadius: 'var(--dcs-r-2)',
                boxShadow: on ? '0 0 0 1px var(--dcs-accent), var(--dcs-shadow-2)' : 'var(--dcs-shadow-2)',
                fontSize: 11, userSelect: 'none', pointerEvents: 'auto',
              }}>
              <div onPointerDown={dragNode(n.id)} style={{
                height: GRAPH_HEAD, background: n.color, color: '#0a1220',
                display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px',
                fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', fontSize: 10,
                cursor: 'grab', borderRadius: 'var(--dcs-r-2) var(--dcs-r-2) 0 0',
              }}>
                <Icon name={n.icon} size="sm" /><span>{n.title}</span>
              </div>
              <div style={{ padding: `${GRAPH_BODY_PAD}px 0` }}>
                {n.ins.map(s => (
                  <div key={s} style={{ height: GRAPH_ROW, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', color: 'var(--dcs-text-dim)' }}>
                    <Socket side="in" filled={conn.has(s)} onPointerDown={startFromInput(n.id, s)} /><span>{s}</span>
                  </div>
                ))}
                {n.outs.map(s => (
                  <div key={s} style={{ height: GRAPH_ROW, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, padding: '0 8px', color: 'var(--dcs-text-dim)' }}>
                    <span>{s}</span><Socket side="out" filled onPointerDown={startConnect(n.id, s)} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating toolbar — zoom, fit, add, delete (overlay, doesn't pan). */}
      <Toolbar floating size="sm" style={{ position: 'absolute', top: 8, right: 8, zIndex: 5 }}>
        <Button ghost sm icon iconLeft="zoom-out" title="Zoom out" onClick={zoomBtn(1 / 1.2)} />
        <span style={{ fontSize: 10, fontFamily: 'var(--dcs-font-mono)', color: 'var(--dcs-text-mute)', minWidth: 32, textAlign: 'center' }}>{Math.round(view.z * 100)}%</span>
        <Button ghost sm icon iconLeft="zoom-in" title="Zoom in" onClick={zoomBtn(1.2)} />
        <Button ghost sm icon iconLeft="fit" title="Fit to view" onClick={fit} />
        <ToolbarSep />
        <Button ghost sm iconLeft="plus" title="Add node" onClick={addNode}>Node</Button>
        <Button ghost sm icon iconLeft="trash" title="Delete selected" onClick={deleteSel} disabled={!sel} />
      </Toolbar>

      <div style={{ position: 'absolute', bottom: 6, left: 8, fontSize: 10, fontFamily: 'var(--dcs-font-mono)', color: 'var(--dcs-text-mute)', pointerEvents: 'none' }}>
        click to select · drag header to move · drag sockets to wire · wheel to zoom · ⌫ deletes
      </div>
    </div>
  );
}

function SectionGraph() {
  return (
    <section className="dw-section" id="graph">
      <div className="dw-section__eyebrow">Editors · 03</div>
      <h2>Node graph</h2>
      <p className="dw-section__lead">
        For shader networks, compositors, animation rigs, audio routing. Colored title bars sort by
        kind without an icon legend; accent-color wires keep signal flow legible against the grid.
        Select nodes and edges, drag sockets to connect or detach, add and delete, and zoom or pan
        the canvas — all from the small JS model, no graph library.
      </p>
      <Demo frame="app">
        <Panel title="Shader network · jane_skin.mat" icon="graph" pad={0}>
          <NodeGraph />
        </Panel>
      </Demo>
    </section>
  );
}

/* ─────────── Texture / image picker ─────────── */
function SectionTexture() {
  const [picked, setPicked] = useStateE(2);
  // Generate procedural tile thumbnails so we don't need image assets
  const tiles = [
    { name: 'Concrete_01', kind: 'PBR', res: '4K', gradient: 'linear-gradient(135deg, #7c8492, #4a5161)' },
    { name: 'WoodPlank_F',  kind: 'PBR', res: '2K', gradient: 'repeating-linear-gradient(90deg, #6e4a2a, #6e4a2a 18px, #4e3520 18px, #4e3520 24px)' },
    { name: 'Sky_Studio',   kind: 'HDRI', res: '8K', gradient: 'linear-gradient(180deg, #ffb84d, #f2b14a 40%, #4d9fff 90%)' },
    { name: 'Metal_Brushed',kind: 'PBR', res: '4K', gradient: 'repeating-linear-gradient(135deg, #cfd4dc, #cfd4dc 1px, #aab0bd 1px, #aab0bd 2px)' },
    { name: 'Noise_Perlin', kind: 'PROC', res: '∞',  gradient: 'radial-gradient(circle at 30% 40%, #555d6e, #20232b)' },
    { name: 'Grass_Hi',     kind: 'PBR', res: '2K', gradient: 'linear-gradient(180deg, #4ed18a, #2bb872 60%, #1a6e44)' },
    { name: 'RustyMetal_3', kind: 'PBR', res: '4K', gradient: 'radial-gradient(circle at 60% 40%, #f2b14a, #8a4a1a)' },
    { name: 'Water_Sub',    kind: 'PBR', res: '2K', gradient: 'linear-gradient(180deg, #4ad5d5, #1c5fb0)' },
    { name: 'Marble_Stat',  kind: 'PBR', res: '4K', gradient: 'linear-gradient(45deg, #e7e9ee, #cfd4dc 50%, #aab0bd)' },
    { name: 'Voronoi_Cell', kind: 'PROC', res: '∞', gradient: 'repeating-radial-gradient(circle at 30% 40%, #b48cff, #b48cff 8px, #6f4eea 8px, #6f4eea 16px)' },
    { name: 'Brick_OldRed', kind: 'PBR', res: '2K', gradient: 'repeating-linear-gradient(0deg, #8a3a3a, #8a3a3a 12px, #602727 12px, #602727 18px)' },
    { name: 'Asphalt_Wet',  kind: 'PBR', res: '4K', gradient: 'radial-gradient(ellipse at 50% 60%, #20232b, #14161c)' },
  ];
  return (
    <section className="dw-section" id="textures">
      <div className="dw-section__eyebrow">Editors · 04</div>
      <h2>Texture &amp; image pickers</h2>
      <p className="dw-section__lead">
        A grid browser with kind/resolution badges, a sidebar of categories, and an inspector for
        the active asset. Tiles use a square aspect with a 1px line keyline.
      </p>
      <Demo frame="app" inset minw={640}>
        <Panel title="Asset browser · /work/intro/textures" icon="texture" pad={0}
               tools={<><Button ghost sm icon iconLeft="search" /><Button ghost sm icon iconLeft="grid" pressed /><Button ghost sm icon iconLeft="menu" /></>}>
          <div style={{ display: 'flex', minHeight: 360 }}>
            <div style={{ width: 160, borderRight: '1px solid var(--dcs-line)', background: 'var(--dcs-surface-1)' }}>
              <div className="dcs-tree" style={{ padding: '6px 0' }}>
                {[
                  { id: 'all', name: 'All assets', icon: 'folder-open', count: 248 },
                  { id: 'pbr', name: 'PBR Surfaces', icon: 'texture', count: 184 },
                  { id: 'hdri', name: 'HDRI', icon: 'globe', count: 12 },
                  { id: 'proc', name: 'Procedural', icon: 'wave-noise', count: 24 },
                  { id: 'fav', name: 'Favorites', icon: 'star', count: 18 },
                  { id: 'recent', name: 'Recent', icon: 'timeline', count: 10 },
                ].map((c, i) => (
                  <div key={c.id} className="dcs-tree__row" aria-selected={i === 0} style={{ '--depth': 0, paddingLeft: 14 }}>
                    <span style={{ width: 14 }} />
                    <Icon name={c.icon} className="dcs-tree__icon" />
                    <span className="dcs-tree__label">{c.name}</span>
                    <span className="dcs-tree__meta">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8, alignContent: 'start' }}>
              {tiles.map((t, i) => (
                <div key={t.name} onClick={() => setPicked(i)} style={{
                  cursor: 'pointer', display: 'flex', flexDirection: 'column',
                  border: i === picked ? '1px solid var(--dcs-accent)' : '1px solid transparent',
                  background: i === picked ? 'var(--dcs-accent-dim)' : 'transparent',
                  padding: 4, borderRadius: 4,
                }}>
                  <div style={{
                    width: '100%', aspectRatio: '1',
                    background: t.gradient,
                    border: '1px solid var(--dcs-line)',
                    borderRadius: 2,
                    position: 'relative',
                  }}>
                    <span className="dcs-badge" style={{
                      position: 'absolute', top: 4, left: 4,
                      fontSize: 9, height: 14, padding: '0 4px',
                      background: 'rgba(0,0,0,.5)', borderColor: 'transparent',
                      color: t.kind === 'HDRI' ? 'var(--dcs-warn)' : t.kind === 'PROC' ? 'var(--dcs-purple)' : 'var(--dcs-accent)',
                    }}>{t.kind}</span>
                    <span className="dcs-badge" style={{
                      position: 'absolute', top: 4, right: 4,
                      fontSize: 9, height: 14, padding: '0 4px',
                      background: 'rgba(0,0,0,.5)', borderColor: 'transparent',
                      color: 'var(--dcs-text-dim)',
                    }}>{t.res}</span>
                  </div>
                  <div style={{
                    fontSize: 10, color: i === picked ? 'var(--dcs-text)' : 'var(--dcs-text-dim)',
                    marginTop: 4, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: 'var(--dcs-font-mono)',
                  }}>{t.name}</div>
                </div>
              ))}
            </div>
            <div style={{ width: 220, borderLeft: '1px solid var(--dcs-line)', background: 'var(--dcs-surface-1)', padding: 12 }}>
              <div style={{
                width: '100%', aspectRatio: '1',
                background: tiles[picked].gradient,
                border: '1px solid var(--dcs-line)', borderRadius: 4, marginBottom: 10,
              }} />
              <div style={{ fontSize: 13, color: 'var(--dcs-text)', marginBottom: 4 }}>{tiles[picked].name}</div>
              <div className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-text-mute)', marginBottom: 12 }}>
                <div>{tiles[picked].kind} · {tiles[picked].res} · linear sRGB</div>
                <div>8.4 MB · 4096 × 4096</div>
                <div>last used 2 days ago</div>
              </div>
              <div className="dcs-col" style={{ gap: 6 }}>
                <Button sm primary iconLeft="check" style={{ width: '100%' }}>Use texture</Button>
                <Button sm ghost iconLeft="export" style={{ width: '100%' }}>Reveal in OS</Button>
                <Button sm ghost iconLeft="trash" style={{ width: '100%' }}>Remove</Button>
              </div>
            </div>
          </div>
        </Panel>
      </Demo>
    </section>
  );
}

Object.assign(window, { SectionColorPicker, SectionCurve, SectionGraph, SectionTexture, CurveEditor, NodeGraph, ColorPicker, ColorField });


/* ===== sections-apps.jsx ===== */
/* sections-apps.jsx
   The two showpieces: Sample DCC tool · Sample synthesizer
*/
const { useState: useStateA, useEffect: useEffectA, useRef: useRefA } = React;

/* ─────────────────────────────────────────────────────────────
   Live sample apps — actual full-page applications built on the
   framework, linked out to /dender/ and /photoedit/. The tiles
   use WebP screenshots from site/public/images/ at thumbnail
   resolution (~600x385 displayed, retina-ready at 1200x770).
   ───────────────────────────────────────────────────────────── */
function SectionLiveApps() {
  const apps = [
    {
      slug: 'dender',
      image: 'dender.webp',
      title: 'Dender',
      tagline: '3D modeler',
      desc: 'A Blender-style modeling environment — dockable inspector and outliner, floating N-panel and tool rail, multi-tab document/viewport, full per-tab toolbars, timeline. Built entirely from decius components.',
      icon: 'cube',
    },
    {
      slug: 'photoedit',
      image: 'photoedit.webp',
      title: 'Photo Edit',
      tagline: 'Image editor',
      desc: 'A Photoshop-style image editor — every panel floats and tears off, document tabs across the top, real color picker popovers from the FG/BG chips, layers / channels / paths / history dockpane.',
      icon: 'image',
    },
  ];
  return (
    <section className="dw-section" id="sample-apps" style={{ marginTop: 24 }}>
      <div className="dw-section__eyebrow">Live sample apps</div>
      <h2>The whole framework, driving real apps.</h2>
      <p className="dw-section__lead">
        Two full applications built only with decius components. Same docking, tear-off, edge-snap,
        popover and color-picker primitives you find in the docs — composed into something you can
        actually click around in.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 24,
        marginTop: 28,
      }}>
        {apps.map(a => (
          <a key={a.slug}
             href={`${import.meta.env.BASE_URL}${a.slug}/index.html`}
             target="_blank" rel="noreferrer noopener"
             style={{
               display: 'block',
               textDecoration: 'none',
               color: 'inherit',
               background: 'var(--dw-surface)',
               border: '1px solid var(--dw-line)',
               borderRadius: 10,
               overflow: 'hidden',
               transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
             }}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-2px)';
               e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,.35)';
               e.currentTarget.style.borderColor = 'var(--dw-accent)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = '';
               e.currentTarget.style.boxShadow = '';
               e.currentTarget.style.borderColor = 'var(--dw-line)';
             }}>
            <div style={{ aspectRatio: '1200 / 770', background: 'var(--dcs-bg-app)', overflow: 'hidden' }}>
              <img src={`${import.meta.env.BASE_URL}images/${a.image}`}
                   alt={`${a.title} — ${a.tagline}`}
                   loading="lazy"
                   style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '14px 18px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Icon name={a.icon} size="sm" style={{ color: 'var(--dw-accent)' }} />
                <span style={{ fontWeight: 600, fontSize: 16 }}>{a.title}</span>
                <span style={{ color: 'var(--dw-text-mute)', fontSize: 13 }}>· {a.tagline}</span>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--dw-accent)', fontSize: 13, fontFamily: 'var(--dw-font-mono)' }}>
                  Open <Icon name="chevron-right" size="sm" />
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--dw-text-mute)' }}>
                {a.desc}
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
Object.assign(window, { SectionLiveApps });

/* ─────────────────────────────────────────────────────────────
   Sample DCC tool — "decius modeler"
   A scaled-down composite of every DCC pattern in the framework.
   ───────────────────────────────────────────────────────────── */
function SampleDCC() {
  const [tool, setTool] = useStateA('select');
  const [shading, setShading] = useStateA('shaded');
  const [snap, setSnap] = useStateA(true);
  const [pos, setPos] = useStateA({ x: 0.000, y: 1.250, z: -0.300 });
  const [rot, setRot] = useStateA({ x: 0, y: 22, z: 0 });
  const [scale, setScale] = useStateA(0.4);
  const [rough, setRough] = useStateA(0.42);
  const [metallic, setMetallic] = useStateA(0.08);
  const [wire, setWire] = useStateA(false);
  const [smooth, setSmooth] = useStateA(true);
  const [shadow, setShadow] = useStateA(true);
  const [inView, setInView] = useStateA(true);
  const [frame, setFrame] = useStateA(48);
  const [playing, setPlaying] = useStateA(false);
  const [expanded, setExpanded] = useStateA(new Set(['scene', 'env', 'chars', 'jane']));
  const [sel, setSel] = useStateA('cube');
  const [last, setLast] = useStateA('—');

  useEffectA(() => {
    if (!playing) return;
    const id = setInterval(() => setFrame(f => (f >= 240 ? 1 : f + 1)), 1000 / 24);
    return () => clearInterval(id);
  }, [playing]);
  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const DCC_MENUS = {
    File: [{ label: 'New Scene', icon: 'file', shortcut: '⌘N' }, { label: 'Open…', icon: 'folder-open', shortcut: '⌘O' }, { sep: true }, { label: 'Save', icon: 'save', shortcut: '⌘S' }, { label: 'Import', icon: 'import', sub: [{ label: 'OBJ' }, { label: 'FBX' }, { label: 'USD' }] }, { label: 'Export', icon: 'export', sub: [{ label: 'glTF' }, { label: 'Alembic' }] }, { sep: true }, { label: 'Quit', icon: 'close', danger: true }],
    Edit: [{ label: 'Undo', icon: 'undo', shortcut: '⌘Z' }, { label: 'Redo', icon: 'redo', shortcut: '⇧⌘Z' }, { sep: true }, { label: 'Cut', icon: 'cut' }, { label: 'Copy', icon: 'copy' }, { label: 'Paste', icon: 'paste' }, { label: 'Duplicate', icon: 'duplicate', shortcut: '⌘D' }],
    View: [{ label: 'Wireframe', check: wire }, { label: 'Smooth shading', check: smooth }, { label: 'Show grid', check: true }, { sep: true }, { label: 'Frame selected', icon: 'fit', shortcut: 'F' }, { label: 'Fullscreen', icon: 'fullscreen' }],
    Mesh: [{ label: 'Extrude', icon: 'extrude' }, { label: 'Subdivide', icon: 'subdivide' }, { label: 'Mirror', icon: 'mirror' }, { label: 'Array', icon: 'array' }, { label: 'Decimate', icon: 'decimate' }],
    Animation: [{ label: 'Insert keyframe', icon: 'key', shortcut: 'I' }, { label: 'Play', icon: 'play', shortcut: 'Space' }, { label: 'Bake action', icon: 'bolt' }],
    Render: [{ label: 'Render image', icon: 'render', shortcut: 'F12' }, { label: 'Render animation', icon: 'play' }, { sep: true }, { label: 'Render settings', icon: 'cog' }],
    Window: [{ label: 'Reset layout', icon: 'array' }, { label: 'Save layout', icon: 'save' }],
    Help: [{ label: 'Documentation', icon: 'help' }, { label: 'About modeler', icon: 'info' }],
  };
  const TAB_META = {
    hier: { label: 'Hierarchy', icon: 'layers' }, proj: { label: 'Project', icon: 'folder' },
    scene: { label: 'Scene', icon: 'cube' }, game: { label: 'Game', icon: 'play' }, uv: { label: 'UV Editor', icon: 'uv' },
    timeline: { label: 'Timeline', icon: 'timeline' }, console: { label: 'Console', icon: 'cpu' },
    inspector: { label: 'Inspector', icon: 'cog' }, lighting: { label: 'Lighting', icon: 'light' },
  };

  const Outliner = () => (
    <Tree expanded={expanded} onExpand={toggle} selected={sel} onSelect={setSel} nodes={[{
      id: 'scene', label: 'Scene_Intro_v014', icon: 'globe', meta: '37', children: [
        { id: 'env', label: 'Environment', icon: 'folder-open', children: [
          { id: 'hdri', label: 'Sky_4k.hdr', icon: 'image' }, { id: 'sun', label: 'Sun.001', icon: 'light' }, { id: 'grnd', label: 'Ground', icon: 'plane' }] },
        { id: 'chars', label: 'Characters', icon: 'folder-open', children: [
          { id: 'jane', label: 'Jane', icon: 'folder-open', children: [
            { id: 'cube', label: 'jane_body', icon: 'mesh', meta: '64k' }, { id: 'rig', label: 'jane_rig', icon: 'bone' }, { id: 'mat', label: 'jane_skin', icon: 'palette' }] },
          { id: 'eric', label: 'Eric', icon: 'folder' }] },
        { id: 'cams', label: 'Cameras', icon: 'folder', meta: '3' }, { id: 'fx', label: 'FX', icon: 'folder', meta: '7' },
      ]
    }]} />
  );
  const Project = () => (
    <div className="dcs-tree" style={{ padding: '4px 0' }}>
      {[['Assets', 'folder-open'], ['Materials', 'palette'], ['Textures', 'texture'], ['Models', 'mesh'], ['Sounds', 'volume'], ['Scripts', 'cpu']].map(([n, ic], i) => (
        <div key={n} className="dcs-tree__row" aria-selected={i === 1}><span style={{ width: 14 }} /><Icon name={ic} className="dcs-tree__icon" /><span className="dcs-tree__label">{n}</span></div>
      ))}
    </div>
  );
  const Viewport = () => (
    <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 38%, #3a4054, #161922 80%)', overflow: 'hidden' }}>
      <svg viewBox="-200 -120 400 240" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs><pattern id="dcc2-grid" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="scale(1.5, 0.5)"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth=".3" /></pattern></defs>
        <rect x="-200" y="0" width="400" height="120" fill="url(#dcc2-grid)" />
        <line x1="-200" y1="0" x2="200" y2="0" stroke="rgba(255,255,255,.15)" strokeWidth=".5" />
        <g transform={`translate(${pos.x * 12} ${-(pos.y * 12)}) rotate(${rot.y + (frame / 240) * 120})`} style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.5))' }}>
          {wire ? (
            <path d="M-22-22 L22-22 L28-14 L28 18 L-16 22 L-22 12 Z M-22-22 L0 0 L22-22 M-22 12 L0 0 L28 18" fill="none" stroke="#4d9fff" strokeWidth=".4" />
          ) : (<>
            <path d="M-22-22 L22-22 L28-14 L28 18 L-16 22 L-22 12 Z" fill="rgba(77,159,255,.22)" stroke="#4d9fff" strokeWidth=".4" />
            <path d="M-22-22 L28-14 L28 18 L-22 12 Z" fill="rgba(77,159,255,.10)" stroke="rgba(77,159,255,.35)" strokeWidth=".3" />
          </>)}
          <line x1="0" y1="0" x2="30" y2="0" stroke="#ef6b6b" strokeWidth=".5" />
          <line x1="0" y1="0" x2="0" y2="-26" stroke="#4ed18a" strokeWidth=".5" />
          <circle cx="0" cy="0" r="1.5" fill="#fff" />
        </g>
      </svg>
      <div className="dcs-viewport__overlay dcs-viewport__overlay--tl">
        <div className="dcs-viewport__floater">
          <Button sm icon iconLeft="select" pressed={tool === 'select'} onClick={() => setTool('select')} />
          <Button sm icon iconLeft="move" pressed={tool === 'move'} onClick={() => setTool('move')} />
          <Button sm icon iconLeft="rotate" pressed={tool === 'rotate'} onClick={() => setTool('rotate')} />
          <Button sm icon iconLeft="scale-corners" pressed={tool === 'scale'} onClick={() => setTool('scale')} />
        </div>
      </div>
      <div className="dcs-viewport__overlay dcs-viewport__overlay--tr">
        <span className="dcs-badge dcs-badge--accent">PERSPECTIVE</span>
        <span className="dcs-badge">F {String(frame).padStart(3, '0')}</span>
        <span className="dcs-badge dcs-badge--ok dcs-badge--dot">SOLVED</span>
      </div>
      <div className="dcs-viewport__overlay dcs-viewport__overlay--br">
        <div style={{ fontFamily: 'var(--dcs-font-mono)', fontSize: 10, color: 'rgba(255,255,255,.6)', textAlign: 'right', lineHeight: 1.5 }}>
          <div>jane_body · 64,201 tri</div><div style={{ color: 'var(--dcs-accent)' }}>60 fps · 4.1 ms</div>
        </div>
      </div>
    </div>
  );
  const Timeline = () => (
    <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 6, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Button ghost sm icon iconLeft="skip-back" onClick={() => setFrame(1)} />
        <Button ghost sm icon iconLeft="rewind" onClick={() => setFrame(f => Math.max(1, f - 24))} />
        <Button ghost sm icon iconLeft={playing ? 'pause' : 'play'} pressed={playing} onClick={() => setPlaying(p => !p)} />
        <Button ghost sm icon iconLeft="fast-forward" onClick={() => setFrame(f => Math.min(240, f + 24))} />
        <Button ghost sm icon iconLeft="skip-fwd" onClick={() => setFrame(240)} />
        <Combo value={frame} onChange={setFrame} min={1} max={240} step={1} format={v => `F ${String(v).padStart(3, '0')}`} width={104} />
        <span style={{ flex: 1 }} />
        <Button ghost sm icon iconLeft="record" /><Button ghost sm icon iconLeft="key" />
      </div>
      <div style={{ flex: 1, minHeight: 40, position: 'relative', background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3, overflow: 'hidden' }}>
        {Array.from({ length: 25 }).map((_, i) => (<div key={i} style={{ position: 'absolute', left: `${(i / 24) * 100}%`, top: 0, bottom: 0, width: 1, background: i % 4 === 0 ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.04)' }} />))}
        {[12, 24, 48, 64, 96, 128, 160, 192, 224].map(f => (<div key={f} style={{ position: 'absolute', left: `${(f / 240) * 100}%`, top: '50%', width: 8, height: 8, marginLeft: -4, marginTop: -4, background: 'var(--dcs-accent)', transform: 'rotate(45deg)', boxShadow: '0 0 6px rgba(77,159,255,.5)' }} />))}
        <div style={{ position: 'absolute', left: `${(frame / 240) * 100}%`, top: -4, bottom: -4, width: 2, background: 'var(--dcs-accent-hi)', boxShadow: '0 0 6px rgba(111,179,255,.6)' }} />
        <div style={{ position: 'absolute', left: `${(frame / 240) * 100}%`, top: 0, marginLeft: -8, fontSize: 9, fontFamily: 'var(--dcs-font-mono)', color: 'var(--dcs-accent)', background: 'var(--dcs-bg)', padding: '0 4px' }}>{String(frame).padStart(3, '0')}</div>
      </div>
    </div>
  );
  const ConsoleView = () => (
    <div style={{ padding: 'var(--dcs-s-3) var(--dcs-s-5)', fontFamily: 'var(--dcs-font-mono)', fontSize: 11, lineHeight: 1.7 }}>
      <div style={{ color: 'var(--dcs-text-mute)' }}><span style={{ color: 'var(--dcs-ok)' }}>[ok]</span> Scene cached · 312 MB</div>
      <div style={{ color: 'var(--dcs-text-mute)' }}><span style={{ color: 'var(--dcs-accent)' }}>[info]</span> playhead → frame {frame}</div>
      <div style={{ color: 'var(--dcs-text-mute)' }}><span style={{ color: 'var(--dcs-warn)' }}>[warn]</span> jane_body has 12 n-gons</div>
    </div>
  );
  const Inspector = () => (
    <Foldouts>
      <Foldout title="Transform" icon="move">
        <div className="dcs-props">
          {[['X', '#ef6b6b', pos.x, 'x'], ['Y', '#4ed18a', pos.y, 'y'], ['Z', '#4d9fff', pos.z, 'z']].map(([k, c, v, key]) => (
            <div key={k} className="dcs-field"><span className="dcs-field__label" style={{ flex: '0 0 14px', color: c, fontSize: 11 }}>{k}</span><Combo value={v} onChange={nv => setPos(p => ({ ...p, [key]: nv }))} min={-10} max={10} step={0.001} format={n => n.toFixed(3)} /></div>
          ))}
        </div>
      </Foldout>
      <Foldout title="Rotation" icon="rotate">
        <div className="dcs-props">
          {[['X', '#ef6b6b', rot.x, 'x'], ['Y', '#4ed18a', rot.y, 'y'], ['Z', '#4d9fff', rot.z, 'z']].map(([k, c, v, key]) => (
            <div key={k} className="dcs-field"><span className="dcs-field__label" style={{ flex: '0 0 14px', color: c, fontSize: 11 }}>{k}</span><Combo value={v} onChange={nv => setRot(r => ({ ...r, [key]: nv }))} min={-180} max={180} step={1} format={n => `${n.toFixed(0)}°`} /></div>
          ))}
        </div>
      </Foldout>
      <Foldout title="Material" icon="palette" meta="Lambert.001">
        <div className="dcs-props">
          <div className="dcs-field"><span className="dcs-field__label">Albedo</span><div className="dcs-swatch"><div className="dcs-swatch__chip" style={{ '--c': '#4d9fff' }} /><span>#4D9FFF</span></div></div>
          <div className="dcs-field"><span className="dcs-field__label">Rough</span><Slider value={rough} onChange={setRough} /></div>
          <div className="dcs-field"><span className="dcs-field__label">Metal</span><Slider value={metallic} onChange={setMetallic} /></div>
        </div>
      </Foldout>
      <Foldout title="Display" icon="eye">
        <div className="dcs-props">
          <div className="dcs-field"><span className="dcs-field__label">Wireframe</span><Switch checked={wire} onChange={setWire} /></div>
          <div className="dcs-field"><span className="dcs-field__label">Smooth shade</span><Switch checked={smooth} onChange={setSmooth} /></div>
          <div className="dcs-field"><span className="dcs-field__label">Cast shadow</span><Switch checked={shadow} onChange={setShadow} /></div>
          <div className="dcs-field"><span className="dcs-field__label">In viewport</span><Switch checked={inView} onChange={setInView} /></div>
        </div>
      </Foldout>
      <Foldout title="Subdivision" icon="subdivide" defaultOpen={false} meta={`L${Math.round(scale * 6)}`}>
        <div className="dcs-props"><div className="dcs-field"><span className="dcs-field__label">Level</span><Slider value={scale} onChange={setScale} /></div></div>
      </Foldout>
      <Foldout title="Modifiers" icon="bolt" defaultOpen={false} meta="3" />
    </Foldouts>
  );
  const Lighting = () => (
    <Foldouts>
      <Foldout title="Ambient" icon="globe"><div className="dcs-props"><div className="dcs-field"><span className="dcs-field__label">Color</span><div className="dcs-swatch"><div className="dcs-swatch__chip" style={{ '--c': '#7c8492' }} /><span>#7C8492</span></div></div><div className="dcs-field"><span className="dcs-field__label">Intensity</span><Slider value={0.4} /></div></div></Foldout>
      <Foldout title="Sun" icon="light"><div className="dcs-props"><div className="dcs-field"><span className="dcs-field__label">Exposure</span><Slider value={0.6} /></div><div className="dcs-field"><span className="dcs-field__label">Soft shadows</span><Switch checked /></div></div></Foldout>
    </Foldouts>
  );
  const Placeholder = (label, icon) => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--dcs-text-mute)' }}><Icon name={icon} size="xl" /><span style={{ fontSize: 12 }}>{label}</span></div>
  );
  const renderContent = (id) => {
    switch (id) {
      case 'hier': return Outliner();
      case 'proj': return Project();
      case 'scene': return <div style={{ position: 'relative', height: '100%', minHeight: 220 }}>{Viewport()}</div>;
      case 'game': return Placeholder('Game view', 'play');
      case 'uv': return Placeholder('UV editor', 'uv');
      case 'timeline': return Timeline();
      case 'console': return ConsoleView();
      case 'inspector': return Inspector();
      case 'lighting': return Lighting();
      default: return null;
    }
  };
  const layout = {
    type: 'split', dir: 'row', sizes: [1, 2.8, 1.15], children: [
      { type: 'tabs', tabs: ['hier', 'proj'] },
      { type: 'split', dir: 'col', sizes: [2.7, 1.1], children: [
        { type: 'tabs', tabs: ['scene', 'game', 'uv'] },
        { type: 'tabs', tabs: ['timeline', 'console'] },
      ] },
      { type: 'tabs', tabs: ['inspector', 'lighting'] },
    ],
  };

  return (
    <div className="dcs" data-dcs-density="compact" style={{ background: 'var(--dcs-bg-app)', height: 660, minWidth: 880, display: 'flex', flexDirection: 'column' }}>
      <MenuBar
        brand={{ icon: 'decius', label: 'modeler' }}
        items={['File', 'Edit', 'View', 'Mesh', 'Animation', 'Render', 'Window', 'Help']}
        menus={DCC_MENUS}
        onPick={(m, v) => setLast(`${m} › ${v}`)}
        meta={<><span>Scene_Intro_v014.dcs</span><span className="dcs-badge dcs-badge--accent">MODIFIED</span></>}
      />
      <Toolbar>
        <ButtonGroup value={tool} onChange={setTool} options={[{ value: 'select', icon: 'select' }, { value: 'move', icon: 'move' }, { value: 'rotate', icon: 'rotate' }, { value: 'scale', icon: 'scale-corners' }]} />
        <ToolbarSep />
        <Button ghost sm icon iconLeft="cube" /><Button ghost sm icon iconLeft="sphere" /><Button ghost sm icon iconLeft="cylinder" /><Button ghost sm icon iconLeft="light" /><Button ghost sm icon iconLeft="camera" />
        <ToolbarSep />
        <Button ghost sm icon iconLeft="extrude" /><Button ghost sm icon iconLeft="subdivide" /><Button ghost sm icon iconLeft="mirror" />
        <ToolbarSep />
        <Button ghost sm icon iconLeft="snap-grid" pressed={snap} onClick={() => setSnap(!snap)} /><Button ghost sm icon iconLeft="magnet" />
        <ToolbarSep />
        <ButtonGroup value={shading} onChange={(v) => { setShading(v); setWire(v === 'wire'); }} options={[{ value: 'wire', icon: 'view-wire' }, { value: 'shaded', icon: 'view-solid' }, { value: 'tex', icon: 'view-tex' }, { value: 'render', icon: 'view-render' }]} />
        <div style={{ flex: 1 }} />
        <Button ghost sm icon iconLeft="undo" /><Button ghost sm icon iconLeft="redo" />
        <ToolbarSep />
        <Button primary sm iconLeft="render">Render</Button>
      </Toolbar>
      <DockLayout initial={layout} tabMeta={(id) => TAB_META[id]} renderContent={renderContent} />
      <div className="dcs-statusbar">
        <span className="dcs-badge dcs-badge--ok dcs-badge--dot" style={{ fontSize: 9, height: 14 }}>READY</span>
        <span className="dcs-statusbar__item">menu: {last}</span>
        <span className="dcs-statusbar__spacer" />
        <span className="dcs-statusbar__item">vertices 32,108 · faces 64,201</span>
        <span className="dcs-statusbar__sep" />
        <span className="dcs-statusbar__item">frame {frame} / 240</span>
        <span className="dcs-statusbar__sep" />
        <span className="dcs-statusbar__item dcs-statusbar__item--accent">60.0 fps</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sample synth — "decius synthesis"
   Big knob array, fader strip, envelope+lfo, FX rack.
   ───────────────────────────────────────────────────────────── */
function SampleSynth() {
  const [k, setK] = useStateA({
    osc1: 0.7, osc2: 0.3, oscMix: 0.5, detune: 0.45,
    cutoff: 0.62, reso: 0.3, drive: 0.18, env: 0.55,
    attack: 0.04, decay: 0.4, sustain: 0.65, release: 0.45,
    lfoRate: 0.35, lfoAmt: 0.4,
    reverb: 0.3, delay: 0.2, chorus: 0.15,
    master: 0.78,
  });
  const upd = (key, v) => setK(o => ({ ...o, [key]: v }));
  const [osc1Wave, setOsc1Wave] = useStateA('saw');
  const [osc2Wave, setOsc2Wave] = useStateA('square');
  const [filterMode, setFilterMode] = useStateA('lp');
  const [armed, setArmed] = useStateA(false);
  const [bpm, setBpm] = useStateA(120);

  // Animated meter
  const [meter, setMeter] = useStateA(0.4);
  useEffectA(() => {
    const id = setInterval(() => setMeter(m => Math.max(0, Math.min(1, m + (Math.random() - 0.5) * 0.4))), 80);
    return () => clearInterval(id);
  }, []);

  // Plain synth panel — the 3d bevel comes from data-dcs-style="3d" on the root,
  // no skeuomorphic hardware chrome.
  const Section = ({ title, icon, color, children, span }) => (
    <div style={{
      background: 'var(--dcs-surface-1)',
      border: '1px solid var(--dcs-line)',
      borderRadius: 'var(--dcs-r-3)',
      boxShadow: 'var(--dcs-bevel-up), var(--dcs-shadow-2)',
      gridColumn: span ? `span ${span}` : undefined,
      display: 'flex', flexDirection: 'column',
      position: 'relative',
    }}>
      {title && (
        <div style={{
          position: 'absolute', top: 8, left: 12,
          display: 'flex', alignItems: 'center', gap: 6,
          color: color || 'var(--dcs-accent)',
          fontSize: 9, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '.12em', opacity: .8,
          pointerEvents: 'none',
        }}>
          {icon && <Icon name={icon} size="sm" />}
          <span>{title}</span>
        </div>
      )}
      <div style={{ flex: 1, padding: '22px 14px 14px', display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  );

  return (
    <div className="dcs" data-dcs-style="3d" style={{ background: 'var(--dcs-bg-app)', padding: 12, minWidth: 820 }}>
      {/* Brand header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '6px 14px 12px', marginBottom: 12,
        borderBottom: '1px solid var(--dcs-line)',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, var(--dcs-accent), var(--dcs-accent-lo))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#0a1220', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.3), 0 4px 12px rgba(77,159,255,.3)',
        }}>
          <Icon name="wave-saw" />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.01em' }}>decius synthesis</div>
          <div className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-text-mute)' }}>polyphonic dual-osc · subtractive · v0.5</div>
        </div>
        <span style={{ flex: 1 }} />
        <div className="dcs-row" style={{ gap: 8 }}>
          <Combo value={bpm} onChange={setBpm} min={20} max={300} step={1} format={v => `${v} BPM`} width={100} />
          <Button ghost sm icon iconLeft="record" pressed={armed} onClick={() => setArmed(!armed)} />
          <Button ghost sm icon iconLeft="play" />
          <Button ghost sm icon iconLeft="stop" />
          <div className="dcs-divider--v" />
          {/* Master meter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 1, height: 10, alignItems: 'flex-end' }}>
              {Array.from({ length: 24 }).map((_, i) => {
                const t = i / 23;
                const active = t < meter;
                const color = t > 0.9 ? '#ef6b6b' : t > 0.75 ? '#f2b14a' : '#4ed18a';
                return <div key={i} style={{ width: 3, height: 10, background: active ? color : 'rgba(255,255,255,.06)', borderRadius: 1 }} />;
              })}
            </div>
            <span className="dcs-mono" style={{ fontSize: 9, color: 'var(--dcs-text-mute)' }}>{Math.round((meter - 0.75) * 60)} dB</span>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 1fr 1.2fr',
        gridAutoRows: 'minmax(0, auto)',
        gap: 10,
      }}>
        {/* Oscillators */}
        <Section title="Oscillators" icon="wave-saw" span={2} hw="brushed">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            {[
              { name: 'OSC 1', wave: osc1Wave, setWave: setOsc1Wave, level: k.osc1, levelKey: 'osc1' },
              { name: 'OSC 2', wave: osc2Wave, setWave: setOsc2Wave, level: k.osc2, levelKey: 'osc2' },
            ].map(o => (
              <div key={o.name} style={{ padding: 10, background: 'transparent', borderRadius: 6, border: '1px solid var(--dcs-line-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-accent)', letterSpacing: '.08em' }}>{o.name}</span>
                  <ButtonGroup value={o.wave} onChange={o.setWave} options={[
                    { value: 'sine',   icon: 'wave-sine' },
                    { value: 'tri',    icon: 'wave-tri' },
                    { value: 'saw',    icon: 'wave-saw' },
                    { value: 'square', icon: 'wave-square' },
                    { value: 'noise',  icon: 'wave-noise' },
                  ]} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', paddingBottom: 14 }}>
                  <Knob value={o.level} onChange={v => upd(o.levelKey, v)} size={48} label="LEVEL" format={v => `${(v*100).toFixed(0)}`} />
                  <Knob value={k.detune} onChange={v => upd('detune', v)} size={48} label="DETUNE" bipolar format={v => `${((v-0.5)*100).toFixed(0)}ct`} />
                  <Knob value={k.oscMix} onChange={v => upd('oscMix', v)} size={48} label="OCTAVE" bipolar format={v => `${Math.round((v-0.5)*4)}`} />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Filter */}
        <Section title="Filter" icon="filter-lp" color="#b48cff" hw="brushed">
          <ButtonGroup value={filterMode} onChange={setFilterMode} options={[
            { value: 'lp', icon: 'filter-lp', label: 'LP' },
            { value: 'hp', icon: 'filter-hp', label: 'HP' },
            { value: 'bp', icon: 'filter-bp', label: 'BP' },
            { value: 'nt', icon: 'filter-notch', label: 'Nt' },
          ]} />
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', marginTop: 18, paddingBottom: 14 }}>
            <Knob value={k.cutoff} onChange={v => upd('cutoff', v)} size={56} label="CUTOFF" format={v => `${Math.round(v * 22050)}`} />
            <Knob value={k.reso} onChange={v => upd('reso', v)} size={56} label="RESO" format={v => `${(v*100).toFixed(0)}%`} />
            <Knob value={k.drive} onChange={v => upd('drive', v)} size={56} label="DRIVE" format={v => `${(v*24).toFixed(1)}`} />
          </div>
        </Section>

        {/* Envelope */}
        <Section title="Amp envelope" icon="envelope" color="#4ed18a" hw="brushed">
          <div style={{ height: 78, marginBottom: 8 }}>
            <svg viewBox="0 0 200 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%', background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3 }}>
              {(() => {
                const ax = k.attack * 60;
                const dx = k.decay * 60;
                const sy = (1 - k.sustain) * 60 + 10;
                const rx = k.release * 60;
                return <>
                  <path d={`M 5 70 L ${5+ax} 10 L ${5+ax+dx} ${sy} L ${130} ${sy} L ${130+rx} 70`}
                        fill="rgba(78,209,138,.18)" stroke="#4ed18a" strokeWidth="1.25" strokeLinejoin="round" />
                  <line x1="5" y1="70" x2="195" y2="70" stroke="rgba(255,255,255,.1)" strokeWidth=".5" />
                  <circle cx={5+ax} cy="10" r="2.5" fill="#4ed18a" />
                  <circle cx={5+ax+dx} cy={sy} r="2.5" fill="#4ed18a" />
                  <circle cx={130} cy={sy} r="2.5" fill="#4ed18a" />
                  <circle cx={130+rx} cy="70" r="2.5" fill="#4ed18a" />
                </>;
              })()}
            </svg>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', paddingBottom: 14 }}>
            <Knob value={k.attack} onChange={v => upd('attack', v)} size={40} label="A" format={v => `${(v*5000).toFixed(0)}ms`} />
            <Knob value={k.decay} onChange={v => upd('decay', v)} size={40} label="D" format={v => `${(v*5000).toFixed(0)}ms`} />
            <Knob value={k.sustain} onChange={v => upd('sustain', v)} size={40} label="S" format={v => `${(v*100).toFixed(0)}%`} />
            <Knob value={k.release} onChange={v => upd('release', v)} size={40} label="R" format={v => `${(v*5000).toFixed(0)}ms`} />
          </div>
        </Section>

        {/* LFO */}
        <Section title="LFO 1" icon="lfo" color="#ff7ab8" hw="brushed">
          <div style={{ marginBottom: 6 }}>
            <ButtonGroup value="sine" options={[
              { value: 'sine', icon: 'wave-sine' },
              { value: 'tri', icon: 'wave-tri' },
              { value: 'sq', icon: 'wave-square' },
              { value: 'sh', icon: 'wave-noise' },
            ]} />
          </div>
          <div style={{ height: 50, marginBottom: 6 }}>
            <svg viewBox="0 0 200 50" preserveAspectRatio="none" style={{ width: '100%', height: '100%', background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3 }}>
              {(() => {
                const freq = 1 + k.lfoRate * 8;
                const amp = k.lfoAmt * 18;
                let d = `M 0 25 `;
                for (let x = 0; x <= 200; x += 2) {
                  d += `L ${x} ${25 - Math.sin((x / 200) * freq * Math.PI * 2) * amp} `;
                }
                return <path d={d} fill="none" stroke="#ff7ab8" strokeWidth="1.25" />;
              })()}
            </svg>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', paddingBottom: 14 }}>
            <Knob value={k.lfoRate} onChange={v => upd('lfoRate', v)} size={44} label="RATE" format={v => `${(1+v*8).toFixed(2)}Hz`} />
            <Knob value={k.lfoAmt} onChange={v => upd('lfoAmt', v)} size={44} label="AMT" format={v => `${(v*100).toFixed(0)}%`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <span className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-text-mute)' }}>DEST</span>
              <select className="dcs-select" defaultValue="cut" style={{ width: 70, fontSize: 10 }}>
                <option value="cut">Cutoff</option>
                <option value="pit">Pitch</option>
                <option value="amp">Amp</option>
              </select>
            </div>
          </div>
        </Section>

        {/* FX rack */}
        <Section title="FX" icon="bolt" color="#4ad5d5" span={2} hw="brushed">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              { name: 'CHORUS', v: k.chorus, key: 'chorus', color: '#b48cff' },
              { name: 'DELAY', v: k.delay, key: 'delay', color: '#4ad5d5' },
              { name: 'REVERB', v: k.reverb, key: 'reverb', color: '#4ed18a' },
            ].map(fx => (
              <div key={fx.name} style={{ padding: 10, background: 'transparent', borderRadius: 6, border: '1px solid var(--dcs-line-soft)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Switch checked={fx.v > 0.01} />
                  <span className="dcs-mono" style={{ fontSize: 10, color: fx.color, letterSpacing: '.08em' }}>{fx.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', paddingBottom: 14, paddingTop: 4 }}>
                  <Knob value={fx.v} onChange={v => upd(fx.key, v)} size={44} label="MIX" format={v => `${(v*100).toFixed(0)}`} />
                  <Knob value={0.5} size={44} label={fx.name === 'DELAY' ? 'TIME' : fx.name === 'CHORUS' ? 'RATE' : 'SIZE'} format={() => '0.50'} />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Master */}
        <Section title="Master" icon="volume" color="#4d9fff" hw="lacquer">
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Fader value={k.master} onChange={v => upd('master', v)} height={120} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="dcs-mono" style={{ fontSize: 11, color: 'var(--dcs-accent)' }}>{((k.master - 0.75) * 60).toFixed(1)} dB</span>
              <span className="dcs-mono" style={{ fontSize: 9, color: 'var(--dcs-text-mute)' }}>MASTER OUT</span>
              <hr style={{ width: '100%', border: 'none', borderTop: '1px solid var(--dcs-line)', margin: '6px 0' }} />
              <span className="dcs-mono" style={{ fontSize: 9, color: 'var(--dcs-text-mute)' }}>VOICES</span>
              <span className="dcs-mono" style={{ fontSize: 11, color: 'var(--dcs-text)' }}>4 / 16</span>
              <span className="dcs-mono" style={{ fontSize: 9, color: 'var(--dcs-text-mute)' }}>CPU</span>
              <span className="dcs-mono" style={{ fontSize: 11, color: 'var(--dcs-ok)' }}>12 %</span>
            </div>
          </div>
        </Section>

        {/* Keyboard strip */}
        <div style={{ gridColumn: 'span 3', background: 'var(--dcs-bg)', border: '1px solid var(--dcs-line)', borderRadius: 'var(--dcs-r-2)', padding: 10 }}>
          <div style={{ display: 'flex', gap: 0, height: 70, position: 'relative' }}>
            {(() => {
              const names = ['C','D','E','F','G','A','B'];
              const blackAfter = [true, true, false, true, true, true, false]; // C# D# (none) F# G# A# (none)
              const startOctave = 2;
              const totalOctaves = 4;
              const keys = [];
              for (let o = 0; o < totalOctaves; o++) {
                for (let n = 0; n < 7; n++) {
                  keys.push({ name: names[n], octave: startOctave + o, hasBlack: blackAfter[n] });
                }
              }
              keys.push({ name: 'C', octave: startOctave + totalOctaves, hasBlack: false });
              const highlight = Math.floor(keys.length / 3); // pick a middle-ish key

              return keys.map((k, i) => (
                <div key={i} style={{
                  flex: 1, background: i === highlight
                    ? 'linear-gradient(180deg, #b8d6ff, #6fa8ff)'
                    : 'linear-gradient(180deg, #f8f9fb, #e3e6ec)',
                  border: '1px solid #14161c', borderRight: i === keys.length - 1 ? '1px solid #14161c' : 'none',
                  borderRadius: '0 0 3px 3px',
                  position: 'relative', cursor: 'pointer',
                }}>
                  {k.hasBlack && (
                    <div style={{
                      position: 'absolute', right: '-30%', top: 0,
                      width: '60%', height: '62%',
                      background: 'linear-gradient(180deg, #2a2e38, #14161c)',
                      border: '1px solid #14161c', borderRadius: '0 0 2px 2px',
                      zIndex: 2, cursor: 'pointer',
                    }} />
                  )}
                  {k.name === 'C' && (
                    <div style={{
                      position: 'absolute', bottom: 4, left: 0, right: 0,
                      textAlign: 'center', fontSize: 9, fontFamily: 'var(--dcs-font-mono)',
                      color: i === highlight ? '#1a3a6e' : '#767c8a',
                      fontWeight: 600, pointerEvents: 'none',
                    }}>C{k.octave}</div>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionSampleDCC() {
  return (
    <section className="dw-section" id="sample-dcc">
      <div className="dw-section__eyebrow">Showpiece · 01</div>
      <h2>Sample DCC tool</h2>
      <p className="dw-section__lead">
        Every primitive in the framework — toolbar, outliner, viewport HUD, timeline, inspector,
        combo fields, gizmos — assembled into a working modeler shell. Play with the transform
        values, scrub the timeline, swap shading modes.
      </p>
      <div className="dw-demo dw-demo--inset" style={{ padding: 0 }}>
        <SampleDCC />
      </div>
    </section>
  );
}

function SectionSampleSynth() {
  return (
    <section className="dw-section" id="sample-synth">
      <div className="dw-section__eyebrow">Showpiece · 02</div>
      <h2>Sample synthesizer</h2>
      <p className="dw-section__lead">
        The same decius primitives, repurposed as a polyphonic synth. Knobs, faders, segmented
        waveform pickers, FX rack, modulation envelope, signal meter. Drag any knob vertically.
      </p>
      <div className="dw-demo dw-demo--inset" style={{ padding: 0 }}>
        <SampleSynth />
      </div>
    </section>
  );
}

Object.assign(window, { SampleDCC, SampleSynth, SectionSampleDCC, SectionSampleSynth });


/* ===== sections-skeuomorphic.jsx ===== */
/* sections-skeuomorphic.jsx
   Skeuomorphic step sequencer — patch jacks, backlit buttons, 7-seg LEDs, meters,
   plus draggable patch cables (Reason-style: drag from one jack to another).
*/
const { useState: useStateSK, useEffect: useEffectSK, useRef: useRefSK, useLayoutEffect: useLayoutEffectSK } = React;

/* ─────────── Cable colors ─────────── */
const CABLE_COLORS = [
  '#6fb3ff', // ui blue (brighter, slightly desaturated)
  '#6fdfa8', // ui green
  '#f5c878', // ui amber
  '#f29090', // ui danger
  '#c8a8ff', // ui purple
  '#80e0e0', // ui teal
];

/* ─────────── Patch board context ─────────── */
const PatchContext = React.createContext(null);

function PatchBoard({ children }) {
  const [cables, setCables] = useStateSK([]);
  const [drag, setDrag] = useStateSK(null);
  const [jackPositions, setJackPositions] = useStateSK({});
  const [version, setVersion] = useStateSK(0);
  const boardRef = useRefSK(null);
  const jackRefs = useRefSK({});
  const dragStateRef = useRefSK(null);
  // Mirror cables in a ref so beginDrag always sees the latest array
  // (avoids stale-closure issues when the user clicks rapidly)
  const cablesRef = useRefSK([]);
  useEffectSK(() => { cablesRef.current = cables; }, [cables]);

  const registerJack = (id, el) => {
    jackRefs.current[id] = el;
  };
  const unregisterJack = (id) => {
    delete jackRefs.current[id];
  };

  const measureAll = React.useCallback(() => {
    if (!boardRef.current) return;
    const board = boardRef.current.getBoundingClientRect();
    const next = {};
    Object.entries(jackRefs.current).forEach(([id, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      next[id] = {
        x: r.left - board.left + r.width / 2,
        y: r.top - board.top + r.height / 2,
      };
    });
    setJackPositions(next);
  }, []);

  useLayoutEffectSK(() => { measureAll(); }, [cables, version, measureAll]);
  useEffectSK(() => {
    const onResize = () => measureAll();
    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(onResize);
    if (boardRef.current) ro.observe(boardRef.current);
    return () => { window.removeEventListener('resize', onResize); ro.disconnect(); };
  }, [measureAll]);

  const beginDrag = (fromId, e) => {
    const board = boardRef.current.getBoundingClientRect();
    // Read cables from the ref so we always have the latest set,
    // not a stale closure capture from an earlier render.
    const currentCables = cablesRef.current;
    const existingIdx = currentCables.findIndex(c => c.from === fromId || c.to === fromId);
    let anchorId = fromId;
    let color = CABLE_COLORS[currentCables.length % CABLE_COLORS.length];
    if (existingIdx >= 0) {
      const c = currentCables[existingIdx];
      anchorId = c.from === fromId ? c.to : c.from;
      color = c.color;
      const removedFromId = fromId;
      setCables(cs => cs.filter(cc => !(cc.from === c.from && cc.to === c.to && cc.color === c.color)));
    }
    const anchorEl = jackRefs.current[anchorId];
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const fromPt = { x: r.left - board.left + r.width / 2, y: r.top - board.top + r.height / 2 };

    const initialPt = { x: e.clientX - board.left, y: e.clientY - board.top };
    // Initial pivot = midpoint between anchor and cursor, dropped by current sag
    const initChord = Math.hypot(initialPt.x - fromPt.x, initialPt.y - fromPt.y);
    let initSag = 0;
    if (initChord < CABLE_LENGTH) {
      initSag = Math.sqrt(Math.max(0, CABLE_LENGTH * CABLE_LENGTH - initChord * initChord)) / 2;
    }
    const initBob = {
      x: (fromPt.x + initialPt.x) / 2,
      y: (fromPt.y + initialPt.y) / 2 + initSag,
    };
    dragStateRef.current = {
      fromId: anchorId, fromPt, color,
      targetPt: { ...initialPt },
      bob: { ...initBob },
      vx: 0, vy: 0,
      lastT: performance.now(),
    };
    setDrag({ fromId: anchorId, fromPt, toPt: initialPt, color, bob: initBob });

    let rafId;
    const tick = () => {
      const s = dragStateRef.current;
      if (!s) return;
      const now = performance.now();
      const dt = Math.min(0.05, (now - s.lastT) / 1000);
      s.lastT = now;

      // Pivot = current midpoint between anchor (fromPt) and dragged plug (targetPt).
      // Bob hangs from pivot by `sag` below — pivot moves WITH the plug.
      const chord = Math.hypot(s.targetPt.x - s.fromPt.x, s.targetPt.y - s.fromPt.y);
      let sag = 0;
      if (chord < CABLE_LENGTH) {
        sag = Math.sqrt(Math.max(0, CABLE_LENGTH * CABLE_LENGTH - chord * chord)) / 2;
      }
      const rest = {
        x: (s.fromPt.x + s.targetPt.x) / 2,
        y: (s.fromPt.y + s.targetPt.y) / 2 + sag,
      };

      // Spring-damper on bob toward rest position (underdamped pendulum)
      const k = 30;
      const damp = 1.4;
      const ax = (rest.x - s.bob.x) * k - s.vx * damp;
      const ay = (rest.y - s.bob.y) * k - s.vy * damp;
      s.vx += ax * dt;
      s.vy += ay * dt;
      s.bob.x += s.vx * dt;
      s.bob.y += s.vy * dt;

      setDrag(d => d ? { ...d, toPt: { ...s.targetPt }, bob: { ...s.bob } } : null);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    const move = (ev) => {
      const s = dragStateRef.current;
      if (!s) return;
      s.targetPt = { x: ev.clientX - board.left, y: ev.clientY - board.top };
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      cancelAnimationFrame(rafId);
      let hitId = null;
      Object.entries(jackRefs.current).forEach(([id, el]) => {
        if (id === anchorId || !el) return;
        const rr = el.getBoundingClientRect();
        if (ev.clientX >= rr.left && ev.clientX <= rr.right && ev.clientY >= rr.top && ev.clientY <= rr.bottom) {
          hitId = id;
        }
      });
      if (hitId) {
        setCables(cs => {
          const filtered = cs.filter(c => c.from !== anchorId && c.to !== anchorId && c.from !== hitId && c.to !== hitId);
          return [...filtered, { from: anchorId, to: hitId, color }];
        });
      }
      dragStateRef.current = null;
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const removeCable = (idx) => setCables(cs => cs.filter((_, i) => i !== idx));

  const ctx = {
    registerJack, unregisterJack, beginDrag, cables, removeCable,
    isPatched: (id) => cables.some(c => c.from === id || c.to === id) || (drag && drag.fromId === id),
    refreshLayout: () => setVersion(v => v + 1),
  };

  return (
    <PatchContext.Provider value={ctx}>
      <div ref={boardRef} style={{ position: 'relative' }}>
        {children}
        <CableLayer cables={cables} jackPositions={jackPositions} drag={drag} onRemove={removeCable} />
      </div>
    </PatchContext.Provider>
  );
}

/* Catenary with constant total length, lateral sway at the midpoint, smooth bottom.
   Sag is maximum when the endpoints are closest (cable hangs in a deep V) and
   smoothly decreases to zero as the chord approaches the cable length. */
const CABLE_LENGTH = 640;   // total "rope" length in px — cable never stretches
function cablePath(a, b, bob) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  // If no bob position is supplied, fall back to gravity rest position
  let m = bob;
  if (!m) {
    const chord = Math.hypot(dx, dy);
    let sag = 0;
    if (chord < CABLE_LENGTH) {
      sag = Math.sqrt(Math.max(0, CABLE_LENGTH * CABLE_LENGTH - chord * chord)) / 2;
    }
    m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 + sag };
  }
  // Smooth cubic through midpoint: control points slightly past the midpoint
  // so the curve passes through (m.x, m.y) without a corner.
  const c1x = a.x + (m.x - a.x) * 0.5;
  const c1y = a.y + (m.y - a.y) * 1.10;
  const c2x = b.x + (m.x - b.x) * 0.5;
  const c2y = b.y + (m.y - b.y) * 1.10;
  return `M ${a.x} ${a.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${b.x} ${b.y}`;
}

/* Lighten / darken a hex color */
function shade(hex, amt) {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * amt)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * amt)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * amt)));
  return `#${[r,g,b].map(v => v.toString(16).padStart(2,'0')).join('')}`;
}

function ConnectedEnd({ x, y, color }) {
  // Chrome plug head — black outer rim, brushed-steel center, no cable color
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={x} cy={y} r="9" fill="#0a0a0a" />
      <circle cx={x} cy={y} r="7.5"
              fill="url(#plug-metal)" />
      <circle cx={x} cy={y} r="3" fill="#1a1a1a" />
      <circle cx={x - 2} cy={y - 2.5} r="2" fill="rgba(255,255,255,.55)" />
    </g>
  );
}

function DragPlug({ x, y, color }) {
  // Free end while dragging — slightly larger, with drop shadow
  return (
    <g style={{ pointerEvents: 'none' }}>
      <ellipse cx={x} cy={y + 2} rx="10" ry="3.5" fill="rgba(0,0,0,.4)" />
      <circle cx={x} cy={y} r="10" fill="#0a0a0a" />
      <circle cx={x} cy={y} r="8.5" fill="url(#plug-metal)" />
      <circle cx={x} cy={y} r="3.5" fill="#1a1a1a" />
      <ellipse cx={x - 2.5} cy={y - 3} rx="2.5" ry="1.6" fill="rgba(255,255,255,.6)" />
    </g>
  );
}

function CableLayer({ cables, jackPositions, drag, onRemove }) {
  const resolved = cables.map((c, i) => {
    const a = jackPositions[c.from];
    const b = jackPositions[c.to];
    if (!a || !b) return null;
    return { ...c, a, b, i };
  }).filter(Boolean);

  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5, overflow: 'visible' }}>
      <defs>
        <radialGradient id="plug-metal" cx="0.4" cy="0.35" r="0.65">
          <stop offset="0%" stopColor="#f0f2f5" />
          <stop offset="45%" stopColor="#9a9ea6" />
          <stop offset="100%" stopColor="#3a3d44" />
        </radialGradient>
        {/* Cable strand gradient: dark edge → light center → dark edge, giving cylindrical look */}
        {[...CABLE_COLORS].map(c => (
          <linearGradient key={c} id={`cable-${c.replace('#','')}`} x1="0" y1="0" x2="0" y2="1" gradientUnits="userSpaceOnUse">
            <stop offset="0%"  stopColor={shade(c, -0.30)} />
            <stop offset="50%" stopColor={shade(c, +0.10)} />
            <stop offset="100%" stopColor={shade(c, -0.30)} />
          </linearGradient>
        ))}
      </defs>

      {/* shadows */}
      {resolved.map(c => (
        <path key={`s${c.i}`} d={cablePath({ x: c.a.x, y: c.a.y + 5 }, { x: c.b.x, y: c.b.y + 5 })}
              fill="none" stroke="rgba(0,0,0,.45)" strokeWidth="7" strokeLinecap="round" />
      ))}
      {drag && (
        <path d={cablePath(drag.fromPt, drag.toPt, drag.bob)} fill="none"
              stroke="rgba(0,0,0,.45)" strokeWidth="7" strokeLinecap="round"
              transform="translate(0,5)" />
      )}

      {/* Plugs first, then cable strokes on top — cable visually enters the plug */}
      {resolved.map(c => (
        <g key={`plugs${c.i}`} style={{ pointerEvents: 'none' }}>
          <ConnectedEnd x={c.a.x} y={c.a.y} color={c.color} />
          <ConnectedEnd x={c.b.x} y={c.b.y} color={c.color} />
        </g>
      ))}

      {/* cable bodies — drawn AFTER plugs, so cable appears to plug into them */}
      {resolved.map(c => {
        const gradId = `cable-${c.color.replace('#','')}`;
        return (
          <g key={c.i} style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
             onClick={(e) => { e.stopPropagation(); onRemove(c.i); }}>
            <path d={cablePath(c.a, c.b)} fill="none" stroke={`url(#${gradId})`} strokeWidth="6" strokeLinecap="round" />
            <path d={cablePath(c.a, c.b)} fill="none" stroke="rgba(255,255,255,.20)" strokeWidth="1.2" strokeLinecap="round" />
          </g>
        );
      })}

      {/* drag preview */}
      {drag && (() => {
        const gradId = `cable-${drag.color.replace('#','')}`;
        const path = cablePath(drag.fromPt, drag.toPt, drag.bob);
        return (
          <g>
            {/* Plug heads first */}
            <ConnectedEnd x={drag.fromPt.x} y={drag.fromPt.y} color={drag.color} />
            <DragPlug x={drag.toPt.x} y={drag.toPt.y} color={drag.color} />
            {/* Cable on top, going INTO the plugs */}
            <path d={path} fill="none" stroke={`url(#${gradId})`} strokeWidth="6" strokeLinecap="round" />
            <path d={path} fill="none" stroke="rgba(255,255,255,.20)" strokeWidth="1.2" strokeLinecap="round" />
          </g>
        );
      })()}
    </svg>
  );
}

function Jack({ id, label }) {
  const ctx = React.useContext(PatchContext);
  const ref = useRefSK(null);
  const jackId = id || React.useId();

  useLayoutEffectSK(() => {
    if (ctx) {
      ctx.registerJack(jackId, ref.current);
      ctx.refreshLayout();
      return () => ctx.unregisterJack(jackId);
    }
  }, []);

  const patched = ctx ? ctx.isPatched(jackId) : false;

  const handleDown = (e) => {
    if (!ctx) return;
    e.preventDefault();
    ctx.beginDrag(jackId, e);
  };

  // Hexagon points (flat-top), centered on 16,16, radius ~15
  const hexR = 15;
  const hexPts = [0,1,2,3,4,5].map(i => {
    const ang = Math.PI / 3 * i;
    return [16 + hexR * Math.cos(ang), 16 + hexR * Math.sin(ang)].join(',');
  }).join(' ');

  return (
    <div className={`dcs-jack${patched ? ' dcs-jack--patched' : ''}`}>
      <div ref={ref} className="dcs-jack__socket" onPointerDown={handleDown} style={{ cursor: 'crosshair' }}>
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <defs>
            <linearGradient id="jack-nut" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e8eaee" />
              <stop offset="35%" stopColor="#b8bcc4" />
              <stop offset="65%" stopColor="#7a7e86" />
              <stop offset="100%" stopColor="#3a3d44" />
            </linearGradient>
            <radialGradient id="jack-bevel" cx="0.45" cy="0.35" r="0.7">
              <stop offset="0%" stopColor="#f0f2f5" />
              <stop offset="40%" stopColor="#9a9ea6" />
              <stop offset="100%" stopColor="#2a2d33" />
            </radialGradient>
            <radialGradient id="jack-inner" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#3a3d44" />
              <stop offset="100%" stopColor="#181a20" />
            </radialGradient>
            <radialGradient id="jack-hole" cx="0.5" cy="0.4" r="0.5">
              <stop offset="0%" stopColor="#0a0a0a" />
              <stop offset="85%" stopColor="#000" />
              <stop offset="100%" stopColor="#000" />
            </radialGradient>
          </defs>
          {/* Hex nut with brushed metal shading */}
          <polygon points={hexPts} fill="url(#jack-nut)"
                   stroke="rgba(0,0,0,.55)" strokeWidth="0.75"
                   strokeLinejoin="miter" />
          {/* Chrome beveled ring */}
          <circle cx="16" cy="16" r="11" fill="url(#jack-bevel)" />
          {/* Inner dark gray recessed ring */}
          <circle cx="16" cy="16" r="7.5" fill="url(#jack-inner)" />
          {/* Center hole */}
          <circle cx="16" cy="16" r="4" fill="url(#jack-hole)" />
          {/* Subtle inner shadow rim */}
          <circle cx="16" cy="16" r="4" fill="none" stroke="rgba(0,0,0,.6)" strokeWidth="0.5" />
          {/* Highlight glint on top-left of bevel */}
          <path d="M 9 12 a 7 7 0 0 1 5 -3" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1" strokeLinecap="round" />
          {/* Patch indicator — orange plug visible in socket */}
          {patched && (
            <>
              <circle cx="16" cy="16" r="3" fill="#f8a23a" />
              <circle cx="15" cy="15" r="1" fill="rgba(255,255,255,.5)" />
            </>
          )}
        </svg>
      </div>
      {label && <div className="dcs-jack__label">{label}</div>}
    </div>
  );
}

function LitButton({ children, lit, color, sm, onClick }) {
  const cls = [
    'dcs-litbtn',
    lit && 'dcs-litbtn--lit',
    color && `dcs-litbtn--${color}`,
    sm && 'dcs-litbtn--sm',
  ].filter(Boolean).join(' ');
  return <button className={cls} onClick={onClick}>{children}</button>;
}

function Lcd({ value, size = 'md', color, digits }) {
  // 7-segment glyph map. segments: a=top, b=top-r, c=bot-r, d=bot, e=bot-l, f=top-l, g=mid
  const SEG = {
    '0':'abcdef','1':'bc','2':'abged','3':'abgcd','4':'fbgc','5':'afgcd','6':'afgecd',
    '7':'abc','8':'abcdefg','9':'abcfgd','A':'abcefg','B':'cdefg','C':'adef','D':'bcdeg',
    'E':'adefg','F':'aefg','H':'bcefg','I':'ef','J':'bcde','L':'def','O':'abcdef',
    'P':'abefg','R':'efg','S':'afgcd','U':'bcdef','-':'g',' ':'','.':'',
    'K':'efg','o':'cdeg','n':'ceg','r':'eg','t':'defg','u':'cde',
  };
  const str = String(value).toUpperCase();
  const padded = digits ? str.padStart(digits, ' ') : str;

  // Segment polygon coordinates (24×40 viewBox)
  const segPath = {
    a: 'M 5 2 L 19 2 L 17 5 L 7 5 Z',
    b: 'M 19 3 L 21 5 L 21 18 L 19 19 L 17 17 L 17 6 Z',
    c: 'M 19 21 L 21 22 L 21 35 L 19 37 L 17 34 L 17 23 Z',
    d: 'M 5 38 L 19 38 L 17 35 L 7 35 Z',
    e: 'M 5 21 L 7 23 L 7 34 L 5 37 L 3 35 L 3 22 Z',
    f: 'M 5 3 L 7 6 L 7 17 L 5 19 L 3 18 L 3 5 Z',
    g: 'M 5 20 L 7 18 L 17 18 L 19 20 L 17 22 L 7 22 Z',
  };

  return (
    <div className={`dcs-lcd dcs-lcd--${size}${color ? ` dcs-lcd--${color}` : ''}`}>
      {padded.split('').map((ch, i) => {
        if (ch === '.') return <span key={i} className="dcs-lcd__dot dcs-lcd__dot--on" />;
        const on = SEG[ch] || '';
        return (
          <div key={i} className="dcs-lcd__digit">
            <svg viewBox="0 0 24 40" preserveAspectRatio="xMidYMid meet">
              {Object.entries(segPath).map(([k, d]) => (
                <path key={k} d={d}
                      className={on.includes(k) ? 'dcs-lcd__seg-on' : 'dcs-lcd__seg-off'} />
              ))}
            </svg>
          </div>
        );
      })}
    </div>
  );
}

function LedMeter({ value, segments = 14, vertical }) {
  const lit = Math.round(value * segments);
  const segs = Array.from({ length: segments }, (_, i) => {
    const t = i / (segments - 1);
    const isLit = i < lit;
    let zone = 'ok';
    if (t > 0.85) zone = 'peak';
    else if (t > 0.65) zone = 'warn';
    const cls = isLit
      ? `dcs-meter__seg dcs-meter__seg--lit-${zone}`
      : `dcs-meter__seg dcs-meter__seg--${zone}`;
    return <div key={i} className={cls} />;
  });
  return <div className={`dcs-meter${vertical ? ' dcs-meter--v' : ''}`} style={{ width: vertical ? 14 : '100%', height: vertical ? 100 : 14 }}>{segs}</div>;
}

function SectionSkeuomorphic() {
  const [playing, setPlaying] = useStateSK(false);
  const [steps, setSteps] = useStateSK([true, false, true, true, false, true, false, true]);
  const [activeStep, setActiveStep] = useStateSK(0);
  const [numSteps, setNumSteps] = useStateSK(8);
  const [meter, setMeter] = useStateSK(0.5);
  const [stepVals, setStepVals] = useStateSK(() => Array.from({ length: 8 }, (_, i) => 0.3 + ((i * 7) % 30) / 100));

  useEffectSK(() => {
    if (!playing) return;
    const id = setInterval(() => setActiveStep(s => (s + 1) % numSteps), 200);
    return () => clearInterval(id);
  }, [playing, numSteps]);

  useEffectSK(() => {
    const id = setInterval(() => setMeter(m => Math.max(0.1, Math.min(0.95, m + (Math.random() - 0.5) * 0.4))), 90);
    return () => clearInterval(id);
  }, []);

  const toggleStep = (i) => setSteps(s => s.map((v, j) => j === i ? !v : v));

  return (
    <section className="dw-section" id="skeuomorphic">
      <div className="dw-section__eyebrow">Showpiece · 03</div>
      <h2>Skeuomorphic hardware</h2>
      <p className="dw-section__lead">
        For when the design needs to feel like real gear — modular synths, drum machines, guitar
        pedals, mixing consoles. Patch jacks with chrome rings, backlit plastic buttons that glow,
        red 7-segment LED displays, LED bargraph meters. <strong style={{ color: 'var(--dw-text)' }}>Drag from one
        jack to another to patch a cable</strong> — Reason-style. Click an existing cable to remove it.
      </p>

      <div className="dw-demo dw-demo--app" style={{ padding: 28, marginBottom: 32 }}>
        <div className="dcs" data-dcs-demo data-dcs-style="3d" style={{ minWidth: 720 }}>
          <PatchBoard>
            <div className="dcs-hw dcs-hw--red" style={{ padding: '36px 24px 24px', position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span className="dcs-hw__screw dcs-hw__screw--tl" />
              <span className="dcs-hw__screw dcs-hw__screw--tr" />
              <span className="dcs-hw__screw dcs-hw__screw--bl" />
              <span className="dcs-hw__screw dcs-hw__screw--br" />

              <div className="dcs-hw__label" style={{ position: 'absolute', top: 12, left: 0, right: 0, textAlign: 'center', fontSize: 13, letterSpacing: '.22em' }}>
                8 STEP SEQUENCER
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <LitButton lit={!playing} onClick={() => setPlaying(false)}>■</LitButton>
                    <Jack id="stop-in" label="STOP" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <LitButton lit={playing} color="green" onClick={() => setPlaying(true)}>▶</LitButton>
                    <Jack id="start-in" label="START" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <LitButton onClick={() => setActiveStep(s => (s + 1) % numSteps)}>▶|</LitButton>
                    <div style={{ height: 30, display: 'flex', alignItems: 'center' }}>
                      <span className="dcs-jack__label">STEP</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <Jack id="play-trig" label="PLAY TRIG" />
                    <Jack id="reset-in" label="RESET" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <LitButton color="amber">EXT</LitButton>
                    <Jack id="ext-clk" label="EXT CLK" />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div className="dcs-step">
                      <div className="dcs-step__btn" onClick={() => setNumSteps(n => Math.min(16, n + 1))}>
                        <Icon name="chevron-up" />
                      </div>
                      <div className="dcs-step__btn" onClick={() => setNumSteps(n => Math.max(1, n - 1))}>
                        <Icon name="chevron-down" />
                      </div>
                    </div>
                    <Lcd value={numSteps} size="md" />
                  </div>
                  <div className="dcs-jack__label">NUM OF STEPS</div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <Jack id="cv-offset" />
                  <div className="dcs-jack__label">CV OFFSET</div>
                </div>

                <fieldset className="dcs-silk">
                  <legend className="dcs-silk__title">CLOCK</legend>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Knob value={0.45} size={50} />
                      <div className="dcs-jack__label" style={{ marginTop: -4 }}>RATE</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Knob value={0.2} size={50} />
                      <div className="dcs-jack__label" style={{ marginTop: -4 }}>GLIDE</div>
                    </div>
                  </div>
                </fieldset>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 4 }}>
                <div style={{ flex: 1 }}>
                  <div className="dcs-jack__label" style={{ marginBottom: 6 }}>STEP ON / OFF</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6 }}>
                    {steps.slice(0, 8).map((on, i) => (
                      <LitButton key={i} lit={on} color="cyan" onClick={() => toggleStep(i)}>
                        <span style={{ opacity: i === activeStep && playing ? .4 : 1 }}>{i + 1}</span>
                      </LitButton>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, marginTop: 10, justifyItems: 'center' }}>
                    {steps.slice(0, 8).map((_, i) => (
                      <Fader key={i} value={stepVals[i]} height={88}
                        onChange={v => setStepVals(s => s.map((x, j) => j === i ? v : x))} />
                    ))}
                  </div>
                </div>

                <fieldset className="dcs-silk">
                  <legend className="dcs-silk__title">OUTPUT</legend>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Knob value={0.6} size={50} />
                      <div className="dcs-jack__label">GATE TIME</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Jack id="gate-out" />
                      <div className="dcs-jack__label">GATE OUT</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Jack id="cv-out" />
                      <div className="dcs-jack__label">CV OUT</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <LedMeter value={meter} segments={14} vertical />
                      <div className="dcs-jack__label">LEVEL</div>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>

            {/* Second module — destination */}
            <div className="dcs-hw dcs-hw--brushed" style={{ marginTop: 18, padding: '24px 20px', position: 'relative', display: 'flex', alignItems: 'center', gap: 20 }}>
              <span className="dcs-hw__screw dcs-hw__screw--tl" />
              <span className="dcs-hw__screw dcs-hw__screw--tr" />
              <span className="dcs-hw__screw dcs-hw__screw--bl" />
              <span className="dcs-hw__screw dcs-hw__screw--br" />
              <div className="dcs-hw__label" style={{ letterSpacing: '.18em' }}>VCO ▸ INPUTS</div>
              <div style={{ display: 'flex', gap: 14, marginLeft: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Jack id="vco-1v" /><div className="dcs-jack__label">1V/OCT</div></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Jack id="vco-gate" /><div className="dcs-jack__label">GATE IN</div></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Jack id="vco-fm" /><div className="dcs-jack__label">FM</div></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Jack id="vco-sync" /><div className="dcs-jack__label">SYNC</div></div>
              </div>
            </div>
          </PatchBoard>
        </div>
        <div className="dw-demo__caption">
          <span>// Drag a jack onto another to patch · click a cable to disconnect</span>
        </div>
      </div>

      {/* Component reference grid */}
      <h3 style={{ marginTop: 40, marginBottom: 12 }}>Components</h3>
      <div className="dw-demo dw-demo--app" style={{ padding: 28, marginBottom: 32 }}>
        <div className="dcs" data-dcs-demo style={{ minWidth: 700 }}>
          <PatchBoard>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 28, color: 'var(--dcs-text)' }}>
              <div>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>Patch jacks</div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                  <Jack id="ref-in"  label="IN" />
                  <Jack id="ref-out" label="OUT" />
                  <Jack id="ref-thru" label="THRU" />
                  <Jack id="ref-aux" label="AUX" />
                </div>
              </div>

              <div>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>Backlit buttons</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <LitButton lit>1</LitButton>
                  <LitButton lit color="cyan">2</LitButton>
                  <LitButton lit color="amber">3</LitButton>
                  <LitButton lit color="green">4</LitButton>
                  <LitButton>5</LitButton>
                </div>
              </div>

              <div>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>7-segment LEDs</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Lcd value="888" size="md" />
                  <Lcd value="42" size="md" color="amber" />
                  <Lcd value="OK" size="md" color="green" />
                </div>
              </div>

              <div>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>LED meters</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <LedMeter value={meter} segments={14} vertical />
                    <div className="dcs-jack__label">L</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <LedMeter value={Math.max(0.1, meter - 0.1)} segments={14} vertical />
                    <div className="dcs-jack__label">R</div>
                  </div>
                </div>
              </div>

              <div>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>Modular row</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <Jack id="ref-vco" label="VCO" />
                  <Jack id="ref-vcf" label="VCF" />
                  <Jack id="ref-vca" label="VCA" />
                  <Jack id="ref-env" label="ENV" />
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div className="dw-caption" style={{ color: 'var(--dcs-text-mute)', marginBottom: 12 }}>Silkscreen group boxes</div>
                <div className="dcs-hw dcs-hw--red" style={{ display: 'flex', flexWrap: 'wrap', gap: 18, padding: '22px 20px', position: 'relative' }}>
                  <span className="dcs-hw__screw dcs-hw__screw--tl" />
                  <span className="dcs-hw__screw dcs-hw__screw--tr" />
                  <span className="dcs-hw__screw dcs-hw__screw--bl" />
                  <span className="dcs-hw__screw dcs-hw__screw--br" />
                  <fieldset className="dcs-silk">
                    <legend className="dcs-silk__title">OSCILLATOR</legend>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Knob value={0.7} size={48} /><div className="dcs-jack__label">TUNE</div></div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Knob value={0.35} size={48} /><div className="dcs-jack__label">SHAPE</div></div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Knob value={0.5} size={48} /><div className="dcs-jack__label">PWM</div></div>
                    </div>
                  </fieldset>
                  <fieldset className="dcs-silk">
                    <legend className="dcs-silk__title">FILTER</legend>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Knob value={0.62} size={48} /><div className="dcs-jack__label">CUTOFF</div></div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><Knob value={0.28} size={48} /><div className="dcs-jack__label">RES</div></div>
                    </div>
                  </fieldset>
                  <fieldset className="dcs-silk">
                    <legend className="dcs-silk__title">DISPLAY</legend>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Lcd value="120" size="md" />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                        <LedMeter value={meter} segments={14} vertical />
                        <div className="dcs-jack__label">VU</div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </PatchBoard>
        </div>
        <div className="dw-demo__caption">
          <span>// Drag jacks to patch · click a connected jack to grab its plug</span>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { SectionSkeuomorphic, Jack, LitButton, Lcd, LedMeter, PatchBoard });


/* ===== sections-intro.jsx ===== */
/* sections-intro.jsx
   Hero · Why · Install
*/
const { useState: useStateH, useEffect: useEffectH } = React;

function HeroDeck() {
  // Animated little knob array that fills out the hero — alive but not noisy
  const [t, setT] = useStateH(0);
  const [sweepX, setSweepX] = useStateH(null);   // sweep position, or null between notes
  useEffectH(() => {
    // 60fps tick: knobs/wave drift continuously; the sweep only fires when a
    // "note" plays. A little sequencer fires notes in musical bursts — a few
    // pulses, a rest, a run of 3–4, a rest — like a real pattern.
    const STEP = 460, SWEEP = 400;   // ms per sequencer step / per sweep pass
    const genPhrase = () => {
      const out = [];
      const bursts = 2 + Math.floor(Math.random() * 3);          // 2–4 bursts
      for (let b = 0; b < bursts; b++) {
        const on = Math.random() < 0.62 ? 1 : 2 + Math.floor(Math.random() * 2);  // mostly 1, sometimes 2–3
        for (let i = on; i > 0; i--) out.push(true);
        for (let i = 4 + Math.floor(Math.random() * 9); i > 0; i--) out.push(false);  // 4–12 rests
      }
      return out;
    };
    let phrase = genPhrase(), pi = 0, nextStep = performance.now(), noteStart = null;
    const id = setInterval(() => {
      const now = performance.now();
      setT(v => v + 0.0064);
      if (now >= nextStep) {
        if (pi >= phrase.length) { phrase = genPhrase(); pi = 0; }
        if (phrase[pi++]) noteStart = now;     // a note hits → trigger one pass
        nextStep = now + STEP;
      }
      if (noteStart !== null) {
        const prog = (now - noteStart) / SWEEP;
        if (prog >= 1) { noteStart = null; setSweepX(null); }
        else setSweepX(prog * 610 - 70);       // -70 (off-left) → 540 (off-right)
      }
    }, 16);
    return () => clearInterval(id);
  }, []);

  const ring = (i, base) => 0.5 + 0.45 * Math.sin(t * 0.7 + i * 0.9 + base);

  return (
    <div className="dw-stage" style={{ overflow: 'hidden' }}>
      <div className="dw-stage__chrome">
        <div className="dw-stage__lights">
          <div className="dw-stage__light" />
          <div className="dw-stage__light" />
          <div className="dw-stage__light" />
        </div>
        <div className="dw-stage__title">decius · live preview · 1920×1080</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="fullscreen" size="sm" style={{ color: 'var(--dcs-text-mute)' }} />
        </div>
      </div>

      <div className="dcs" style={{ padding: 20, background: 'var(--dcs-bg-app)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
          {/* Left: viewport-y card with knob rack */}
          <Panel title="Filter Bank ▸ Lowpass" icon="filter-lp" headerActive
                 tools={<><Button ghost sm icon iconLeft="bolt" /><Button ghost sm icon iconLeft="more-h" /></>}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', padding: '6px 6px 16px', gap: 8, flexShrink: 0 }}>
              <Knob value={ring(0, 0)}     size={56} label="CUT"   format={v => `${Math.round(v * 22050)}`} />
              <Knob value={ring(1, 1.2)}   size={56} label="RES"   format={v => `${(v*100).toFixed(0)}%`} />
              <Knob value={ring(2, 0.4)}   size={56} label="DRIVE" format={v => `${(v*24).toFixed(1)}`} />
              <Knob value={ring(3, 2.1)}   size={56} label="ENV"   bipolar format={v => `${((v-0.5)*2).toFixed(2)}`} />
              <Knob value={ring(4, 1.7)}   size={56} label="LFO"   bipolar format={v => `${((v-0.5)*100).toFixed(0)}`} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexShrink: 0 }}>
              <ButtonGroup value="lp" options={[{ value: 'lp', label: 'LP' }, { value: 'hp', label: 'HP' }, { value: 'bp', label: 'BP' }, { value: 'nt', label: 'Nt' }]} />
              <ButtonGroup value="24" options={[{ value: '12', label: '12' }, { value: '24', label: '24' }]} />
              <span style={{ flex: 1 }} />
              <Switch checked />
              <span style={{ fontSize: 10, color: 'var(--dcs-text-mute)', letterSpacing: '.06em' }}>KEY&nbsp;TRK</span>
            </div>

            <div style={{ flex: 2, minHeight: 78, position: 'relative' }}>
              <svg viewBox="0 0 400 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%', background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3 }}>
                <defs>
                  <linearGradient id="hero-fill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--dcs-accent)" stopOpacity=".4" />
                    <stop offset="100%" stopColor="var(--dcs-accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0.25, 0.5, 0.75].map(y => <line key={y} x1="0" y1={y * 80} x2="400" y2={y * 80} stroke="rgba(255,255,255,.05)" strokeWidth=".5" />)}
                {(() => {
                  const cutoff = ring(0, 0);
                  const reso = ring(1, 1.2);
                  let d = `M 0 ${75 - 5 * reso} `;
                  for (let x = 0; x <= 400; x += 4) {
                    const f = x / 400;
                    const resonance = Math.exp(-Math.pow((f - cutoff) * 24, 2)) * reso * 35;
                    const drop = f < cutoff ? 0 : Math.min(60, (f - cutoff) * 180);
                    const y = 75 - resonance + drop;
                    d += `L ${x} ${Math.min(78, y)} `;
                  }
                  return <>
                    <path d={d + ' L 400 80 L 0 80 Z'} fill="url(#hero-fill)" />
                    <path d={d} fill="none" stroke="var(--dcs-accent)" strokeWidth="1.5" />
                  </>;
                })()}
              </svg>
              <div style={{ position: 'absolute', top: 6, left: 8, fontFamily: 'var(--dcs-font-mono)', fontSize: 9, color: 'var(--dcs-text-mute)' }}>FREQ RESP</div>
            </div>

            {/* Oscilloscope — flexes to fill whatever height the panel has left,
                so the column never shows dead space against the taller side. */}
            <div style={{ flex: 3, minHeight: 56, marginTop: 8, position: 'relative' }}>
              <svg viewBox="0 0 400 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%', background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3 }}>
                {(() => {
                  const amp = 16 + 22 * ring(2, 0.4);   // driven by DRIVE
                  let wave = 'M 0 50 ';
                  for (let x = 0; x <= 400; x += 3) {
                    const ph = (x / 400) * Math.PI * 6;
                    const y = 50 + Math.sin(ph + t) * amp + Math.sin(ph * 2.4 + t * 1.6) * (amp * 0.32);
                    wave += `L ${x} ${y.toFixed(1)} `;
                  }
                  let grid = '';
                  for (let x = 0; x <= 400; x += 50) grid += `M ${x} 0 L ${x} 100 `;
                  [25, 50, 75].forEach(y => { grid += `M 0 ${y} L 400 ${y} `; });
                  // Vital-style playback sweep: a soft-edged head with a long
                  // logarithmic tail rides L→R; a blurred copy makes the trace
                  // and the grid glow as it passes.
                  // Vital/radar playback sweep: the bar is the leading edge; the
                  // whole effect (bg wash + glowing trace/grid) trails BEHIND it
                  // and is clipped at the bar, so ahead is just the faded base
                  // wave. Additive (screen) so it only brightens.
                  // The sweep only renders while a note is playing (sweepX set
                  // by the sequencer); otherwise just the faded base trace + grid.
                  return <>
                    {sweepX !== null && (
                      <>
                        <defs>
                          <clipPath id="scope-clip"><rect x="0" y="0" width={Math.max(0, sweepX)} height="100" /></clipPath>
                          {/* background wash — logarithmic white→blue→fade behind the bar */}
                          <linearGradient id="scope-bg" gradientUnits="userSpaceOnUse" x1={sweepX - 230} y1="0" x2={sweepX} y2="0">
                            <stop offset="0" stopColor="var(--dcs-accent)" stopOpacity="0" />
                            <stop offset="0.45" stopColor="var(--dcs-accent)" stopOpacity=".12" />
                            <stop offset="0.78" stopColor="var(--dcs-accent)" stopOpacity=".36" />
                            <stop offset="0.93" stopColor="#4d9fff" stopOpacity=".62" />
                            <stop offset="0.99" stopColor="#bfe0ff" stopOpacity=".85" />
                            <stop offset="1" stopColor="#f2f8ff" stopOpacity=".95" />
                          </linearGradient>
                          {/* foreground highlight — grid + wave bloom over the wash */}
                          <linearGradient id="scope-sweep" gradientUnits="userSpaceOnUse" x1={sweepX - 175} y1="0" x2={sweepX} y2="0">
                            <stop offset="0" stopColor="var(--dcs-accent)" stopOpacity="0" />
                            <stop offset="0.5" stopColor="var(--dcs-accent)" stopOpacity=".5" />
                            <stop offset="0.82" stopColor="#bfe0ff" stopOpacity=".95" />
                            <stop offset="0.96" stopColor="#eaf4ff" stopOpacity="1" />
                            <stop offset="1" stopColor="#ffffff" stopOpacity="1" />
                          </linearGradient>
                          <filter id="scope-glow" x="-5%" y="-80%" width="110%" height="260%">
                            <feGaussianBlur stdDeviation="4" />
                          </filter>
                        </defs>
                        <rect x="0" y="0" width="400" height="100" fill="url(#scope-bg)" clipPath="url(#scope-clip)" style={{ mixBlendMode: 'screen' }} />
                      </>
                    )}
                    {/* normal-brightness grid + wave — the faded outline, everywhere */}
                    <path d={grid} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth=".5" />
                    <path d={wave} fill="none" stroke="var(--dcs-accent)" strokeWidth="1.4" strokeOpacity=".75" />
                    {sweepX !== null && (
                      <>
                        {/* additive bloom on grid + wave, clipped to behind the bar */}
                        <g clipPath="url(#scope-clip)" style={{ mixBlendMode: 'screen' }}>
                          <g filter="url(#scope-glow)">
                            <path d={grid} fill="none" stroke="url(#scope-sweep)" strokeWidth="1.8" />
                            <path d={wave} fill="none" stroke="url(#scope-sweep)" strokeWidth="4.5" />
                          </g>
                          <path d={grid} fill="none" stroke="url(#scope-sweep)" strokeWidth=".7" />
                          <path d={wave} fill="none" stroke="url(#scope-sweep)" strokeWidth="1.8" />
                        </g>
                        {/* leading-edge sweep bar */}
                        <line x1={sweepX} y1="0" x2={sweepX} y2="100" stroke="#eaf4ff" strokeWidth="1" strokeOpacity=".9" style={{ mixBlendMode: 'screen' }} />
                      </>
                    )}
                  </>;
                })()}
              </svg>
              <div style={{ position: 'absolute', top: 6, left: 8, fontFamily: 'var(--dcs-font-mono)', fontSize: 9, color: 'var(--dcs-text-mute)' }}>SCOPE</div>
              {/* lights golden whenever the sequencer fires a note */}
              <div style={{
                position: 'absolute', top: 6, right: 6,
                padding: '3px 9px', display: 'flex', alignItems: 'center',
                borderRadius: 999, fontFamily: 'var(--dcs-font-mono)', fontSize: 9,
                fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
                background: sweepX !== null ? '#f4c84e' : 'rgba(255,255,255,.05)',
                color: sweepX !== null ? '#1c1606' : 'var(--dcs-text-mute)',
                border: `1px solid ${sweepX !== null ? 'transparent' : 'var(--dcs-line)'}`,
                transition: 'background .08s, color .08s',
              }}>play</div>
            </div>
            </div>
          </Panel>

          {/* Right: outliner + inspector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Panel title="Outliner" icon="layers" pad={0}>
              <div style={{ padding: '4px 0', minHeight: 0 }}>
                <Tree
                  expanded={new Set(['scn', 'rig'])}
                 
                  selected="hand"
                 
                  nodes={[{
                    id: 'scn', label: 'Scene', icon: 'globe',
                    children: [
                      { id: 'jane', label: 'Jane', icon: 'cube', meta: '64k' },
                      { id: 'rig', label: 'jane_rig', icon: 'bone', children: [
                        { id: 'spine', label: 'spine', icon: 'bone' },
                        { id: 'hand', label: 'L_hand', icon: 'bone', meta: 'IK' },
                      ]},
                      { id: 'sun', label: 'Sun.001', icon: 'light' },
                    ]
                  }]}
                />
              </div>
            </Panel>
            <Panel title="Inspector" icon="cog">
              <div className="dcs-col" style={{ gap: 6 }}>
                <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 20 }}>X</label><Combo value={1.428}  min={-10} max={10} step={0.001} format={v => v.toFixed(3)} width="100%" /></div>
                <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 20 }}>Y</label><Combo value={-0.952} min={-10} max={10} step={0.001} format={v => v.toFixed(3)} width="100%" /></div>
                <div className="dcs-field"><label className="dcs-field__label" style={{ minWidth: 20 }}>Z</label><Combo value={3.000}  min={-10} max={10} step={0.001} format={v => v.toFixed(3)} width="100%" /></div>
              </div>
            </Panel>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginTop: 10 }}>
          {[
            { l: 'attack', v: ring(5, 0) },
            { l: 'decay', v: ring(6, 0.5) },
            { l: 'sustain', v: ring(7, 1.1) },
            { l: 'release', v: ring(8, 1.6) },
          ].map(s => (
            <div key={s.l} style={{ display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--dcs-well)', border: '1px solid var(--dcs-line)', borderRadius: 3, padding: '5px 7px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--dcs-text-mute)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{s.l}</span>
                <span className="dcs-mono" style={{ fontSize: 10, color: 'var(--dcs-accent)' }}>{Math.round(s.v * 100)}</span>
              </div>
              <Slider value={s.v} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionHero() {
  return (
    <section className="dw-section" id="top" style={{ marginBottom: 60 }}>
      <a href="https://github.com/benjcooley/affineui" target="_blank" rel="noreferrer" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px',
        background: 'var(--dw-accent-dim)',
        border: '1px solid rgba(77,159,255,.35)',
        borderRadius: 999,
        fontSize: 12,
        color: 'var(--dw-accent-lo)',
        marginBottom: 28,
        textDecoration: 'none',
        width: 'fit-content',
        fontFamily: 'var(--dw-font-mono)',
        letterSpacing: '.02em',
      }}>
        <Icon name="bolt" size="sm" />
        <span style={{ color: 'var(--dw-text)', fontWeight: 600 }}>Built for AffineUI</span>
        <span>·</span>
        <span>the single-file, zero-dependency, GPU-accelerated UI engine</span>
        <Icon name="chevron-right" size="sm" />
      </a>
      <div className="dw-hero">
        <div>
          <div className="dw-hero__eyebrow">decius.css · v0.6.2</div>
          <h1>The CSS framework for things that aren't websites.</h1>
          <p className="lede">
            A complete component system for digital content creation tools, synths, video editors, and
            pro desktop interfaces — built around the conventions of Blender, DaVinci Resolve, and Vital,
            distributed as plain CSS, a vanilla-JS runtime, an icon font, and self-hosted type.
            <strong style={{ color: 'var(--dw-text)' }}> One download. Zero dependencies.</strong>
          </p>
          <div className="dw-hero__actions">
            <a className="dw-cta dw-cta--primary dw-cta--lg" href={`${import.meta.env.BASE_URL}dl/decius-css.zip`} download>
              <Icon name="import" size="sm" /> Download decius <span className="dw-cta__sub" style={{ opacity: .7, fontWeight: 400 }}>· .zip · css + js + fonts</span>
            </a>
            <a className="dw-cta dw-cta--ghost" href="#install">
              <Icon name="rocket" size="sm" /> Get started
            </a>
            <a className="dw-cta dw-cta--ghost" href="#buttons">
              <Icon name="graph" size="sm" /> View components
            </a>
            <span className="dw-pill dw-pill--accent dw-pill--dot">MIT license</span>
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 14, fontSize: 13, color: 'var(--dw-text-mute)', flexWrap: 'wrap' }}>
            <span>or grab just what you need:</span>
            <a href={`${import.meta.env.BASE_URL}dl/css/decius.bundle.min.css`} download>decius.bundle.min.css</a>
            <a href={`${import.meta.env.BASE_URL}dl/js/decius.min.js`} download>decius.min.js</a>
            <a href="#install">CDN &amp; npm</a>
          </div>
        </div>
        <HeroDeck />
      </div>

      <div className="dw-install">
        <span className="dw-install__prompt">$</span>
        <span className="dw-install__cmd">npm install decius-css</span>
        <button className="dw-install__copy"><Icon name="copy" size="sm" /> Copy</button>
      </div>
    </section>
  );
}

function SectionWhy() {
  const features = [
    { icon: 'cpu',    title: 'Zero runtime',     desc: 'One stylesheet with the fonts and icon font baked in. No tokens compile step, no design-system tax. Drop the link tag in and ship.' },
    { icon: 'palette', title: 'Tokenized to the wall',  desc: 'Every color, size, radius, shadow and motion value lives in a CSS custom property. Re-theme by overriding seven variables.' },
    { icon: 'magnet', title: 'Pixel-snapped',    desc: 'Lines, bevels, and 1.25px icon strokes line up at 100% zoom. Built for Sundays-on-a-23-inch-screen.' },
    { icon: 'rocket', title: 'Built for pros',   desc: 'Combo number fields, knobs, bipolar sliders, dockable panels. The vocabulary your power users already speak.' },
    { icon: 'globe',  title: 'Framework-agnostic', desc: 'Plain CSS classes. Use it with React, Vue, Svelte, htmx, vanilla JS, Electron, Tauri. Same markup, same shadows.' },
    { icon: 'eye',    title: 'Designed in pairs', desc: 'A light docs-web stylesheet ships alongside, so your marketing site and your app stay siblings, not strangers.' },
  ];
  return (
    <section className="dw-section" id="why">
      <div className="dw-section__eyebrow">Why decius</div>
      <h2>Built for the interfaces nobody else dresses up.</h2>
      <p className="dw-section__lead">
        Bootstrap is for websites. Material is for apps. Decius is for the dense, instrument-panel
        UIs that sit between you and a render — modelers, sequencers, samplers, mixers, editors,
        DAWs, color-graders, terminals.
      </p>
      <div className="dw-feature-grid">
        {features.map(f => (
          <div key={f.title} className="dw-feature">
            <div className="dw-feature__icon"><Icon name={f.icon} /></div>
            <h4>{f.title}</h4>
            <p>{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionInstall() {
  return (
    <section className="dw-section" id="install">
      <div className="dw-section__eyebrow">Getting started</div>
      <h2>Install</h2>
      <p className="dw-section__lead">
        One stylesheet — fonts, icon font, and the whole framework, self-hosted. Add the
        <code>.dcs</code> class to the root of anything you want styled; the framework is fully
        scoped, so it won't leak into the rest of your page. Built for
        {' '}<a href="https://github.com/benjcooley/affineui">affineui</a>, runs in any browser.
      </p>

      <div className="dw-subhead"><h3>From a CDN</h3><div className="dw-subhead__meta">Zero build</div></div>
      <CodeBlock lang="html" code={`<!-- everything: self-hosted fonts + icon font + framework -->
<link rel="stylesheet"
  href="https://cdn.jsdelivr.net/npm/decius-css@0.5/dist/css/decius.bundle.min.css">`} />

      <div className="dw-subhead"><h3>Or from npm</h3><div className="dw-subhead__meta">Bundler / Sass</div></div>
      <CodeBlock lang="bash" code={`npm install decius-css`} />
      <CodeBlock lang="js" code={`// plain CSS artifacts
import 'decius-css/css/decius.bundle.min.css';

// …or compile from Sass source and theme it yourself
@use 'decius-css/scss/decius';`} />

      <div className="dw-subhead"><h3>Wrap your app</h3><div className="dw-subhead__meta">.dcs scopes the system</div></div>
      <CodeBlock lang="html" code={`<div class="dcs" data-dcs-density="comfortable">
  <button class="dcs-btn dcs-btn--primary">
    <i class="di di-render"></i>
    Render
  </button>
</div>`} />

      <div style={{ marginTop: 28 }}>
        <div className="dcs-alert" style={{ background: 'var(--dw-bg-soft)', borderColor: 'var(--dw-line)', borderLeftColor: 'var(--dw-accent)', color: 'var(--dw-text)' }}>
          <div className="dcs-alert__icon"><Icon name="info" /></div>
          <div className="dcs-alert__body">
            <div className="dcs-alert__title" style={{ color: 'var(--dw-text)' }}>This page is decius.web</div>
            <div className="dcs-alert__msg" style={{ color: 'var(--dw-text-dim)' }}>
              The white chrome around the embedded panels is <code>decius-web.css</code>, a companion stylesheet
              meant for your marketing site, docs, and changelogs. The dark panels embedded throughout are
              <code>decius.css</code> proper — siblings, sharing the same accent, type, and icon family.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { SectionHero, SectionWhy, SectionInstall, HeroDeck });


/* ===== app.jsx ===== */
/* app.jsx — main docs site wiring */
const { useState: useStateApp, useEffect: useEffectApp } = React;

const REPO_URL = 'https://github.com/benjcooley/decius-css';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "blue",
  "darkness": "default",
  "density": "comfortable",
  "radius": "default"
}/*EDITMODE-END*/;

const NAV = [
  { group: 'Overview', items: [
    { id: 'top',    label: 'Introduction',  icon: 'decius' },
    { id: 'sample-apps', label: 'Live apps',  icon: 'rocket' },
    { id: 'why',    label: 'Why decius',    icon: 'help' },
    { id: 'install',label: 'Install',       icon: 'rocket' },
    { id: 'javascript', label: 'JavaScript', icon: 'cpu' },
  ]},
  { group: 'Foundations', items: [
    { id: 'colors',  label: 'Color',    icon: 'palette' },
    { id: 'type',    label: 'Type',     icon: 'edit' },
    { id: 'spacing', label: 'Spacing',  icon: 'grid' },
    { id: 'icons',   label: 'Icons',    icon: 'star' },
  ]},
  { group: 'Components', items: [
    { id: 'buttons', label: 'Buttons',  icon: 'check-circle' },
    { id: 'inputs',  label: 'Inputs',   icon: 'edit' },
    { id: 'sliders', label: 'Sliders & faders',  icon: 'wave-sine' },
    { id: 'knobs',   label: 'Knobs',    icon: 'cog' },
    { id: 'combo',   label: 'Combo fields', icon: 'caret-up-down' },
    { id: 'checks',  label: 'Checks · switches', icon: 'check' },
  ]},
  { group: 'Layout', items: [
    { id: 'panels',  label: 'Panels',     icon: 'array' },
    { id: 'subpanels', label: 'Subpanels', icon: 'menu' },
    { id: 'foldouts',  label: 'Foldouts',  icon: 'chevron-down' },
    { id: 'dock',    label: 'Dock panels', icon: 'layers' },
    { id: 'tearoff', label: 'Tear-off &amp; document pane', icon: 'export' },
  ]},
  { group: 'Data', items: [
    { id: 'lists',  label: 'Lists',  icon: 'menu' },
    { id: 'trees',  label: 'Trees',  icon: 'folder-open' },
    { id: 'dnd',    label: 'Drag & drop', icon: 'move' },
    { id: 'tables', label: 'Tables', icon: 'grid' },
    { id: 'cards',  label: 'Cards',  icon: 'image' },
    { id: 'badges', label: 'Badges & keys', icon: 'tag' },
  ]},
  { group: 'Feedback', items: [
    { id: 'alerts', label: 'Alerts', icon: 'alert' },
    { id: 'modals', label: 'Modals', icon: 'info' },
  ]},
  { group: 'Overlays', items: [
    { id: 'menus',    label: 'Menus & dropdowns', icon: 'menu' },
    { id: 'popovers', label: 'Popovers',          icon: 'info' },
    { id: 'toasts',   label: 'Toasts',            icon: 'alert' },
  ]},
  { group: 'Editors', items: [
    { id: 'color-pickers', label: 'Color pickers', icon: 'droplet' },
    { id: 'curve',         label: 'Curve editor',  icon: 'curve' },
    { id: 'graph',         label: 'Node graph',    icon: 'graph' },
    { id: 'textures',      label: 'Texture pickers', icon: 'texture' },
  ]},
  { group: 'Showpieces', items: [
    { id: 'sample-dcc',   label: 'Sample DCC tool', icon: 'cube' },
    { id: 'sample-synth', label: 'Sample synth',    icon: 'wave-saw' },
    { id: 'skeuomorphic', label: 'Skeuomorphic hw', icon: 'cable' },
  ]},
];

function Sidebar({ active, onJump, open }) {
  return (
    <aside className={`dw-sidebar${open ? ' dw-sidebar--open' : ''}`}>
      {NAV.map(group => (
        <div key={group.group}>
          <div className="dw-sidebar__group">{group.group}</div>
          {group.items.map(it => (
            <a key={it.id} href={`#${it.id}`}
               className={active === it.id ? 'active' : ''}
               onClick={(e) => { e.preventDefault(); onJump(it.id); }}>
              <Icon name={it.icon} size="sm" /> {it.label}
            </a>
          ))}
        </div>
      ))}
    </aside>
  );
}

function TopBar({ onMenu }) {
  return (
    <header className="dw-top">
      <button className="dw-hamburger" onClick={onMenu} aria-label="Toggle navigation"><Icon name="menu" /></button>
      <a href="#top" className="dw-brand">
        <span className="dw-brand__mark">
          <Icon name="decius" size="lg" />
        </span>
        <span>decius<span style={{ color: 'var(--dw-accent-lo)' }}>.css</span></span>
        <span className="dw-brand__version">v0.6.2</span>
      </a>
      <nav className="dw-nav">
        <a href="#install" className="active">Docs</a>
        <a href="#buttons">Components</a>
        <a href="#sample-dcc">Showpieces</a>
        <a href="#install">Install</a>
      </nav>
      <div className="dw-top__spacer" />
      <div className="dw-top__actions">
        <a href={REPO_URL} target="_blank" rel="noreferrer noopener"
           className="dw-cta dw-cta--ghost" style={{ height: 32, padding: '0 12px' }}>
          <Icon name="export" size="sm" /> GitHub
        </a>
      </div>
    </header>
  );
}

const ACCENT_OPTIONS = [
  { name: 'blue',   hex: '#4d9fff' },
  { name: 'cyan',   hex: '#00b8d4' },
  { name: 'orange', hex: '#ff8a3a' },
  { name: 'violet', hex: '#8b6dff' },
  { name: 'green',  hex: '#3dd68a' },
];

function Tweaks() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Apply tweaks at the document level — the CSS vars then cascade through
  // every .dcs subtree. Demos that have their own attribute explicitly set
  // override locally (per-demo toggle wins; otherwise the global cascades in).
  useEffectApp(() => {
    const root = document.body;
    if (t.accent === 'blue') root.removeAttribute('data-dcs-accent');
    else root.setAttribute('data-dcs-accent', t.accent);
    if (t.darkness === 'default') root.removeAttribute('data-dcs-dark');
    else root.setAttribute('data-dcs-dark', t.darkness);
    root.setAttribute('data-dcs-density', t.density);
    if (t.radius === 'default') root.removeAttribute('data-dcs-radius');
    else root.setAttribute('data-dcs-radius', t.radius);
  }, [t]);

  const currentHex = (ACCENT_OPTIONS.find(o => o.name === t.accent) || ACCENT_OPTIONS[0]).hex;

  return (
    <TweaksPanel title="Tweaks">
      <TweakSection title="Theme">
        <TweakColor
          label="Accent"
          options={ACCENT_OPTIONS.map(o => o.hex)}
          value={currentHex}
          onChange={hex => {
            const opt = ACCENT_OPTIONS.find(o => o.hex === hex);
            if (opt) setTweak('accent', opt.name);
          }}
        />
        <TweakRadio
          label="Darkness"
          options={[
            { value: 'darker',  label: 'Darker' },
            { value: 'default', label: 'Default' },
            { value: 'lighter', label: 'Lighter' },
          ]}
          value={t.darkness}
          onChange={v => setTweak('darkness', v)}
        />
      </TweakSection>
      <TweakSection title="Sizing">
        <TweakRadio
          label="Density"
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'comfortable', label: 'Cozy' },
            { value: 'spacious', label: 'Spacious' },
          ]}
          value={t.density}
          onChange={v => setTweak('density', v)}
        />
        <TweakRadio
          label="Radius"
          options={[
            { value: 'sharp', label: 'Sharp' },
            { value: 'default', label: 'Default' },
            { value: 'round', label: 'Round' },
          ]}
          value={t.radius}
          onChange={v => setTweak('radius', v)}
        />
      </TweakSection>
    </TweaksPanel>
  );
}

function App() {
  const [active, setActive] = useStateApp('top');
  const [navOpen, setNavOpen] = useStateApp(false);
  useEffectApp(() => {
    // Sync hash → active
    const sync = () => {
      const h = window.location.hash.slice(1);
      if (h) setActive(h);
    };
    sync();
    window.addEventListener('hashchange', sync);

    // Intersection observer for scroll-spy
    const sections = NAV.flatMap(g => g.items.map(i => i.id));
    const els = sections.map(id => document.getElementById(id)).filter(Boolean);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, { rootMargin: '-30% 0% -60% 0%', threshold: 0 });
    els.forEach(el => obs.observe(el));

    return () => {
      window.removeEventListener('hashchange', sync);
      obs.disconnect();
    };
  }, []);

  const jump = (id) => {
    setActive(id);
    setNavOpen(false);   // close the mobile drawer on navigate
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
      history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <div className="dw-page">
      <TopBar onMenu={() => setNavOpen(o => !o)} />
      <Sidebar active={active} onJump={jump} open={navOpen} />
      {navOpen && <div className="dw-scrim" onClick={() => setNavOpen(false)} />}
      <main className="dw-main">
        <SectionHero />
        <SectionLiveApps />
        <SectionWhy />
        <SectionInstall />
        <SectionJavaScript />

        <hr />
        <SectionColors />
        <SectionType />
        <SectionSpacing />
        <SectionIcons />

        <hr />
        <SectionButtons />
        <SectionInputs />
        <SectionSliders />
        <SectionKnobs />
        <SectionCombo />
        <SectionChecksSwitches />

        <hr />
        <SectionPanels />
        <SectionSubpanels />
        <SectionFoldouts />
        <SectionDock />
        <SectionTearoff />

        <hr />
        <SectionLists />
        <SectionTrees />
        <SectionDragDrop />
        <SectionTables />
        <SectionCards />
        <SectionBadges />

        <hr />
        <SectionAlerts />
        <SectionModals />

        <hr />
        <SectionMenus />
        <SectionPopovers />
        <SectionToasts />

        <hr />
        <SectionColorPicker />
        <SectionCurve />
        <SectionGraph />
        <SectionTexture />

        <hr />
        <SectionSampleDCC />
        <SectionSampleSynth />
        <SectionSkeuomorphic />

        <footer style={{ marginTop: 80, paddingTop: 32, borderTop: '1px solid var(--dw-line)', display: 'flex', gap: 28, color: 'var(--dw-text-mute)', fontSize: 13, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="dw-brand" style={{ fontSize: 14 }}>
            <span className="dw-brand__mark" style={{ width: 24, height: 24 }}><Icon name="decius" /></span>
            decius.css
          </span>
          <span>v0.6.2 · MIT</span>
          <span style={{ flex: 1 }} />
          <a href="#install">Install</a>
          <a href="#colors">Tokens</a>
          <a href="#sample-dcc">Showpieces</a>
          <a href={`${import.meta.env.BASE_URL}dender/index.html`} target="_blank" rel="noreferrer noopener">Dender</a>
          <a href={`${import.meta.env.BASE_URL}photoedit/index.html`} target="_blank" rel="noreferrer noopener">Photo Edit</a>
          <a href={REPO_URL} target="_blank" rel="noreferrer noopener">GitHub</a>
          <a href={`${REPO_URL}/releases`} target="_blank" rel="noreferrer noopener">Changelog</a>
        </footer>
      </main>
      <Tweaks />
    </div>
  );
}

// Icons are a web font (loaded via CSS), so we can mount immediately.
// DndProvider wraps the app so typed drag-and-drop works across panels/demos.
createRoot(document.getElementById('root')).render(<DndProvider><App /></DndProvider>);
